import { isVerifiedSanityCategory } from '../../lib/preservedUserDecisions';
import {
  collectNarrativeStrings,
  isFalsePositiveQueueDurationFindingText,
} from '../../lib/audit/queueDurationCheck';
import { isReviewIssueAlreadyQualifiedInText } from '../../lib/audit/reviewIssueSurvival';
import { reportDemonstrationDisclosure } from '../../lib/demonstration/disclosure';
import { admittedReportReferenceIndices, projectReportSourceEvidence } from '../../lib/reportGeneration/reportEvidenceSourceAdmission';
import { downloadReportSummary } from './summary/reportSummaryArtifactActions';
import { reportSummaryEligibility } from '../../lib/reportSummary/eligibility';
import { reportSummaryPath } from '../../lib/siteNavigation';

import { floodRiskKpi } from '../../lib/landFloodRisk';
import { openAuditBannerSummary, openAuditDisclosureSentence, openAuditFindings } from '../../lib/audit/openAuditFlag';
import { filterUnsettledTruthQuestions } from '../../lib/audit/truthAttestations';
import { reportPdfDeliveryNotice } from '../../lib/reportGeneration/pdfDeliveryNotice';
import React, { useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SiteRagReport, RagStatus } from '../../types/siteRag';
// NB-2 / NB-3 — lazy render-time hygiene backfill + version pin.
import { applyHygieneBackfill } from '../../services/narrativeBackfill';
import { HYGIENE_VERSION } from '../../services/narrativeHygiene';
import { applySectionFallbacksToReport } from '../../lib/applySectionFallbacks';
import { isImmutableReportReadSource, sourceForReportActions } from '../../lib/reportGeneration/reportReadSource';
import { hasRetiredSimulationReference } from '../../lib/reportGeneration/legacySimulationRetirement';
// HR-QA (2026-06-10) — `prepareReportForRender` = scrubInternalSentinels +
// filterUnavailableSections, the SINGLE render-time hygiene chokepoint shared
// with PrintReportView so the viewer and the server PDF apply identical
// scrubbing/filtering (parity). `computeEffectiveAppendixVisibleRefIndices`
// resolves inline `[Ref N]` against the rows the appendix ACTUALLY renders.
import {
  prepareReportForRender,
  computeEffectiveAppendixVisibleRefIndices,
} from '../../lib/reportRendering';
import { detectInstructionShape } from '../../lib/instructionShapeGuard';
import { computeReportTypeFieldCoverage } from '../../lib/coverage/computeReportTypeFieldCoverage';
import type { ReportTypeId } from '../../config/reportTypes';
import {
  Download,
  Calendar,
  Activity,
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Globe,
  Send,
  FolderOpen,
  ChevronDown,
  Trash2,
  Loader2,
  Pencil,
} from 'lucide-react';
import { SafeResponsiveContainer } from '../ui/SafeResponsiveContainer';
import { COMPANY_ARCHETYPES, type CompanyArchetype } from '../../types/company';
import { CarbonDioxideFormula } from './report/CarbonDioxideFormula';
import { LandRequirementCell } from './report/LandRequirementCell';
import { isDatacrateAdmin } from '../../lib/isDatacrateAdmin';
import { walkCitationText } from '../../lib/walkCitationText';
// RV5 — walkClaimText extends walkCitationText to also parse [verified|inferred|unverified] tokens.
import { walkClaimText } from '../../lib/walkClaimText';
// CT6 — walkClaimsText extends walkClaimText with per-sentence [¹] markers when report.claims[] exists.
import { walkClaimsText } from '../../lib/walkClaimsText';
// Citation unification — appendix-visible ref index set keeps in-text [Ref N] consistent with the appendix.
import { getDisplayVerdict } from '../../lib/reportVerdict';
// BTM-4 (v1.2.0) — derive phasing/expanded-intent risks from the gen-time
// intent and merge them into the deterministic developmentRisks register.
import { mergeDevelopmentRisks } from '../../lib/phasingRisks';
import { REPORT_TYPE_REGISTRY } from '../../config/reportTypes';
import { createReportArtifactRequest } from '../../lib/reportArtifacts/reportArtifactRequest';
import { REPORT_PDF_BUILDER_VERSION } from '../../lib/screeningSummary/vocabulary';
import { resolveSharedReportExportAction } from '../../lib/reportArtifacts/sharedReportExportAction';
import { buildReportExportFilename } from '../../lib/reportArtifacts/reportExportFilename';
import { auth } from '../../firebase';
import type { DetailedRisk } from '../../types/siteRag';
import { ReportHeaderStrip } from './report/ReportHeaderStrip';
// No banners on report pages (William, 2026-10-13): the states the page used
// to announce with DraftBanner / ReportAlertBanner / the truth, legacy-sim,
// PDF, template and provider banners resolve to ONE header status chip, and
// the in-section notes render only in the delivered PDF / print tree.
import { resolveReportStatusChip, selectActiveReportGeneration } from './report/reportStatusChip';
import { collectReportSectionNotices, misconfiguredProviders } from './report/reportSectionNotices';
import { isNarrativeDegraded, liveHighSeverityConflicts } from './report/reportAlertSelection';
import { InSectionNoticesProvider } from './report/inSectionNotices';
import { SectionNoticesDialog } from './report/SectionNoticesDialog';
import { resolveDraftTierUx } from '../../lib/draftTierUx';
import { resolveReportConflictNotice } from '../../lib/reportConflictNotice';
import { reportAuditPath, siteOverviewPath } from '../../lib/siteNavigation';

import { LcoeProvenanceFootnote } from './report/LcoeProvenanceFootnote';
import { deriveBtmLandCell, deriveBtmLcoeCell, resolveBtmLandBasis, dropAllEmptyColumns } from '../../lib/btmTechOverview';
// Editorial restyle — content-body status pills + hairline cards read the
// active (preset-resolved) `theme` via roleColor()/EditorialBadge so the
// inline report body re-skins per preset (Journal/Institutional/Review/…),
// mirroring the already-converted report/* + sections/* trees (canonical
// reference: report/SpecializedSections.tsx).
import { roleColor, roleColorAlpha, attachReaderMode, READER_DARK_PAGE_BG } from '../../lib/reportRoleColors';
import { useAppResolvedTheme, ReportReaderModeContext, type ReportReaderMode } from '../../lib/useReportReaderMode';
import type { ReportTheme } from '../../types/reportTheme';
import { EditorialBadge, EditorialSubsection, EditorialRecommendations, EditorialKpiRail, EditorialDataTable, EditorialFigureCaption, EditorialTimeline, EditorialHeroMetric } from './report/editorial';
import type { EditorialKpiItem } from './report/editorial';
import { resolveExecutiveSummaryRail, resolveExecutiveSummaryHero } from './report/executiveSummaryRail';
import { ProvenanceStrip } from './report/ProvenanceStrip';
import { SectionQuarantineBanner } from './report/SectionQuarantineBanner';
// MEL-MR-2 — admin-only per-section chip surfacing
// `qualityWarnings.meta-instruction-leak`. Composes with the existing
// SectionQuarantineBanner (SNT-3). No-op on the 99% case where no leak
// fired. Like SectionQuarantineBanner, it renders only in the delivered PDF /
// print tree, never on the report page (InSectionNoticesProvider below).
import { MetaLeakChip } from './report/MetaLeakChip';
// CI-2 (CSE-FOLLOWUP-CONFLICT-DETECTION) — admin-only chip surfacing
// internal CSE-claim conflicts (same sectionId + field, different value).
import { CseConflictChip } from './report/CseConflictChip';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { SiteRagReportSummary } from './SiteRagReportSummary';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  // TCP-6 (row 0.99233) — AreaChart / Area / BarChart / Bar were only
  // used by the now-deleted stray "Monthly Climate Profile" duplicate
  // block. The canonical chart lives in ClimateProfileChart which
  // imports its own recharts primitives. Dropping these here keeps
  // bundle imports honest.
} from 'recharts';
import { toast } from 'sonner';
import { openGoogleEarthWeb, resolveSiteEarthViewCenter, siteHasEarthViewLocation } from '../../lib/googleEarthWebUrl';
import { useStore } from '../../store';

import { navigateBackToReports } from './reportBackNavigation';
import { SEDVariableBrowser } from './SEDVariableBrowser';
import { RerunWrapper } from './RerunWrapper';
import { DeleteReportDialog } from './DeleteReportDialog';
// SedVersionBanner is no longer rendered directly — its drift logic lives in
// `pickReportRefreshAlert` and surfaces as the header status chip's "Inputs
// changed" state. Other call sites (e.g. SiteLandingPage's report list) still
// import it from './SedVersionBanner' independently.
import { ProviderFailureCard } from './ProviderFailureCard';
import { STABLE_EMPTY_RECORD } from '../../store/stableEmpty';
import { EditableHeading } from './report/EditableHeading';
import { LcoeChartSection } from './report/LcoeChartSection';
// Task 5 — the standalone climateProfile section stranded a near-empty page,
// so the chart is now FOLDED into the Climate & Severe Weather section body.
// Mount the existing chart component (no reimplementation); resolve its
// time-series via the same shared util the retired standalone used so the
// data + empty-state behaviour is unchanged.
import { ClimateProfileChart } from './report/charts/ClimateProfileChart';
import { resolveReportTimeSeries } from '../../lib/resolveReportTimeSeries';
import { ExtendedDataSection, extendedDataHasContent } from './report/ExtendedDataSection';
import { SimulationResilienceSection } from './report/SimulationResilienceSection';
import { hasSimEvidenceExhibits } from '../../lib/reportGeneration/evidence/simEvidenceExhibit';
// KMU — the in-report Site Boundary card's split menu opens this editor
// pre-snapped to the Import dropzone. Mutates the LIVE site doc, NOT
// the rendered report — the report keeps its frozen snapshot and a
// post-save toast clarifies the immutability rule.
import { SiteLocationBoundaryEditor } from './SiteLocationBoundaryEditor';
import { SiteLayerEditor } from './SiteLayerEditor';
import { BlockedPublishDialog } from './report/BlockedPublishDialog';
import { FeasibilityProfileSection } from './report/FeasibilityProfileSection';
import { ScoreProvenancePanel } from './report/ScoreProvenancePanel';
import { stripPhasedExecutiveLeadIn } from './report/PhasedSummaryBanner';
import { TableOfContents } from './report/TableOfContents';
import { ExecutiveLetterPage } from './report/ExecutiveLetterPage';
import { deriveTocEntries } from '../../lib/tocEntries';
import { coverCarriesExecSummary, coverCarriesToc, resolveOnCoverTocEntries } from '../../lib/normalizeCoverLayout';
import { defaultSectionHeading } from '../../lib/defaultSectionHeading';
import { resolveEditorialLayer, hasGeneratedEditorialLayer } from '../../lib/editorial/resolveEditorialLayer';
import { EditorialProseBody } from './report/editorial/EditorialProseBody';
// William QA BUG #15 — the Part-divider sheet was removed from the rendered
// report; EditorialPartDivider / EDITORIAL_PARTS / buildPartTocEntries are no
// longer imported here (the reportParts lib still exports them for other use).
import { buildSectionDisplayNumbers, sectionNumberDataAttr } from '../../lib/sectionDisplayNumbers';
// TOR-2 — pure projection that maps the report's frozen section ids
// through the live template's section order. Lets template re-orders
// in Studio (drag-reorder, Pages-tab re-assignment) propagate to the
// saved report's TOC + body section numbering without regenerating
// the report. Opt-out via `template.dynamicOrder === false`. Legacy
// reports without a templateId fall through to the gen-time order.
import { ReferencesSection } from './report/ReferencesSection';
import { ReportSubSectionBlocks } from './report/ReportSubSectionBlocks';
// RV6 — "What this report does not know" lives in ReferencesSection (after appendix).
import { ReportClosingTail } from './report/ReportClosingTail';
import { GlossarySection } from './report/GlossarySection';
// RTB-2/RTB-3 — neutral placeholder + claim-derived fallback so the
// inline Key Findings render survives an empty `structuredFindings`
// (live-prod 2026-05-18 Redhead Feasibility v4.0 dropped the section
// entirely, leaving the TOC pointing at a dead anchor).
import { diagnoseEmptyKeyFindings } from '../../lib/keyFindingsDerive';
import { ProjectIntentOverviewSection } from './report/ProjectIntentOverviewSection';
import { CustomBriefExecutiveSummaryBody } from './sections/CustomBrief/CustomBriefExecutiveSummaryBody';
import { extractAcronymsFromReport } from '../../lib/acronymExtractor';
import { RunPreferencesPanel } from './RunPreferencesPanel';
import { getSectionsForType, resolveHeading, reportTypeFoldsExtendedDataIntoAppendix } from '../../config/reportSections';
// COMBINES (fold #5) — raw visibility chain (report → template → true) for the
// folded Extended-Site-Data appendix subsection. `isVisible('extendedData')`
// cannot be used: the id left the type registries, so the type-applicability
// gate would report false for every report; the ARCHIVED explicit hide
// (`sectionVisibility.extendedData === false`, stamped when the section was
// still Studio-controllable) is what must keep being honoured.
import { resolveSectionVisibility } from '../../lib/effectiveTemplateConfig';
import { resolveReportPresentation, resolveBodyColumns } from '../../lib/resolveReportPresentation';
import { isEditorialLayoutsEnabled, isEditorial2ColEnabled } from '../../lib/runtimeFlags';
import { siteDecisionTab } from '../../lib/siteDecisionTab';
import { labelClass } from '../../lib/reportTypography';
import { reportTypographySizes, REPORT_LINE_HEIGHT, editorialFlat, reportHeadingInk } from '../../lib/reportSectionHeading';
import { PAGE_BG_VAR, REPORT_PRINTABLE_WIDTH_CSS, resolvePageBg } from '../../lib/reportLayoutTokens';
import {
  reportContentPaddingClass,
  reportShellPaddingClass,
  REPORT_SECTION_PAGE_COLS_2_CLASS,
} from '../../lib/reportSectionChrome';
import {
  reportSectionGapClass,
  REPORT_FLOW_GAP_CLASS,
  REPORT_HEADING_MB_CLASS,
} from '../../lib/reportSpacingTokens';
import { ConfidentialSidebar } from './report/ConfidentialSidebar';
import { EditorialSectionShell } from './report/EditorialSectionShell';
import { SectionErrorBoundary } from './report/SectionErrorBoundary';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../lib/sectionFallbacks/derivedFallbacks';
import {
  resolveSectionPageColumnCountForSection,
  resolveSectionFlowModeForSection,
} from '../../lib/resolveSectionPageColumnCount';
import { spanColumnsSelectors } from '../../lib/reportSpanSelectors';
import { stampTemplatePageBreaks } from '../../lib/templatePages/computeTemplatePageBreaks';
import { buildLayoutPlanPageMapOverrides } from '../../lib/reportLayout/applyLayoutPlanBreaks';
import {
  computeMeasuredBreaks,
  readMeasuredSections,
  contentHeightPx,
  A4_SHEET_HEIGHT_PX,
  DEFAULT_PRINT_MARGINS_MM,
} from '../../lib/templatePages/measuredPageBreaks';
import { EnvironmentalSections } from './report/EnvironmentalSections';
import { PowerSections, WaterSections, TestFitSections, InterconnectionSections } from './report/SpecializedSections';
import { NewTypeSectionsRouter, hasModernSections } from './sections/SectionsRouter';
// R-131-FIN-DECISION-K — pinned pathway comparison renders via NewTypeSectionsRouter
// for modern types (test-fit / executive-summary). Feasibility does NOT route
// through the modern router (no sentinel), so it is mounted inline below.
import { PathwayComparisonReportSection, StudioPreviewProvider } from './sections/PathwayComparison/PathwayComparisonReportSection';
import { SectionIdProvider, RouterSectionChromeProvider } from './sections/SectionShell';
import { CapacityCreditDeepDiveSection } from './sections/PowerAvailability';
import { ViewModeProvider, useViewMode, type ViewMode } from './viewMode';
import { FigureCounterProvider } from './report/figureCounter';
import { FindingClaimsList } from './report/FindingClaimsList';
// SLK-H4 — partial: SiteRagReportShell built and ready, but the
// in-viewer mount is deferred (paginated reportRef body's div balance
// interacts badly with inline wrapping). SLK-D / SLK-S consume the
// shell directly from './SiteRagReportShell'. Re-enable inline wrap
// once reportRef body is decomposed into balanced sub-trees.
import { SleekChartTooltip } from './sleek/SleekChartTooltip';
import { useEffectiveReportTheme, ThemeOverrideProvider } from '../../lib/useEffectiveReportTheme';
import { resolveEffectiveTheme } from '../../lib/mergeTemplateTheme';
import { stripTemplateLinkedLayoutSnapshots } from '../../lib/stripTemplateLinkedLayoutSnapshots';
import { useTemplateOverride } from '../../lib/useEffectiveTemplate';
import { resolveActiveReportTemplate } from '../../lib/resolveActiveReportTemplate';
import { fetchCompanyReportTemplates } from '../../services/reportTemplateService';
import type { ReportTemplate } from '../../types/reportTemplate';
import { ChartShrinkContext } from '../../lib/ChartShrinkContext';
// ReportFooter renders via ReportClosingTail at the report tail.
import { renderCover } from './report/covers/renderCover';
import { resolveReportBranding, resolveReportCoverBranding } from '../../lib/reportBranding';
import { useCompanyBranding } from '../../lib/companyBranding';
import { resolveUploadedLogo } from '../../lib/resolveEffectiveLogo';
import { splitNarrativeParagraphs } from '../../lib/formatKeyFindingText';

interface SiteRagReportViewProps {
  report: SiteRagReport;
  /**
   * Audience the report tree is rendering for. Defaults to 'editor'.
   *
   *   - 'editor' (default): the report's creator is looking at it
   *     inside the product. Admin signals are visible (override chip,
   *     edit-history appendix, edit pencils, etc.).
   *   - 'reader': a logged-in collaborator is reading the report.
   *     Same as 'editor' for now; reserved for future shareable views.
   *   - 'export': the report is being rendered by a controlled artifact
   *     renderer. Admin signals are stripped from the delivered projection.
   */
  viewMode?: import('./viewMode').ViewMode;
  /**
   * Optional admin-flag override. PrintReportView (the puppeteer tab)
   * passes this through after reading the signed-token's `isAdmin`
   * claim — the headless tab has no Firebase session so it can't
   * derive `companyRole === 'admin'` from the store. In every other
   * mount (interactive app), this is left undefined and the store
   * value wins. AG pack, AG2.
   */
  isAdminOverride?: boolean;
  /**
   * TSF-6 — explicit heading-override map for preview use only. When
   * supplied, REPLACES the template-derived `headingOverrides` (the
   * map normally pulled from the store via `templateId`). Per-report
   * overrides (`report.headingOverrides`) still merge on top when the
   * user edited a heading in the report viewer. Production reports
   * resolve live template overrides via `resolveReportPresentation`
   * unless the report carries an explicit per-key override.
   */
  headingOverrides?: Record<string, string>;
  /**
   * ROC — explicit site snapshot for PDF print / export contexts where
   * the Zustand store is empty (headless print tab). When supplied,
   * replaces the store lookup for map layers, doc filenames, KMZ, etc.
   */
  exportSite?: import('../../types/site').SiteEntity | null;
  /**
   * ROC — pre-loaded SED for print/export. Skips client authedFetch
   * load when the server bundles SED in print-data.
   */
  exportSed?: import('../../types/siteEvidence').SiteEvidenceDocument | null;
  /**
   * Template Studio preview — uses viewMode="export" to strip admin chrome
   * but still needs section-order flatten so drag-reordered sections
   * interleave across page shells. Real PDF export omits this flag so
   * template page physics keeps intact page buckets.
   */
  studioPreview?: boolean;
  /**
   * #93 / B1 — page-level two-column FLOW active. Passed TRUE by
   * PaginatedReportView for the OFFSCREEN export host it flows through pagedjs,
   * whenever the active preset is two-column (`themeBodyColumns === 2`). When
   * TRUE AND the preset is two-column, the post-cover body is wrapped in a real
   * `[data-page-flow-box]` element (`column-count: 2; column-fill: auto`) and
   * each section is classified flow|span (page-level continuous column flow with
   * INLINE headings) instead of the legacy #159 per-section island. Default
   * FALSE ⇒ no page-flow box; every section is an "island" — the continuous
   * (non-paged) fallback and all single-column presets are byte-stable (the
   * page-flow markers/CSS never engage). The page-flow CSS is additionally gated
   * on the `[data-report-paged]` ancestor (stamped by pagedjs on the deck), so
   * the multicol geometry only engages once the body is actually paginated.
   */
  pageFlowActive?: boolean;
  /**
   * W2 (e) — gate-hint UNIFICATION. The AUTHORITATIVE body-column count resolved
   * by PaginatedReportView (via the SHARED `resolveBodyColumns`). When provided
   * it OVERRIDES this component's own `resolveBodyColumns(...)` derivation so the
   * export host (which decides `pageFlowActive`) and the rendered body box CANNOT
   * disagree about whether the page-flow box engages — the mismatch that left a
   * `data-page-flow="2"` root with no `[data-page-flow-box]` descendant (no flow,
   * silent island fallback). Both derivations call the same resolver on the same
   * template+theme, so this is normally identical; passing it explicitly makes
   * the agreement structural rather than coincidental. Absent ⇒ derive locally
   * (the standalone / continuous-viewer path, byte-stable).
   */
  bodyColumnsHint?: 1 | 2;
  /**
   * RC-2 (2-Pass Layout): the map of `[data-export-id]` -> `fitMaxHeight` (px)
   * generated by the paginator during pass 1. Passed down via context so
   * specific `ChartFrame` boundaries can tighten their reserved height to
   * prevent page-wrap white gaps without us needing to thread it through every
   * section component.
   */
  chartShrinkMap?: Record<string, number>;
  /** Generation-time immutable artifact rules; absent for legacy surfaces. */
  immutableArtifactMode?: boolean;
  /**
   * E0 SPIKE (export rewrite) — TOC page numbers READ BACK from a real PDF by
   * the native-print two-pass driver. When present it replaces the page-map
   * ESTIMATE (`templatePageBreakPlan.pageMap`) as the TableOfContents
   * `pageNumbers` source. Undefined ⇒ byte-identical to today.
   */
  tocPageNumbersOverride?: Record<string, number>;
}

/**
 * #34 P2 STAGE 2 — props for the UNIFIED renderer. Both report surfaces (the
 * in-app reader and the PDF/export tree) now render through ONE component,
 * `SiteRagReportRenderer`, parameterized by `surface`. `SiteRagReportView`
 * (`surface='view'`) and `SiteRagPrintExport` (`surface='export'`) are thin
 * wrappers over it. `surface` drives the per-surface presentation deltas that
 * the two trees previously hand-synced (the eyebrow-strip label case via
 * `labelClass`/`resolveReportPresentation`); the pervasive audience branching
 * stays keyed on the EXISTING `audienceMode === 'export'` signal, which the
 * export surface drives through `viewMode='export'` exactly as before.
 */
interface SiteRagReportRendererProps extends SiteRagReportViewProps {
  /** Render surface — 'view' (in-app reader) or 'export' (PDF/print/DOCX tree). */
  surface: 'view' | 'export';
}

/**
 * Section-heading chrome derived from `theme.headingScale`, so the six theme
 * presets read as DISTINCT design systems (not the same `text-xl` recoloured).
 *
 * The SIZE / WEIGHT / RULE / SPACING vary by scale; the COLOUR + FONT still
 * come from `theme.headingColor` / `theme.headingFont` (editorial uses the
 * brand `accent` role for its hairline rule). All three are pure CSS-class +
 * inline-style — every `<h2>` keeps its `data-heading`, `data-testid`,
 * EditableHeading `headingKey`, and section id untouched.
 *
 *   - editorial (Journal / Quarterly / Monograph — journal voice): a large
 *     display heading (`text-2xl`, tight tracking) over a THIN full-width
 *     accent hairline, generous bottom padding. Reads like a periodical
 *     section opener.
 *   - technical (Institutional): the balanced reference treatment
 *     (`text-xl`, a crisp 2px heading-colour rule) — the original look.
 *   - compact (Review / Proceedings — IEEE/ACM density): a small, UPPERCASE,
 *     wide-tracked heading over a tight thin rule, reading like a numbered
 *     conference-paper section head.
 *
 * `mbClass` lets the two call sites that previously appended `mb-6` keep it.
 */
function resolveReportHeadingScale(
  scale: ReportTheme['headingScale'] | undefined,
): ReportTheme['headingScale'] {
  return scale === 'editorial' || scale === 'technical' || scale === 'compact'
    ? scale
    : 'editorial';
}

function reportSectionHeadingChrome(
  theme: ReportTheme,
): { h2Class: string; textClass: string; h2Style: React.CSSProperties } {
  const scale = resolveReportHeadingScale(theme.headingScale);
  // PART K1 — drive the heading SIZE + leading through the ONE shared report
  // type scale (`reportTypographySizes`) via an inline `fontSize`, so the
  // keystone view head densifies to the redesign's hierarchy in lockstep with
  // the router/shell heads (which already resolve through the same scale).
  // The Tailwind CLASS keeps the weight/tracking/border treatment (so the
  // reportSurfaceParity class-skeleton oracle is unaffected — it serializes
  // classes, not inline style); the inline `fontSize` wins over the residual
  // `text-*` size class. Editorial uses the big serif DISPLAY tier (the
  // redesign's prominent section head); technical the H2 tier; compact stays
  // the small uppercase wide-tracked head (eyebrow tier).
  const sizes = reportTypographySizes(theme);
  const baseStyle: React.CSSProperties = {
    color: reportHeadingInk(theme),
    fontFamily: theme.headingFont,
    breakAfter: 'avoid',
    lineHeight: REPORT_LINE_HEIGHT.heading,
  };
  switch (scale) {
    case 'editorial':
      // Refined journal display head: lighter font weight, tighter tracking, subtle bottom padding, thin accent hairline
      return {
        h2Class: 'text-2xl font-semibold tracking-tight border-b pb-4',
        textClass: 'text-2xl font-semibold tracking-tight',
        h2Style: { ...baseStyle, fontSize: sizes.display, borderColor: roleColor(theme, 'accent') },
      };
    case 'compact':
      // Dense conference-paper section head: small, uppercase, wide-tracked.
      return {
        h2Class: 'text-sm font-bold uppercase tracking-[0.14em] border-b pb-1.5',
        textClass: 'text-sm font-bold uppercase tracking-[0.14em]',
        h2Style: { ...baseStyle, fontSize: sizes.eyebrow, lineHeight: REPORT_LINE_HEIGHT.eyebrow, borderColor: reportHeadingInk(theme) },
      };
    case 'technical':
    default:
      // Balanced reference treatment — byte-identical to the legacy literals.
      return {
        h2Class: 'text-xl font-bold border-b-2 pb-2',
        textClass: 'text-xl font-bold',
        h2Style: { ...baseStyle, fontSize: sizes.h2, borderColor: reportHeadingInk(theme) },
      };
  }
}

/**
 * emptyStateNoteProps — the ONE source for the inline "insufficient data" /
 * graceful-omit note rendered inside a section body (gridStudy / transportStudy
 * / connectivityStudy / riskAssessment / actionPlan).
 *
 * N1.5C-flat (EDITORIAL-MODE ONLY) — when `editorialActive` is ON the note reads
 * as an italic muted note on the cream page behind a 3px accent LEFT-rule (the
 * exact treatment ported from `sections/SectionShell.tsx`, routed through the
 * shared `editorialFlat()` seam), instead of the grey `bg-surface-2/50`-filled,
 * fully-bordered box. Flag OFF returns the EXACT legacy className + no inline
 * style, so the flag-OFF DOM/CSS is byte-identical. `mt` toggles the historical
 * `mt-4` top margin some call sites carried.
 */
function emptyStateNoteProps(
  editorialActive: boolean,
  theme: ReportTheme,
  mt: boolean,
): { className: string; style?: React.CSSProperties } {
  if (editorialActive) {
    const flat = editorialFlat(theme.density === 'compact', {
      rule: roleColor(theme, 'rule'),
      accent: roleColor(theme, 'accent'),
      muted: roleColor(theme, 'muted'),
    });
    return {
      className: `text-xs italic px-3 py-2${mt ? ' mt-4' : ''}`,
      style: { ...flat.leftRule, color: roleColor(theme, 'muted') },
    };
  }
  return {
    className: `text-xs text-text-muted italic px-3 py-2 border border-border-subtle bg-surface-2/50${mt ? ' mt-4' : ''}`,
  };
}

/**
 * sectionBlockChrome — the SINGLE theme-adaptive layout source for the
 * report-BODY content sub-blocks (per-tech BTM entries, the study
 * assessment / recommendation pairs). It is the structural sibling of
 * `reportSectionHeadingChrome`: instead of one boxy `border border-border`
 * card on every preset, the sub-block treatment ADAPTS by
 * `theme.headingScale` (+ `theme.density`) so each preset reads as a
 * structurally DIFFERENT design system, matching the editorial primitives
 * (EditorialSubsection / EditorialRecommendations) the same blocks route
 * through:
 *
 *   - editorial (Journal / Quarterly / Monograph): NO bordered box. A
 *     journal-article flow — a hairline TOP rule, generous whitespace, the
 *     label small-caps mono, body as flowing prose. Per-tech "cards" become
 *     hairline-separated entries; the GREEN/AMBER/RED badge floats with the
 *     label, not inside a box.
 *   - technical (Institutional): a LIGHT structured frame — a thin LEFT
 *     accent rule + subtle inset, crisp but not a heavy card.
 *   - compact (Review / Proceedings — IEEE/ACM density): DENSE, minimal
 *     chrome — a tight thin TOP hairline, smallest type, no card padding.
 *
 * Every rule/ink reads roleColor(theme,…) — no hardcoded hex.
 * `entryStyle` frames a single per-tech entry; `labelStyle`
 * is the run-in small-caps label; `defLabelStyle`/`defBodyStyle` style the
 * ASSESSMENT / RECOMMENDED ACTION definition flow. The recommendations
 * accent box is rendered via <EditorialRecommendations> (same source).
 */
function sectionBlockChrome(theme: ReportTheme): {
  scale: ReportTheme['headingScale'];
  /** Per-tech entry frame (editorial = hairline top, technical = left rule, compact = tight top). */
  entryStyle: React.CSSProperties;
  /** Run-in tech-name label (small-caps mono). */
  labelStyle: React.CSSProperties;
  /** Header row holding the label + floating status badge. */
  headerRowStyle: React.CSSProperties;
  /** Definition-flow sub-label (ASSESSMENT / RECOMMENDED ACTION). */
  defLabelStyle: React.CSSProperties;
  /** Definition-flow body prose. */
  defBodyStyle: React.CSSProperties;
  /** Gap between the assessment / action definition pair. */
  defGroupGap: number;
} {
  const scale = resolveReportHeadingScale(theme.headingScale);
  const dense = theme.density === 'compact';
  const MONO = 'var(--report-body-font, inherit)';

  let entryStyle: React.CSSProperties;
  let labelStyle: React.CSSProperties;
  let defLabelStyle: React.CSSProperties;
  let defBodyStyle: React.CSSProperties;
  let defGroupGap: number;

  if (scale === 'technical') {
    // William QA 2026-06-28 — left accent rule removed report-wide
    entryStyle = {
      paddingTop: dense ? 4 : 6,
      paddingBottom: dense ? 4 : 6,
    };
    labelStyle = { fontSize: 13, fontWeight: 700, color: roleColor(theme, 'ink') };
    defLabelStyle = {
      fontFamily: MONO, fontSize: 10, fontWeight: 600, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: roleColor(theme, 'muted'), marginBottom: 3,
    };
    defBodyStyle = { fontSize: dense ? 12.5 : 13, lineHeight: 1.55, color: roleColor(theme, 'body') };
    defGroupGap = dense ? 8 : 10;
  } else if (scale === 'compact') {
    // Dense — tight thin TOP hairline, smallest type, no padding bloat.
    entryStyle = {
      borderTop: `1px solid ${roleColor(theme, 'hair')}`,
      paddingTop: dense ? 5 : 6,
    };
    labelStyle = { fontSize: 12, fontWeight: 700, color: roleColor(theme, 'ink') };
    defLabelStyle = {
      fontFamily: MONO, fontSize: 9, fontWeight: 600, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: roleColor(theme, 'muted'), marginBottom: 1,
    };
    defBodyStyle = { fontSize: 12, lineHeight: 1.45, color: roleColor(theme, 'body') };
    defGroupGap = dense ? 4 : 6;
  } else {
    // editorial — journal-article flow: hairline TOP rule, generous lead.
    entryStyle = {
      borderTop: `1px solid ${roleColor(theme, 'rule')}`,
      paddingTop: dense ? 10 : 14,
    };
    labelStyle = { fontSize: 14, fontWeight: 700, color: roleColor(theme, 'ink') };
    defLabelStyle = {
      fontFamily: MONO, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.14em',
      textTransform: 'uppercase', color: roleColor(theme, 'muted'), marginBottom: 4,
    };
    defBodyStyle = { fontSize: 13.5, lineHeight: 1.6, color: roleColor(theme, 'body') };
    defGroupGap = dense ? 8 : 12;
  }

  const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: defGroupGap,
  };

  return { scale, entryStyle, labelStyle, headerRowStyle, defLabelStyle, defBodyStyle, defGroupGap };
}

/**
 * B1 — PageFlowBox: the single CONTINUOUS page-level two-column flow container.
 *
 * When `engaged` (a two-column academic preset mounted in the paged deck — i.e.
 * `pageFlowEngaged`), it renders ONE real `[data-page-flow-box]` element around
 * ALL post-cover body sections. The page-flow CSS (gated
 * `[data-report-paged][data-page-flow="2"] [data-page-flow-box]`) turns that box
 * into `column-count: 2; column-fill: auto`, so the body flows top-to-bottom
 * across section seams instead of every section owning its own
 * `column-count: 2` island (the legacy #159 path). Prose sections classify as
 * `data-section-flow="flow"` (inline heading, packs with following lines); wide
 * charts/tables/banners/TOC classify as `data-section-flow="span"`
 * (`column-span: all`); cards/callouts carry `break-inside: avoid`.
 *
 * When NOT engaged it renders a bare Fragment — NO wrapper element, NO marker —
 * so single-column presets and the continuous (non-paged) fallback are
 * byte-identical to before (each section keeps its per-section island geometry).
 * The cover is rendered by the caller OUTSIDE this box (single full-bleed page).
 */
export const PageFlowBox: React.FC<{ engaged: boolean; children: React.ReactNode }> = ({
  engaged,
  children,
}) => {
  if (!engaged) return <>{children}</>;
  // `display: contents` so the box is transparent to the report-root flex
  // layout EXCEPT for the multicol it establishes: the CSS rule sets
  // column-count/fill on this element. We do NOT set flex here — multicol flows
  // its children in DOM order (CSS `order` does not apply inside a multicol
  // container), which is the intended continuous top-to-bottom fill.
  return <div data-page-flow-box="">{children}</div>;
};

// BTM-4 (v1.2.0) — `phasingRiskToDetailedRisk` maps a derived PhasingRisk to the
// report's DetailedRisk shape so phasing/expanded-intent risks render in the same
// developmentRisks register as the AI/deterministic risks.
//
// #34 P2 STAGE B — the mitigation map + mapper + the `mergeDevelopmentRisks`
// helper now live in `lib/phasingRisks` so the lifted feasibility Risk Assessment
// registry section can re-derive the merged risk set WITHOUT importing this view
// (render-tree import cycle). Re-exported here for the existing
// SiteRagReportView.phasingRiskMerge test.
export { phasingRiskToDetailedRisk } from '../../lib/phasingRisks';

/**
 * #34 P2 STAGE 2 — the UNIFIED report renderer. This is the former
 * `SiteRagReportView` body, now surface-parameterized so the in-app reader
 * (`surface='view'`) and the PDF/export tree (`surface='export'`) render
 * through ONE source of truth instead of two hand-synced files (the drift that
 * silently dropped `capacityCreditDeepDive` from feasibility PDFs — 9c7c7990).
 *
 * Exported so the `SiteRagPrintExport` wrapper can mount it with
 * `surface='export'`. The pervasive audience branching is unchanged — it stays
 * keyed on `audienceMode === 'export'`, which the export surface drives via
 * `viewMode='export'`. `surface` adds only the per-surface label-case choice
 * (the 14 eyebrow strips route through `labelClass(labelTransform)`), and
 * `resolveReportPresentation` is resolved for `surface` so its `labelTransform`
 * token de-uppercases on export. The whole collapse is gated by
 * `isReportEngineUnifiedEnabled()` in the wrappers below — flag-OFF keeps the
 * legacy forked export body, so STAGE 2 is fully reversible.
 */
export const SiteRagReportRenderer: React.FC<SiteRagReportRendererProps> = ({
  surface,
  report: reportProp,
  viewMode: viewModeProp,
  isAdminOverride,
  headingOverrides: headingOverridesProp,
  exportSite: exportSiteProp,
  exportSed: exportSedProp,
  studioPreview = false,
  pageFlowActive = false,
  bodyColumnsHint,
  chartShrinkMap = {},
  immutableArtifactMode = false,
  tocPageNumbersOverride,
}) => {
  // NB-2 — lazy render-time hygiene backfill. Reports generated BEFORE
  // the RNH-5 / MEL / KFH-3 / WPG packs shipped still carry pollution
  // in their persisted body. `applyHygieneBackfill` composes the
  // gen-time strips on read so the view gets a cleaned copy WITHOUT
  // touching Firestore. Identity short-circuits on already-clean
  // reports so memoised selectors downstream stay hot.
  //
  // Memo key folds in `HYGIENE_VERSION` (NB-3) so a strip-rule bump
  // invalidates every render-side memo on next read. We key off the
  // `reportProp` reference (not just `reportProp.id`) so an
  // in-session re-fetch that produces a new report object also
  // re-strips — covers the case where the editor save-cycle hands the
  // view a fresh persisted copy.
  const hygienedReport = React.useMemo<SiteRagReport>(() => {
    // Immutable historical content cannot be repaired against today's rules.
    // Keep any presentation cleanup separate from its validated source body.
    if (isImmutableReportReadSource(reportProp)) return reportProp;
    const { report: cleaned, applied } = applyHygieneBackfill(reportProp);
    // NB-2 — annotate the in-memory report with a backfill marker so
    // the admin chip (NB-4) can surface "Cleaned at render". Append
    // rather than overwrite — the gen-time hygiene helpers stamp their
    // OWN qualityWarnings entries we must preserve. Guard against
    // `qualityWarnings` being absent on very-old (pre-RNH) reports.
    if (applied.length === 0) {
      return cleaned;
    }
    const existing = Array.isArray(cleaned.qualityWarnings) ? cleaned.qualityWarnings : [];
    return {
      ...cleaned,
      qualityWarnings: [
        ...existing,
        // Discriminated entry the renderer chip filters on (NB-4).
        // `count` mirrors `applied.length` so the union shape stays
        // uniform with the other variants (existing tests assert
        // `.count` without narrowing on `type`).
        { type: 'backfill-strip-applied' as const, count: applied.length, applied },
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- HYGIENE_VERSION
    // is a module-level constant; React's lint rule flags it as
    // unnecessary, but including it makes the version pin's effect
    // visible at the call site.
  }, [reportProp, HYGIENE_VERSION]);
  const reportRef = useRef<HTMLDivElement>(null);
  // SLK-H4 / SLK-T2 — read theme through useEffectiveReportTheme so the
  // Studio's <ThemeOverrideProvider> preview can override the global
  // store theme for this subtree without mutating Zustand. Default
  // behaviour (no provider) returns the store theme unchanged.
  const { updateRagReport, companyId, approvalWorkflow, companyRole } = useStore();
  const userRole = useStore(s => s.userRole);
  const baseTheme = useEffectiveReportTheme();
  // Reader color mode — the ON-SCREEN report follows the app's light/dark
  // toggle (`useAppResolvedTheme` observes the `<html>` class); EXPORT is
  // resolved to 'light' below so PDF/print/DOCX/paged stay white. Combined
  // with `audienceMode` at the `theme` memo (audienceMode is declared later
  // but is in scope by then).
  const appResolvedTheme = useAppResolvedTheme();
  // Imported KMZ overlays for the site map. The legend's hidden-categories state lives
  // inside SiteMapLayers — viewer-only override per the spec, no persistence.
  // X-USER-VIEW — resolve the report's siteId + owner uid from the report BODY
  // OR, when the body is a GCS render payload that omits them, the store's
  // reports INDEX doc (fetchRagReports stamps every index doc with `siteId` +
  // the transient `_ownerUid`). Without this fallback an admin opening a
  // sub-company report whose body lacks `siteId` could never resolve its site
  // (blank map + missing SED) even after the site is loaded.
  const reportIndexDoc = useStore(s => hygienedReport.id ? s.siteRag.reports.find(r => r.id === hygienedReport.id) : undefined);
  const resolvedSiteId = hygienedReport.siteId
    ?? (reportIndexDoc as { siteId?: string } | undefined)?.siteId;
  const reportOwnerUid = (reportProp as { _ownerUid?: string })._ownerUid
    ?? (reportIndexDoc as { _ownerUid?: string } | undefined)?._ownerUid;
  const reportSiteFromStore = useStore(s => resolvedSiteId ? s.siteRag.sites.find(x => x.id === resolvedSiteId) : undefined);
  const reportSite = exportSiteProp ?? reportSiteFromStore;
  // CROSS-USER FRESHNESS — when this is a foreign-owned (cross-user) report
  // (`reportOwnerUid` is stamped only for company-shared reports the viewer
  // doesn't own), fetch the owner's site DIRECTLY by (ownerUid, siteId) on every
  // open. We do NOT skip when a store copy already exists: a cross-user site
  // arrives once via the company sweep and has NO live listener, so it goes
  // stale (e.g. the owner rebuilt the SED to v4 'complete' but this session
  // still holds the old 'failed' snapshot → the viewer wrongly showed "rebuild
  // your SED"). The fresh getDoc overwrites the stale copy so reportSiteFromStore
  // reflects the owner's current state (SED status/version, boundary, map). The
  // direct getDoc is authorised by the same per-doc sites rule (the collectionGroup
  // LIST sweep degrades to self-only — its get()-based isInCompanyTree isn't
  // query-safe). Skipped on export (gets exportSiteProp) and for same-user reports
  // (no reportOwnerUid → own sites already have a live listener).
  const ensureReportOwnerSiteLoaded = useStore(s => s.ensureReportOwnerSiteLoaded);
  React.useEffect(() => {
    if (exportSiteProp) return;
    if (resolvedSiteId && reportOwnerUid) {
      void ensureReportOwnerSiteLoaded(resolvedSiteId, reportOwnerUid);
    }
  }, [exportSiteProp, resolvedSiteId, reportOwnerUid, ensureReportOwnerSiteLoaded]);
  const reportSiteLayers = reportSite?.siteLayers ?? [];
  // Brand logo (feat/org-logo) — the EFFECTIVE uploaded logo (user → org)
  // feeds resolveReportBranding's `effectiveLogoUrl` so the cover's
  // `template → theme → effective → default` chain picks it up unchanged.
  const brandUserLogoUrl = useStore(s => s.userLogoUrl);
  const brandOrgLogoUrl = useStore(s => s.orgLogoUrl);
  // Template-link dropdown (NOT-LINKED chip) — the link mutation the Studio
  // uses, so a user can link/change the template straight from the report
  // header. (`reportTemplates`, the saved-templates list, is selected below.)
  const applyTemplateToExistingReports = useStore(s => s.applyTemplateToExistingReports);
  // CB2 — load the LIVE site SED so the header status chip's conflicts state
  // (`pickReportAlert` high-conflicts) can derive its count from the current
  // state instead of the frozen gen-time snapshot stamped on
  // `report.unresolvedConflicts`. `liveSiteSedNonce` bumps when
  // EvidenceConflictsPanel saves a conflict resolution (window event below),
  // so the chip updates without a page reload.
  const [liveSiteSedNonce, setLiveSiteSedNonce] = React.useState(0);
  const [liveSiteSed, setLiveSiteSed] = React.useState<import('../../types/siteEvidence').SiteEvidenceDocument | null>(null);
  React.useEffect(() => {
    const onUpdate = () => setLiveSiteSedNonce(n => n + 1);
    window.addEventListener('sed-conflicts-updated', onUpdate);
    return () => window.removeEventListener('sed-conflicts-updated', onUpdate);
  }, []);

  // TPN-5 (2026-05-18) — mirror every `[data-section]` wrapper's id
  // onto a `data-section-id` attribute so the print-mode page-count
  // measurer (`measurePagesFromDom`) and any future PagedJS hook
  // can introspect rendered sections without colliding with the
  // existing `data-section` selector that the print stylesheet uses
  // for `page-break-before: always`. Splitting the attributes lets
  // both consumers coexist; this is a NO-OP at print time (CSS doesn't
  // know about `data-section-id`).
  //
  // Runs after every render so newly-mounted sections (e.g. async
  // chart sub-trees) pick up the mirror. Bails early when the root
  // isn't mounted (initial render).
  React.useEffect(() => {
    const root = reportRef.current;
    if (!root) return;
    const wrappers = root.querySelectorAll<HTMLElement>('[data-section]');
    wrappers.forEach((el) => {
      const id = el.getAttribute('data-section');
      if (!id) return;
      if (el.getAttribute('data-section-id') !== id) {
        el.setAttribute('data-section-id', id);
      }
    });
  });

  // W2 (d) — BACKFILL `data-section-flow` on in-box sections that lack it, and
  // emit a dev-mode warning when a `data-page-flow="2"` root has no
  // `[data-page-flow-box]` descendant (export host ⇄ rendered box gate-hint
  // mismatch). Gated on pageFlowEngaged; runs after every render so async-mounted
  // sections (chart sub-trees) pick up the flow marker.
  //
  // ⚠️ The former DOM REORDER (`appendChild`-moving the live `[data-section]`
  // nodes into resolved `order`) was REMOVED from here. Mutating React-managed
  // nodes out-of-band desynced the fiber tree from the DOM: the next reconcile
  // that touched them — linking OR SWITCHING the report's template, which
  // changes sectionOrder / coverLayout / pageFlowEngaged — then threw
  // "Failed to execute 'removeChild' on 'Node': The node to be removed is not a
  // child of this node." The deck/export ordering now happens on the DETACHED
  // pagedjs clone via `reorderClonedSectionsByFlowOrder` (renderPagedDeck), so
  // React's tree is never reordered behind its back. The remaining work here is
  // attribute-only (setAttribute), which cannot desync child bookkeeping.
  React.useEffect(() => {
    if (!pageFlowEngaged) return;
    const root = reportRef.current;
    if (!root) return;
    const box = root.querySelector<HTMLElement>('[data-page-flow-box]');
    if (!box) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn(
          '[SiteRagReportView] data-page-flow="2" is engaged but no ' +
          '[data-page-flow-box] descendant was found — the page-flow box did ' +
          'not render, so the two-column page flow will fall back to per-section ' +
          'islands. This indicates the export host and the rendered box disagree ' +
          'on bodyColumns (W2 gate-hint mismatch).',
        );
      }
      return;
    }
    // ROUND-3 DEFECT-A — BACKFILL data-section-flow on any in-box section that
    // lacks it. The feasibility inline blocks + the NewTypeSectionsRouter stamp
    // data-section-flow at render, but the LEGACY specialized renderers
    // (EnvironmentalSections / PowerSections / WaterSections / TestFitSections /
    // InterconnectionSections) emit bare `<div data-section="…">` with NO flow
    // marker, so every non-feasibility legacy report had ZERO flow sections and
    // its prose defaulted to column-span:all (dead 2-col). Stamping here — the
    // single chokepoint that runs over the export host BEFORE pagedjs clones it
    // into the deck — covers EVERY renderer at once: a column-capable prose
    // section resolves to "flow", a chart/card/wide section to "span". The
    // post-layout reflow then packs the flow prose into the two page columns.
    // Skips the cover and any node already carrying the attribute (idempotent).
    box.querySelectorAll<HTMLElement>('[data-section]').forEach((el) => {
      const id = el.getAttribute('data-section');
      if (!id || id === 'cover') return;
      if (el.getAttribute('data-section-flow')) return;
      const mode = resolveSectionFlowModeForSection(id, pageFlowEngaged, report.reportType);
      el.setAttribute('data-section-flow', mode);
    });

    // NOTE: resolved-order section sorting used to happen HERE via
    // `appendChild`. It now runs on the detached pagedjs clone in
    // `reorderClonedSectionsByFlowOrder` (renderPagedDeck) so the live React
    // tree is never reordered out-of-band. See the header comment above.
  });

  React.useEffect(() => {
    if (exportSedProp !== undefined) return;
    let cancelled = false;
    setLiveSiteSed(null);
    if (!reportSite?.sedId) return;
    const expectedSiteId = reportSite.id;
    const expectedSedId = reportSite.sedId;
    (async () => {
      try {
        const { loadSedById } = await import('../../services/sedLoader');
        const doc = await loadSedById(expectedSedId, reportSite.sedStoragePath, reportSite.sedVersion);
        if (
          !cancelled &&
          (!doc || ((!expectedSiteId || !doc.siteId || doc.siteId === expectedSiteId) && (!doc.id || doc.id === expectedSedId)))
        ) {
          setLiveSiteSed(doc);
        }
        if (!doc) {
          // A silent null here surfaced downstream as the unactionable
          // "Site evidence is not loaded — reopen the report and try again"
          // in the truth-resolution dialog (reopening re-runs the same
          // failing load). Name the cause in the console for triage.
          console.warn(
            `[report-view] SED ${expectedSedId} resolved to null (path=${reportSite.sedStoragePath ?? 'none'}, v=${reportSite.sedVersion ?? '?'}) — value corrections will be unavailable.`,
          );
        }
      } catch (e) {
        console.warn('[report-view] SED load failed:', (e as Error)?.message);
        if (!cancelled) setLiveSiteSed(null);
      }
    })();
    return () => { cancelled = true; };
    // sedVersion in the deps catches store-driven SED bumps; nonce
    // catches in-session resolutions that don't write back to the
    // store (EvidenceConflictsPanel persists to GCS directly).
  }, [reportSite?.id, reportSite?.sedId, reportSite?.sedStoragePath, reportSite?.sedVersion, liveSiteSedNonce, exportSedProp]);
  const effectiveLiveSed = exportSedProp !== undefined
    ? exportSedProp
    : (liveSiteSed
      && (!reportSite?.sedId || !liveSiteSed.id || liveSiteSed.id === reportSite.sedId)
      && (!reportSite?.id || !liveSiteSed.siteId || liveSiteSed.siteId === reportSite.id)
      ? liveSiteSed
      : null);
  const report = React.useMemo(() => {
    const type = (hygienedReport.reportType ?? 'feasibility') as ReportTypeId;
    const withFallbacks = (isImmutableReportReadSource(hygienedReport) || !reportSite || !effectiveLiveSed)
      ? hygienedReport
      : applySectionFallbacksToReport(
        type,
        hygienedReport as unknown as Record<string, unknown>,
        reportSite,
        effectiveLiveSed,
      ) as unknown as SiteRagReport;
    // R-2 + HR-QA — `prepareReportForRender` scrubs internal failure sentinels
    // ("… unavailable — content failed quality gate. Regenerate report.") AND
    // strips narrative sections whose body is only unavailable sentinel text.
    // Render-time shallow copy, no Firestore write. The SAME chokepoint runs in
    // PrintReportView so the viewer and the server PDF are byte-identical
    // (filterUnavailableSections internally scrubs too — idempotent).
    return prepareReportForRender(projectReportSourceEvidence(withFallbacks));
  }, [hygienedReport, reportSite, effectiveLiveSed]);
  const actionReport = sourceForReportActions(reportProp, report);
  const reportSiteWithSed = React.useMemo(
    () => reportSite ? { ...reportSite, sed: effectiveLiveSed } : undefined,
    [reportSite, effectiveLiveSed],
  );
  const requiresLegacySimulationRegeneration = React.useMemo(
    () => hasRetiredSimulationReference(actionReport),
    [actionReport],
  );
  /*
   * Truth questions (2026-08-05). A held report carries an answerable question
   * per truth violation. Audit now owns the settlement UI; it writes SED
   * amendments so a later regeneration uses corrected evidence rather than
   * re-running the identical prompt against the identical inputs, which is
   * what made the live 2026-08-04 Redhead holds unrecoverable.
   */
  const storeAmendments = useStore(s => s.amendmentState?.amendments);
  const currentSedId =
    reportSite?.sedId ??
    reportSite?.sed?.id ??
    (exportSedProp !== undefined ? exportSedProp?.id : undefined) ??
    reportSite?.sedAmendmentHistory?.find(a => a.status === 'applied')?.sedId;
  const currentSedVersion =
    typeof reportSite?.sedVersion === 'number'
      ? reportSite.sedVersion
      : typeof reportSite?.sed?.version === 'number'
        ? reportSite.sed.version
        : exportSedProp !== undefined && typeof exportSedProp?.version === 'number'
          ? exportSedProp.version
          : undefined;
  const isEffectiveLiveSedCurrent = Boolean(
    effectiveLiveSed &&
      typeof effectiveLiveSed.id === 'string' &&
      effectiveLiveSed.id.trim().length > 0 &&
      currentSedId &&
      effectiveLiveSed.id === currentSedId &&
      typeof currentSedVersion === 'number' &&
      typeof effectiveLiveSed.version === 'number' &&
      effectiveLiveSed.version === currentSedVersion,
  );
  const combinedAmendments = React.useMemo(
    () => [
      ...(isEffectiveLiveSedCurrent
        ? (effectiveLiveSed?.amendments ?? []).map(a => ({
            ...a,
            sedId: effectiveLiveSed?.id,
            sedVersion: effectiveLiveSed?.version,
          }))
        : []),
      ...(reportSite?.sedAmendmentHistory ?? []).filter(
        a =>
          Boolean(
            a &&
              currentSedId &&
              a.sedId === currentSedId &&
              (typeof currentSedVersion !== 'number' ||
                typeof a.sedVersion !== 'number' ||
                a.sedVersion <= currentSedVersion),
          ),
      ),
      ...(storeAmendments ?? []).filter(
        a =>
          Boolean(
            a &&
              currentSedId &&
              a.sedId === currentSedId &&
              (typeof currentSedVersion !== 'number' ||
                typeof a.sedVersion !== 'number' ||
                a.sedVersion <= currentSedVersion),
          ),
      ),
    ],
    [
      isEffectiveLiveSedCurrent,
      effectiveLiveSed,
      reportSite?.sedAmendmentHistory,
      storeAmendments,
      currentSedId,
      currentSedVersion,
    ],
  );
  const truthQuestions = React.useMemo(
    () => filterUnsettledTruthQuestions(
      report.qaFindings?.truthQuestions,
      reportSite?.auditOverrides,
      report.reportType,
      combinedAmendments,
      currentSedId,
      currentSedVersion,
      report.qaFindings as Record<string, unknown> | undefined,
    ),
    [
      report.qaFindings,
      reportSite?.auditOverrides,
      report.reportType,
      combinedAmendments,
      currentSedId,
      currentSedVersion,
    ],
  );
  // BTM-4 (v1.2.0) — merge derived phasing/expanded-intent risks into the
  // developmentRisks register. derivePhasingRisks was implemented but had ZERO
  // consumers; this surfaces it in the Risk Assessment section. Keyed on the
  // gen-time intent snapshot (projectIntentAtGen) with a live fallback. The
  // report's own developmentRisks come FIRST so AI/deterministic risks lead;
  // phasing ids are distinct, so no dedup is needed.
  const mergedDevelopmentRisks = React.useMemo<DetailedRisk[]>(
    () => mergeDevelopmentRisks(report),
    [report],
  );
  const displayExecutiveSummary = React.useMemo(() => {
    const text = report.executiveSummary?.trim() ?? '';
    if (!text) return '';
    if (report.sectionGuardStatus?.['executiveSummary']) return text;
    return detectInstructionShape(text).flagged ? '' : text;
  }, [report.executiveSummary, report.sectionGuardStatus]);
  // AG2 — `isAdminOverride` lets the headless print tab pass through
  // the admin claim from its signed token (the store has no
  // `companyRole` there). Interactive mounts leave it undefined and
  // the store wins.
  const isAdmin = isAdminOverride ?? (companyRole === 'admin');
  // Generation COST telemetry (dollar figures) is Datacrate internal — it
  // must NOT leak to a tenant's own company-admin. `isAdmin` above is the
  // COMPANY-admin check (companyRole==='admin') and correctly gates tenant
  // admin features (templates, exports, audit panels). Cost surfaces instead
  // gate on the PLATFORM-admin predicate (isDatacrateAdmin: platform `role`
  // claim OR root-company admin). The headless print path passes through the
  // signed token's platform-admin claim via `isAdminOverride`, so honour it.
  const isPlatformAdmin = isAdminOverride
    ?? isDatacrateAdmin({ userRole, companyId, companyRole });
  const [isEditing, setIsEditing] = React.useState(false);
  // EV1 (2026-05-15) — `showEvidenceBrowser` was the toggle for the
  // inline <SEDBrowserPanel> mount that lived above the cover. Removed
  // along with the panel itself; the canonical surface is the dedicated
  // Site Evidence tab, reached via the always-on breadcrumb (EV2) in
  // ReportHeaderStrip. Per-claim citation popovers (CT6) replace the
  // "browse evidence while reading" workflow with something focused.
  const [showRerunPanel, setShowRerunPanel] = React.useState(false);
  // When the user explicitly closes the rerun panel (Continue in
  // background, X, Esc), suppress the route-bridge effect from
  // immediately re-opening while navigate(-1) is still in flight and
  // the URL still matches `/rerun`.
  const rerunPanelDismissedRef = useRef(false);

  // ── RWX-5 — rerun route bridge ────────────────────────────────────
  //
  // `/sites/:siteId/reports/:reportId/rerun` is the canonical
  // deep-link for the rerun surface. The route falls through to the
  // App shell (see main.tsx) so the underlying store / viewMode
  // remains in charge of the current report; this hook reads the
  // URL on mount + on every navigation and mirrors the match into
  // `showRerunPanel` so the panel opens automatically when the URL
  // matches. Browser back from the rerun URL clears the match → the
  // panel unmounts. The callback paths below (`onRerun` props) push
  // the URL via `useNavigate`; the legacy `setShowRerunPanel(true)`
  // path stays as a back-compat short-circuit for callers that
  // haven't migrated yet.
  const rerunRouteLocation = useLocation();
  const rerunRouteNavigate = useNavigate();
  const rerunRouteMatchesThisReport = React.useMemo(() => {
    // Match `/sites/<id>/reports/<id>/rerun[/...]` — only opens this
    // panel when the report id segment matches our `report.id`.
    const m = rerunRouteLocation.pathname.match(/^\/sites\/([^/]+)\/reports\/([^/]+)\/rerun(?:\/|$)/);
    return !!(m && m[2] === report.id);
  }, [rerunRouteLocation.pathname, report.id]);
  React.useEffect(() => {
    if (report.reportType === 'executive-summary'
      || REPORT_TYPE_REGISTRY[report.reportType ?? 'feasibility'].status === 'retired') {
      setShowRerunPanel(false);
      return;
    }
    if (rerunRouteMatchesThisReport) {
      if (!rerunPanelDismissedRef.current) {
        setShowRerunPanel(true);
      }
      return;
    }
    rerunPanelDismissedRef.current = false;
    setShowRerunPanel(false);
  }, [rerunRouteMatchesThisReport, report.reportType]);

  // P3 — audit-panel RESOLUTION-LOOP deep-link SCROLL TARGET.
  //
  // The SiteAuditTab "View detail / Open" action builds a report-reader
  // deep-link of the shape `/sites/<s>/reports/<r>#section-<sectionId>`
  // from a finding's `anchor.sectionId` (src/types/userAudit.ts). The
  // panel navigates with a plain `<a href>` (full navigation), so the
  // report lands with the `#section-<id>` fragment sitting in
  // `window.location.hash`. This effect is the READER half: it reads the
  // hash on mount AND on every subsequent hashchange / router-location
  // change, resolves `#section-<id>` to the matching `[data-section-id]`
  // wrapper (mirrored from `[data-section]` by the TPN-5 effect above),
  // then scrolls it into view and briefly ring-highlights it — reusing
  // the exact scroll+ring pattern the SiteLandingPage focus receiver
  // (EDX/SET-B1) and the H-4 passage-offset deep-links already use
  // (`scrollIntoView({behavior:'smooth',block:'center'})` +
  // `ring-2 ring-warning ring-offset-2`, removed after a short dwell).
  //
  // Additive + non-breaking: a hash that is absent, malformed, or points
  // at a section that did not render (e.g. filtered/unavailable) is a
  // no-op — the report renders exactly as before. We retry on a short,
  // bounded schedule because async sub-trees (charts) and the
  // `data-section-id` mirror run after the initial paint, so the target
  // may not exist on the very first tick.
  const sectionDeepLinkHash = rerunRouteLocation.hash;
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const HIGHLIGHT_CLASSES = ['ring-2', 'ring-warning', 'ring-offset-2'] as const;
    const SECTION_HASH_RE = /^#section-(.+)$/;

    let scheduleTimers: number[] = [];
    let dwellTimer: number | null = null;
    let highlighted: HTMLElement | null = null;

    const clearHighlight = () => {
      if (highlighted) {
        highlighted.classList.remove(...HIGHLIGHT_CLASSES);
        highlighted = null;
      }
      if (dwellTimer != null) {
        window.clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };

    const resolveSectionId = (): string | null => {
      const raw = window.location.hash || '';
      const m = SECTION_HASH_RE.exec(raw);
      if (!m || !m[1]) return null;
      try {
        return decodeURIComponent(m[1]);
      } catch {
        // Malformed percent-encoding — fall back to the raw token.
        return m[1];
      }
    };

    const tryScroll = (sectionId: string): boolean => {
      const root = reportRef.current;
      if (!root) return false;
      let target: HTMLElement | null = null;
      try {
        const safe =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(sectionId)
            : sectionId;
        target = root.querySelector<HTMLElement>(`[data-section-id="${safe}"]`);
      } catch {
        // Bad selector (exotic id without CSS.escape) — best-effort bail.
        return false;
      }
      if (!target) return false;
      try {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch {
        // jsdom / older UAs may not honour scrollIntoView options.
      }
      clearHighlight();
      target.classList.add(...HIGHLIGHT_CLASSES);
      highlighted = target;
      dwellTimer = window.setTimeout(() => {
        if (highlighted) {
          highlighted.classList.remove(...HIGHLIGHT_CLASSES);
          highlighted = null;
        }
        dwellTimer = null;
      }, 2800);
      return true;
    };

    const run = () => {
      // Reset any retry schedule from a prior hash before starting a new one.
      scheduleTimers.forEach((t) => window.clearTimeout(t));
      scheduleTimers = [];
      const sectionId = resolveSectionId();
      if (!sectionId) {
        // Hash cleared / not a section deep-link — drop any live highlight.
        clearHighlight();
        return;
      }
      if (tryScroll(sectionId)) return;
      // Target not mounted yet — retry on a short, bounded schedule so a
      // late-mounting async section (charts) still receives the deep-link.
      [120, 300, 600, 1200].forEach((delay) => {
        const id = window.setTimeout(() => {
          tryScroll(sectionId);
        }, delay);
        scheduleTimers.push(id);
      });
    };

    run();
    // Native event covers external/full navigations; the `location.hash`
    // dep below covers react-router in-app fragment changes that don't
    // dispatch a native `hashchange`.
    window.addEventListener('hashchange', run);
    return () => {
      window.removeEventListener('hashchange', run);
      scheduleTimers.forEach((t) => window.clearTimeout(t));
      clearHighlight();
    };
  }, [sectionDeepLinkHash, report.id]);

  /**
   * RWX-5 — push the canonical rerun URL. The legacy callback
   * `setShowRerunPanel(true)` is preserved for back-compat callers
   * (notifications, deep links) but every new entry point should go
   * through this so the browser back-button returns to the report.
   * Per pass-2 #10, no `setRerunMode` store flag exists; this
   * helper is the only safe wrapper.
   */
  const handleOpenRerunPanel = React.useCallback((opts?: {
    step?: 'gaps' | 'config' | 'review';
    focus?: 'questions';
  }) => {
    if (REPORT_TYPE_REGISTRY[report.reportType ?? 'feasibility'].status === 'retired') {
      toast.info('This report is retired. Its water screen is part of the Early Screening Assessment.');
      return;
    }
    if (report.reportType === 'executive-summary') {
      if (!resolvedSiteId) {
        toast.error('This legacy Decision Guide is not linked to a site, so its live Decision cannot be opened.');
        return;
      }
      // The saved Decision Guide remains a frozen historical artifact. Its
      // screen-only primary action opens the live successor: the site's
      // Decision tab (Analyses when that tab is rolled back). It used to open
      // the retired Path to GO tab (Path to GO removal, stage 2).
      rerunRouteNavigate(siteOverviewPath(resolvedSiteId, siteDecisionTab()));
      return;
    }
    if (!report.siteId) {
      // Legacy reports without a siteId can't deep-link; fall through
      // to the local-state path so the modal still opens.
      rerunPanelDismissedRef.current = false;
      setShowRerunPanel(true);
      return;
    }
    rerunPanelDismissedRef.current = false;
    const query = new URLSearchParams();
    if (opts?.step) query.set('step', opts.step);
    if (opts?.focus) query.set('focus', opts.focus);
    const suffix = query.toString();
    rerunRouteNavigate(`/sites/${report.siteId}/reports/${report.id}/rerun${suffix ? `?${suffix}` : ''}`);
  }, [report.siteId, report.id, report.reportType, rerunRouteNavigate, resolvedSiteId]);
  const handleEditCustomBriefQuestions = React.useCallback(() => {
    handleOpenRerunPanel({ step: 'config', focus: 'questions' });
  }, [handleOpenRerunPanel]);
  /**
   * RWX-5 — symmetric close. When the panel closes (either via the X
   * button or a successful diff confirmation), navigate back to
   * strip the rerun URL segment. Falls through to the local-state
   * setter when no siteId or when the URL doesn't match (legacy
   * surface mode).
   */
  const handleCloseRerunPanel = React.useCallback(() => {
    rerunPanelDismissedRef.current = true;
    setShowRerunPanel(false);
    if (rerunRouteMatchesThisReport && report.siteId) {
      // Push to the report's canonical URL (one step up from /rerun).
      rerunRouteNavigate(-1);
    }
  }, [rerunRouteMatchesThisReport, report.siteId, rerunRouteNavigate]);

  /**
   * Promote an explicitly persisted legacy draft through the current
   * investment-grade generation path. DraftBanner exposes this callback only
   * when `generationTier === 'draft'`; quality holds, degraded reports and
   * invalid artifacts receive their own recovery actions instead.
   */
  const handleFinalizeDraft = React.useCallback(async () => {
    if (!report.siteId) {
      toast.error('Cannot finalize — this legacy draft is not attached to a site.');
      return;
    }
    setIsFinalizing(true);
    toast.loading('Finalizing legacy draft at investment-grade quality…', { id: 'finalize-draft' });
    try {
      await useStore.getState().generateTypedReport(
        report.siteId,
        report.reportType ?? 'feasibility',
        undefined,
        'update',
        report.id,
        undefined,
        undefined,
        undefined,
        { generationTier: 'investment-grade' },
      );
      toast.success('Investment-grade regeneration started.', { id: 'finalize-draft' });
    } catch (error) {
      toast.error(`Finalize failed: ${(error as Error)?.message ?? 'unknown error'}`, { id: 'finalize-draft' });
    } finally {
      setIsFinalizing(false);
    }
  }, [report.siteId, report.reportType, report.id]);

  /**
   * MEL-MR-2 — wire the MetaLeakChip's [Regenerate] click through to
   * the store's admin-only single-section regen action. The store
   * action handles the Gemini call, defensive re-strip, persistence,
   * and audit emit — this callback just relays the click.
   */
  const handleSectionRegen = React.useCallback(async (sectionId: string) => {
    try {
      await useStore.getState().triggerSectionRegen(report.id, sectionId);
    } catch (e) {
      // The store action already toasts on failure; log here so the
      // forensic trail captures the originating click site.
      console.warn('[mel:section-regen-trigger-failed]', (e as Error)?.message);
    }
  }, [report.id]);

  // RN-2 — one-shot hint stamp for thin Project Intent / Overview section.
  React.useEffect(() => {
    const overview = report.projectIntentOverview?.trim();
    if (overview) return;
    const intentFallback = report.projectIntentAtGen;
    if (intentFallback) return;
    const reason = diagnoseEmptyKeyFindings(report);
    if (reason !== 'data-too-thin') return;
    if (report.sectionRegenHints?.projectIntentOverview === 'auto-fill-thin') return;
    // Defensive: stamp via the store action so the persistence layer
    // catches errors. Fire-and-forget; render must NOT block on this.
    //
    // Hydration guard: skip the stamp if the report is still metadata-only
    // (body not yet loaded from GCS). updateRagReport would try to prefetch,
    // fail silently, then throw "Refusing GCS save for metadata-only report"
    // and warn-spam the console on every Test Fit / thin-intent open. The
    // hint will be stamped naturally on the next render after hydration.
    let cancelled = false;
    void (async () => {
      try {
        const { isReportHydratedForGcsSave } = await import('../../lib/reportGcsSerialization');
        if (cancelled) return;
        if (!isReportHydratedForGcsSave(report)) return;
        const { setAutoFillThinHint } = await import('../../lib/sectionRegenHints');
        if (cancelled) return;
        const nextHints = setAutoFillThinHint(report.sectionRegenHints, 'projectIntentOverview');
        // Idempotent guard at the persistence boundary as well —
        // `setAutoFillThinHint` returns the same reference when the
        // hint is already set, so this is also a no-op in that case.
        if (nextHints === report.sectionRegenHints) return;
        await updateRagReport(report.id, { sectionRegenHints: nextHints });
      } catch (e) {
        if (cancelled) return;
        // Best-effort: hint stamp failures should never crash the
        // viewer. Future renders will retry naturally.
        console.warn('[rn-2:hint-stamp-failed]', (e as Error)?.message);
      }
    })();
    return () => { cancelled = true; };
    // Intentionally narrow deps — re-run only when the inputs that
    // change the reason routing change. Functions are module imports
    // so they're stable across renders. The full `report` object
    // identity changes on every store update, so we slice the fields
    // diagnoseEmptyKeyFindings actually reads. `sectionQuarantine` is
    // a soft-read field (not on the typed surface — keyFindingsDerive
    // accesses it via `(report as unknown as {…})`), so we depend on
    // the parent reference identity instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    report.id,
    report.structuredFindings,
    report.claims,
    report.references,
    (report as unknown as { sectionQuarantine?: unknown }).sectionQuarantine,
    report.sectionRegenHints,
  ]);

  // SET-D1 (2026-05-17) — `showLibrary` state + the inline
  // <DocumentLibrary /> mount below were retired together with the
  // ⋮ More menu's "Documents" entry. The top-level Documents tab
  // (7-tab IA at SiteLandingPage) is the canonical doc-library surface.
  // qualityCollapsed state retired with the in-body Report Quality
  // panel; its content moved to the Insights tray's Quality tab.
  // The tray was removed 2026-07-28 (William): report quality is an AUDIT
  // concern, so every former tray entry point navigates to the site Audit
  // tab's Report-quality section, deep-linked to THIS report. The reader
  // itself stays purely the printable deliverable.
  const openQualityAudit = React.useCallback(() => {
    if (!resolvedSiteId) return;
    rerunRouteNavigate(reportAuditPath(resolvedSiteId, report.id));
  }, [rerunRouteNavigate, resolvedSiteId, report.id]);
  /**
   * Truth disagreements have one recovery door: the selected report's Audit
   * queue. The reader no longer owns a second modal/rerun workflow for the
   * same questions; Audit is where every report correction is reviewed and
   * persisted.
  */
  const openTruthAudit = React.useCallback(() => {
    if (!resolvedSiteId) return;
    rerunRouteNavigate(reportAuditPath(resolvedSiteId, report.id, 'truth'));
  }, [rerunRouteNavigate, resolvedSiteId, report.id]);
  // Legacy `?tray=findings` deep links (site-card "Resolve N sanity
  // findings" CTAs) redirect to the relocated surface instead of dying.
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('tray') !== 'findings') return;
      url.searchParams.delete('tray');
      window.history.replaceState({}, '', url.toString());
      openQualityAudit();
    } catch { /* SSR / sandboxed iframe — ignore */ }
  }, [report.id, openQualityAudit]);
  // Reader-profile inline editor — admin-only. Edits the persisted snapshot
  // on the report; does NOT regenerate. Regen happens via the rerun flow,
  // which reads the latest companyArchetypeOverride off the report.
  const [readerEditing, setReaderEditing] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  // R-131 publish gate v1 follow-up — the 412 payload's blocking findings.
  // Non-null opens BlockedPublishDialog (list + "Review in Audit tab").
  const [blockedPublishFindings, setBlockedPublishFindings] = React.useState<
    import('../../lib/audit/runFullAudit').FullAuditFinding[] | null
  >(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isFinalizing, setIsFinalizing] = React.useState(false);
  // Audience the tree is rendering for. Controlled renderers pass `export`
  // explicitly so admin-only chrome never enters delivered projections.
  const [audienceMode, setAudienceMode] = React.useState<ViewMode>(viewModeProp ?? 'editor');
  React.useEffect(() => {
    setAudienceMode(viewModeProp ?? 'editor');
  }, [viewModeProp]);
  const [editedReport, setEditedReportRaw] = React.useState<SiteRagReport>(report);
  // Wrap setter to auto-mark dirty on any edit
  const setEditedReport = React.useCallback((r: SiteRagReport | ((prev: SiteRagReport) => SiteRagReport)) => {
    setEditedReportRaw(r);
    setIsDirtyFlag(true);
  }, []);
  const handleCoverArchetypeSelect = React.useCallback((next: CompanyArchetype | undefined) => {
    const arch = report.company?.archetype;
    const nextMeta = next ? COMPANY_ARCHETYPES[next] : undefined;
    const updates: Partial<SiteRagReport> = {
      companyArchetypeOverride: next,
      company: next
        ? { ...report.company, archetype: next, archetypeLabel: nextMeta?.label }
        : report.company,
    };
    setEditedReport((prev) => ({ ...prev, ...updates }));
    updateRagReport(report.id, updates);
    setReaderEditing(false);
    toast.success(
      next
        ? 'Reader profile updated for this report'
        : `Reverted to SED default${arch ? ` (${arch})` : ''}`,
    );
  }, [report, setEditedReport, updateRagReport]);
  const [aiSummary, setAiSummary] = React.useState<string | null>(null);
  const [isSummarizing, setIsSummarizing] = React.useState(false);
  const [showEditHistory, setShowEditHistory] = React.useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  // KMU — in-report Update KMZ flow. Opening this mounts
  // SiteLocationBoundaryEditor (initialMode='boundary',
  // initialBoundaryView='import') so the user lands straight on the
  // KMZ dropzone. The editor writes to the LIVE site doc on save (NOT
  // to this report's snapshot); the toast on close clarifies that
  // contract — but ONLY fires when the boundary actually changed (we
  // snapshot the boundary's identity at open-time and compare on close,
  // so a cancel-without-save closes silently).
  const [kmzEditorOpen, setKmzEditorOpen] = React.useState(false);
  const [layerEditorOpen, setLayerEditorOpen] = React.useState(false);
  // Snapshot the live site's boundary identity at editor-open. On close
  // we compare against the current value — only if it differs do we
  // fire the reproducibility-clarification toast (cancels are silent).
  const kmzEditorOpenedSnapshotRef = React.useRef<{ siteBoundary?: { lat: number; lng: number }[]; updatedAt?: string } | null>(null);
  const handleExportKmzShared = React.useCallback(async () => {
    if (!reportSite || !(reportSite.siteBoundary?.length || (reportSite.siteLayers?.length ?? 0) > 0)) {
      toast.error('This report has no boundary or KMZ-layers to export.');
      return;
    }
    try {
      const [{ exportSiteAsKmz }, { saveAs }] = await Promise.all([
        import('../../services/kmzExporter'),
        import('file-saver'),
      ]);
      const out = await exportSiteAsKmz(reportSite);
      saveAs(out.blob, out.filename);
      toast.success(`Exported ${out.filename}. Import it in Google Earth Web to view in 3D.`);
    } catch (e) {
      toast.error(`KMZ export failed: ${(e as Error).message}`);
    }
  }, [reportSite]);
  const handleUpdateKmzShared = React.useCallback(() => {
    // Record the LIVE site's boundary identity at open-time so the
    // close handler can detect a real save vs a plain cancel.
    kmzEditorOpenedSnapshotRef.current = reportSite
      ? { siteBoundary: reportSite.siteBoundary, updatedAt: reportSite.updatedAt }
      : null;
    setKmzEditorOpen(true);
  }, [reportSite]);
  const handleOpenGoogleEarthShared = React.useCallback(() => {
    if (!reportSite) return;
    const center = resolveSiteEarthViewCenter(reportSite);
    if (!center) {
      toast.error('Draw or import a boundary first.');
      return;
    }
    openGoogleEarthWeb(center);
  }, [reportSite]);

  // ELV-2 — handler for the in-report Site Boundary card's "Refresh
  // elevation" CTA. Re-fetches the elevation triple via the polygon
  // path (when the live site carries a boundary) or falls back to a
  // single-point centroid Google lookup. Persists to the LIVE
  // SiteEntity via `updateSite`; the report itself stays frozen, but
  // the next regen inherits the refreshed value through the gen-time
  // stamping path (ELV-3). The accordion's read-side fallback
  // (resolveSiteElevation) picks up the live site change immediately
  // through the Zustand subscription on `reportSite`.
  const updateSite = useStore(s => s.updateSite);
  const [isRefreshingElevation, setIsRefreshingElevation] = React.useState(false);
  const handleRefreshElevation = React.useCallback(async () => {
    if (!reportSite) {
      toast.error('No live site to refresh — open the site to re-fetch elevation.');
      return;
    }
    setIsRefreshingElevation(true);
    try {
      const { refreshSiteElevation } = await import('../../lib/refreshSiteElevation');
      const result = await refreshSiteElevation(reportSite);
      if (!result) {
        toast.error('Elevation fetch returned no usable samples.');
        return;
      }
      // ELV-4 — stamp the refresh time + source provenance onto the
      // live SiteEntity. The Property → Site Boundary diagnostic
      // reads these to render "last refreshed N ago" + the source
      // label after the next render.
      // DC-3 — also persist the cascade audit trail so the SED
      // bootstrap can stamp `attemptStages` on the siteProfile
      // section. Matches the boundary-confirm / KMZ-import paths
      // so the PRE chip tooltip reads identically across surfaces.
      await updateSite(reportSite.id, {
        siteElevationRange: {
          ...result.stats,
          refreshedAt: Date.now(),
          source: result.source,
          ...(result.cascadeSource ? { cascadeSource: result.cascadeSource } : {}),
          ...(result.cascadeStages && result.cascadeStages.length > 0
            ? { cascadeStages: result.cascadeStages }
            : {}),
        },
      });
      const avg = Math.round(result.stats.avg);
      const sourceLabel = result.source === 'polygon-sample' ? `${result.stats.pointsSampled ?? '?'}-point polygon sample` : 'centroid sample';
      toast.success(`Elevation updated — ${avg} m avg (${sourceLabel}).`);
    } catch (e) {
      toast.error(`Elevation refresh failed: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      setIsRefreshingElevation(false);
    }
  }, [reportSite, updateSite]);

  // Track whether edits are dirty (flag set on any edit action, cheaper than JSON.stringify)
  const [isDirtyFlag, setIsDirtyFlag] = React.useState(false);
  const isDirty = isEditing && isDirtyFlag;

  // Resync edit buffer when the user navigates to a different report id.
  // Keying off `report.id` rather than the `report` object reference
  // avoids a Maximum-update-depth loop when an unrelated store write
  // (e.g. evidenceReadiness recompute on AuthGate load) flips the
  // surrounding siteRag wrapper without actually changing the report.
  React.useEffect(() => {
    setEditedReportRaw(report);
    setAiSummary(null);
    setIsDirtyFlag(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.id]);

  // Unsaved changes warning — beforeunload
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Guard navigation away while editing.
  // PH-C2 — replace window.confirm with the canonical openConfirmDialog
  // primitive (portal-backed AlertDialog) for a11y + iframe-safety.
  // Semantics preserved: only prompt when dirty; gating identical.
  const handleCancelEdit = useCallback(async () => {
    if (isDirty) {
      const { openConfirmDialog } = await import('../ui/confirmDialog');
      const ok = await openConfirmDialog({
        title: 'Discard unsaved changes?',
        body: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setEditedReportRaw(report);
    setIsEditing(false);
    setIsDirtyFlag(false);
  }, [isDirty, report]);

  const handleQuickSummary = async () => {
    setIsSummarizing(true);
    try {
      const { quickSummarize } = await (await import('../../utils/safeImport')).safeImport(() => import('../../services/geminiService'));
      const summary = await quickSummarize(editedReport.executiveSummary || editedReport.summary);
      setAiSummary(summary);
    } catch (e) {
      toast.error('Failed to generate summary');
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleSaveEdits = async () => {
    setIsSaving(true);
    try {
      const { saveEditCorrections } = useStore.getState();
      await saveEditCorrections(report.id, report, editedReport);
      const newMinor = (report.minorVersion || 0) + 1;
      await updateRagReport(report.id, { ...editedReport, minorVersion: newMinor });
      setIsEditing(false);
      setIsDirtyFlag(false);
      toast.success(`Report updated (v${report.version || 1}.${newMinor})`);
    } catch (e) {
      toast.error(`Save failed: ${(e as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };


  // --- Template presentation (live-auto propagation) ---
  // Template linkage (`templateId`, layout snapshots) must come from the
  // live store-backed `report` prop — NOT `editedReport`. The edit buffer
  // only resyncs on `report.id` change, so GCS hydration / Apply Template
  // can update `report.templateId` while `editedReport` stays stale; that
  // blocked live-auto layout and left gen-time `coverLayout` snapshots
  // winning on the cover.
  const activeArchetype = report.companyArchetypeOverride ?? report.company?.archetype;
  const templateOverride = useTemplateOverride();
  const reportTemplates = useStore(s => s.reportTemplates);
  const activeTemplateId = useStore(s => s.activeTemplateId);
  const reportTemplateId = (report as { templateId?: string }).templateId;
  // Cross-org template resolution — the report must render with the template
  // assigned by the company that CREATED it, not the viewer's own. The store's
  // `reportTemplates` are hydrated for the VIEWER's company, so when an ancestor
  // opens a descendant org's report we fetch the CREATOR company's templates
  // (rules permit it: report visibility is downward-only, so the viewer is
  // always an ancestor of the creator and `isInCompanyTree` grants the read).
  // Same-org reports keep using the store templates unchanged.
  const reportCompanyId = (report as { companyId?: string }).companyId;
  const isCrossOrgReport =
    !!reportCompanyId && !!companyId && reportCompanyId !== companyId;
  const [creatorCompanyTemplates, setCreatorCompanyTemplates] =
    React.useState<ReportTemplate[] | null>(null);
  React.useEffect(() => {
    if (immutableArtifactMode || !isCrossOrgReport || !reportCompanyId) {
      setCreatorCompanyTemplates(null);
      return;
    }
    let cancelled = false;
    fetchCompanyReportTemplates(reportCompanyId)
      .then(t => { if (!cancelled) setCreatorCompanyTemplates(t); })
      .catch(err => {
        if (!cancelled) {
          console.warn('[report-template] cross-org creator template fetch failed', err);
          setCreatorCompanyTemplates(null);
        }
      });
    return () => { cancelled = true; };
  }, [immutableArtifactMode, isCrossOrgReport, reportCompanyId]);
  // Cover ownership guard (Pronghorn masthead RCA) — a cross-org report's
  // cover brand comes from the report's OWNING company, never the viewer's
  // session/base theme. Only fetched for cross-org reports (rules permit it:
  // the viewer is an ancestor of the creator, same downward-only visibility
  // as the template fetch above); same-org covers keep the session chain and
  // this hook stays inert (null id → no read).
  const owningCompanyBranding = useCompanyBranding(
    !immutableArtifactMode && isCrossOrgReport ? reportCompanyId : null,
  );
  // For cross-org, resolve against the creator's company templates and do NOT
  // apply the viewer's session active/default (that's the viewer's choice, not
  // the creator's). Falls back to the report's archetype seed if the creator
  // template can't be read (e.g. it was a private user-scoped template).
  const useCreatorTemplates = isCrossOrgReport && creatorCompanyTemplates !== null;
  const templatesForResolution = useCreatorTemplates
    ? (creatorCompanyTemplates as ReportTemplate[])
    : reportTemplates;
  const activeTemplateIdForResolution = useCreatorTemplates ? null : activeTemplateId;
  const activeTemplate = React.useMemo(
    () => immutableArtifactMode
      ? null
      : resolveActiveReportTemplate({
          reportTemplateId,
          templates: templatesForResolution,
          activeTemplateId: activeTemplateIdForResolution,
          templateOverride,
          archetype: activeArchetype,
        }),
    [
      immutableArtifactMode,
      reportTemplateId,
      templatesForResolution,
      activeTemplateIdForResolution,
      templateOverride,
      activeArchetype,
    ],
  );
  const presentationReport = React.useMemo(
    () => {
      const base = isEditing
        ? { ...report, headingOverrides: editedReport.headingOverrides }
        : report;
      // COVER FIX — gate the layout-snapshot strip on a LIVE template
      // actually being resolved. When the linked template row is
      // unresolved (still hydrating / private / cross-org) the strip would
      // otherwise delete the report's `coverLayout` and the cover would
      // fall back to Hero; keeping the snapshot (and mirroring it to
      // `coverLayoutAtGen`) lets the assigned Masthead/Classic survive.
      const stripped = stripTemplateLinkedLayoutSnapshots(base, activeTemplate != null);
      // AI LAYOUT PASS (breaks) — fold the plan's per-section breakHints into the
      // report's page-map overrides as a FLOOR: the plan-derived startMode hints
      // sit UNDER the report's own `pageMapOverrides` (an explicit user choice
      // wins on key collision). ABSENT plan / no actionable hint ⇒
      // `buildLayoutPlanPageMapOverrides` returns undefined and `stripped` is
      // returned UNCHANGED (reference-identical), so `resolvePageMap` keeps its
      // byte-identical no-override fast path.
      const planOverrides = buildLayoutPlanPageMapOverrides(report.layoutPlan);
      if (!planOverrides) return stripped;
      return {
        ...stripped,
        pageMapOverrides: { ...planOverrides, ...(stripped.pageMapOverrides ?? {}) },
      };
    },
    [report, isEditing, editedReport.headingOverrides, activeTemplate],
  );

  // P2.1 — fold the curated theme PRESET into the single theme memo so EVERY
  // downstream `theme.*` consumer re-skins with no per-section edit. With no
  // `template.themePreset`, resolveEffectiveTheme reduces EXACTLY to the prior
  // `mergeTemplateTheme(baseTheme, activeTemplate)` so legacy (absence-of-preset)
  // reports stay byte-stable on colors/fonts.
  // Export is ALWAYS light (byte-identical baseline); the on-screen reader
  // follows the app theme. This single value gates the theme stamp, the page
  // background, the `data-report-reader-mode` attribute, and the context below.
  const reportReaderMode: ReportReaderMode =
    audienceMode === 'export' ? 'light' : appResolvedTheme;
  const theme = React.useMemo(
    // RC5 — the live `report` override tier is wired here. No per-report theme
    // override field exists yet, so `report: undefined` hits the merge's
    // reference-identical empty-override fast path (byte-stable).
    // `attachReaderMode` stamps the reader mode under a Symbol key so every
    // `roleColor(theme, …)` in this subtree (and the drilled primitives) is
    // mode-aware with no call-site churn; 'light' stamps are inert.
    () => attachReaderMode(
      resolveEffectiveTheme({ base: baseTheme, template: activeTemplate, report: undefined }),
      reportReaderMode,
    ),
    [baseTheme, activeTemplate, reportReaderMode],
  );

  // Section-heading treatment derived from `theme.headingScale` so the six
  // presets read as distinct design systems (editorial / technical / compact).
  // Consumed by EVERY section <h2> below in place of the old hardcoded
  // `text-xl font-bold border-b-2 pb-2` literal — color/font still come from
  // the theme; size/weight/rule/spacing now vary by scale.
  const headingChrome = React.useMemo(
    () => reportSectionHeadingChrome(theme),
    [theme],
  );

  // Theme-adaptive content sub-block layout (per-tech BTM entries + study
  // assessment/recommendation pairs). ONE source so every body sub-block
  // re-skins per preset instead of rendering the same boxy `border
  // border-border` card. See `sectionBlockChrome`.
  const blockChrome = React.useMemo(
    () => sectionBlockChrome(theme),
    [theme],
  );

  const coverBranding = React.useMemo(
    () => {
      if (immutableArtifactMode) {
        return report._frozenBranding ?? resolveReportBranding({
          site: {
            id: report.siteId ?? `report-${report.id}`,
            name: report.siteName,
            reportAudienceDefault: 'internal',
          },
          theme,
          template: null,
          effectiveLogoUrl: null,
        });
      }
      // Cover ownership guard — for a CROSS-ORG report the viewer's session
      // brand (template/theme/uploaded logo) is suppressed in favour of the
      // owning company's brand → neutral platform default. Same-org reports
      // reduce exactly to the prior resolveReportBranding chain.
      return resolveReportCoverBranding({
        site: reportSite,
        theme,
        template: activeTemplate,
        effectiveLogoUrl: resolveUploadedLogo({ userLogoUrl: brandUserLogoUrl, orgLogoUrl: brandOrgLogoUrl }),
        reportCompanyId,
        viewerCompanyId: companyId,
        owningCompanyBranding,
      });
    },
    [
      immutableArtifactMode,
      report,
      reportSite,
      theme,
      activeTemplate,
      brandUserLogoUrl,
      brandOrgLogoUrl,
      reportCompanyId,
      companyId,
      owningCompanyBranding,
    ],
  );

  const presentation = React.useMemo(
    () => resolveReportPresentation({
      report: presentationReport,
      template: activeTemplate ?? null,
      theme,
      headingOverridesProp,
      forceLiveTemplateLayout: immutableArtifactMode ? false : !!templateOverride,
      // #34 P2 STAGE 2 — resolve the per-surface label-case token. 'view'
      // keeps 'uppercase' (byte-identical to today); 'export' resolves 'none'
      // so the 14 hand-inline eyebrow strips de-uppercase on the unified export
      // render (routed below via `labelClass(labelTransform)`).
      surface,
    }),
    [
      presentationReport,
      activeTemplate,
      theme,
      headingOverridesProp,
      templateOverride,
      immutableArtifactMode,
      surface,
    ],
  );

  // Part L2 — the Editorial-Feature Monograph treatment becomes the DEFAULT
  // STRUCTURE for ALL report types whenever the VITE_EDITORIAL_LAYOUTS flag is
  // ON. The palette presets (journal cream / institutional white / monograph
  // near-white) remain selectable as PALETTE *on top of* this structure — the
  // editorial opener/rhythm + report-root marker apply under every preset, not
  // only the `editorial` preset. (L1 gated this additionally on
  // `presetId === editorial`; L2 drops that conjunct so the structure is the
  // default render path.) This single gate drives the report-root marker (which
  // scopes the pull-quote / drop-cap CSS) and the per-section editorial opener in
  // NewTypeSectionsRouter. With the flag OFF it is always false → byte-stable
  // (the attribute is absent, the openers are not mounted, every existing
  // report/preset/visual-matrix cell renders byte-identically).
  const editorialActive = React.useMemo(
    () => isEditorialLayoutsEnabled(),
    [],
  );

  // N2 — the editorial TRUE 2-COLUMN body gate. NESTED under `editorialActive`
  // via `isEditorial2ColEnabled()` (requires BOTH `VITE_EDITORIAL_LAYOUTS` AND
  // `VITE_EDITORIAL_2COL`), the documented FALLBACK lever the N1 author wired:
  // the editorial STRUCTURE (openers / standfirsts / drop-caps / full-bleed
  // sheet) ships as the 1-col default, and the load-bearing 2-col magazine body
  // engages only when this sub-flag is explicitly ON. When it is, the report
  // re-engages the existing, battle-tested column-flow engine (the per-section
  // islands on the continuous viewer + the page-flow box + the post-layout
  // `reflowFlowProseToTwoColumns` packer on the paged deck) by forcing
  // `themeBodyColumns` to 2 below — the SAME mechanism the academic 2-col
  // presets used before they were neutered, now gated on editorial intent
  // instead of preset id. Default OFF everywhere ⇒ byte-identical flag-OFF.
  const editorial2ColActive = React.useMemo(
    () => isEditorial2ColEnabled(),
    [],
  );

  const {
    sectionOrder,
    effectiveVisibility,
    isVisible: isVisibleFromPresentation,
    columnLayout: colLayout,
    headingOverrides,
    coverLayout: resolvedCoverLayout,
    disclaimer: resolvedDisclaimer,
    pageBreakPlan: templatePageBreakPlan,
    templateStale,
    templateConfig,
    // B — the watermark comes FROM the contract (`presentation.watermark`,
    // normalised), not a re-derivation of `theme.themeWatermark`, so the
    // on-screen overlay can never diverge from the DOCX/PPTX watermark.
    watermark: resolvedWatermark,
    // #34 P2 STAGE 2 — the per-surface eyebrow-label case token. Resolves
    // 'uppercase' on 'view' (today's chrome) and 'none' on 'export'. The 14
    // hand-inline eyebrow strips below compose `labelClass(labelTransform)`
    // instead of the historical hardcoded literal, so they de-uppercase on the
    // export render and stay byte-identical on the view render.
    labelTransform,
  } = presentation;

  // AI LAYOUT PASS (cover) — the plan's `coverTreatment` is an OVERRIDE FLOOR: it
  // replaces the engine's DEFAULT cover (preset / theme / stock default) but
  // YIELDS to an EXPLICIT user/template cover choice (mirrors the floor-vs-choice
  // discipline in `resolveCoverLayout`). Resolved ONCE here so the cover ART, the
  // `data-cover-layout` attribute, and the cover-carries-TOC / cover-carries-exec
  // derivations all read the SAME effective cover. ABSENT plan (or an explicit
  // cover choice present) ⇒ `coverLayout === resolvedCoverLayout`, byte-identical
  // to today.
  const coverChoiceIsExplicit =
    report.coverLayout != null
    || report.coverLayoutAtGen != null
    || activeTemplate?.coverLayout != null
    || templateConfig?.coverLayout != null;
  const planCoverTreatment = report.layoutPlan?.coverTreatment;
  const coverLayout =
    planCoverTreatment != null && !coverChoiceIsExplicit
      ? planCoverTreatment
      : resolvedCoverLayout;

  const isVisible = React.useCallback(
    (sectionId: string) => isVisibleFromPresentation(sectionId),
    [isVisibleFromPresentation],
  );

  // C0-GEN — section headlines resolve THROUGH the editorial resolver so a
  // generated `editorialHeadline` (Wave 2+) can supersede the registry label.
  // In Wave 1 no layer exists, so `resolveEditorialLayer(...).editorialHeadline`
  // is the registry label (when known) or undefined (when not) — both keep the
  // rendered heading byte-identical to the prior `defaultSectionHeading(...)`
  // output. The `?? defaultSectionHeading(...)` retains the call-site literal
  // fallback for ids the registry doesn't label. Mirrored in SiteRagPrintExport.
  const sectionDefaultHeading = React.useCallback(
    (sectionId: string, fallback: string) =>
      resolveEditorialLayer(report, sectionId).editorialHeadline
        ?? defaultSectionHeading(sectionId, report.reportType, fallback),
    [report],
  );

  /** Hero cover is full-bleed; minimal/data-dense/classic keep padded margins. */
  const heroCoverLayout = coverLayout === 'hero-image';

  // Legacy reports historically let carrying covers own the complete executive
  // summary. Immutable V2 artifacts instead keep a bounded cover lede and the
  // complete canonical section in the body, where projection accounting can
  // prove it was preserved.
  const coverHasExecSummaryContent = React.useMemo(() => {
    if (immutableArtifactMode || !coverCarriesExecSummary(coverLayout)) return false;
    if (displayExecutiveSummary.trim()) return true;
    return !!(
      report.summary?.trim()
      || report.executive?.trim()
      || report.conclusion?.recommendation?.trim()
    );
  }, [
    immutableArtifactMode,
    coverLayout,
    displayExecutiveSummary,
    report.summary,
    report.executive,
    report.conclusion,
  ]);

  // DEFECT 8 (v1.6.0 polish-v2) — TOC ON THE COVER. The masthead journal title
  // page renders a compact 2-column numbered index below the abstract; the
  // standalone `[data-section="toc"]` page is then suppressed (coverCarriesToc).
  // Compute the SAME entry list the standalone TOC uses (deriveTocEntries + the
  // exec-summary de-dup) so the on-cover index and the body agree, and so the
  // suppression only fires when the index actually has ≥3 entries (mirrors the
  // standalone TOC's <3 guard — a thin report keeps the standalone page).
  const coverTocEntries = React.useMemo(() => {
    if (!coverCarriesToc(coverLayout)) return undefined;
    if (theme.tocEnabled === false) return undefined;
    const allTocEntries = deriveTocEntries({
      reportType: report.reportType,
      sectionOrder,
      effectiveVisibility,
      reportOverrides: editedReport.headingOverrides,
      templateOverrides: activeTemplate?.headingOverrides,
      report: report as unknown as Record<string, unknown>,
    });
    const entries = coverHasExecSummaryContent
      ? allTocEntries.filter(e => e.id !== 'executiveSummary' && e.id !== 'executive')
      : allTocEntries;
    // House contract: every eligible index stays on the cover. The
    // editorial-masthead renderer splits the full list into two explicit block
    // columns, avoiding the Paged.js multicol row-loss that originally caused
    // this list to be routed to a standalone page.
    return resolveOnCoverTocEntries(coverLayout, entries, theme.tocEnabled);
  }, [coverLayout, theme, report, sectionOrder, effectiveVisibility, editedReport.headingOverrides, activeTemplate, coverHasExecSummaryContent]);

  // True when the cover actually carries a renderable index → suppress the
  // standalone TOC section (mirrors coverHasExecSummaryContent suppression).
  const coverCarriesTocActive = !!coverTocEntries;
  /**
   * TCP-3 — when the template carries an executive letter (`coverOverlay`)
   * AND the cover is a NON-full-page academic title page (classic /
   * boundary-plate / data-snapshot / masthead / minimal), the letter renders
   * INLINE directly beneath the cover on page 1, and the executive summary
   * (first body section) is forced onto a fresh page. Full-page (hero-image)
   * covers keep the legacy separate-page placement (TCP-2).
   */
  const coverOverlayBelowCover =
    !!activeTemplate?.coverOverlay && !heroCoverLayout;
  /**
   * RETIRED — the vertical left "STRICTLY CONFIDENTIAL" rail is a removed
   * feature. The prior preset-gated removal (`!themePresetActive && …`) only
   * fired in the Studio preview: the LIVE report resolves `activeTemplate` from
   * the report's linked template / store, which does NOT reliably carry
   * `themePreset`, so the rail kept rendering on real saved/applied-theme
   * reports (the user's "old removed features … left solid line" report). It is
   * now removed UNCONDITIONALLY — confidential labeling remains in the report
   * header — so every section body uses the full-width no-rail padding
   * (generous symmetric left/right inset) in BOTH the studio preview and the
   * live report. Flipping this single source suppresses all ConfidentialSidebar
   * mounts + the export print-rail and flips every `omitSectionSidebar`
   * consumer to the no-rail padding.
   */
  const showConfidentialSidebar = false;
  const omitSectionSidebar = !showConfidentialSidebar;
  const reportPageShellPadding = reportShellPaddingClass(omitSectionSidebar);
  const reportContentPadding = reportContentPaddingClass(omitSectionSidebar);
  /** Cover page shell: hero is full-bleed; classic drops left padding for the rail. */
  const coverPageShellPadding = heroCoverLayout ? 'p-0' : reportPageShellPadding;

  const defaultPageColumns: 1 | 2 =
    templateConfig?.pageLayouts?.[0]?.columns === 2 ? 2 : 1;

  // Two-column academic presets (Quarterly / Review / Proceedings) set
  // `theme.bodyColumns === 2`. This is the DEFAULT body column layout — the
  // registry `supportsColumnLayout` guard + explicit per-section overrides
  // still win inside resolveSectionPageColumnCountForSection, so the cover,
  // the executive-summary lead, and card/wide-figure sections stay full
  // width and per-section user choices are preserved. Absent ⇒ 1 (legacy).
  //
  // #135/#163 + B2 — the body column count now resolves through the SHARED
  // `resolveBodyColumns(template, theme)` (preset-id authority — a stale store
  // `bodyColumns: 2` cannot keep a 1-column preset at two columns — else the
  // legacy `theme.bodyColumns` fallback). The SAME resolver feeds
  // PaginatedReportView and the DOCX export, so the three engines never
  // diverge. Identical to `presentation.bodyColumns`; called directly here so
  // the dependency is explicit at the use site.
  const localBodyColumns: 1 | 2 = resolveBodyColumns(activeTemplate ?? null, theme);
  // W2 (e) — gate-hint unification. When PaginatedReportView passes its
  // authoritative `bodyColumnsHint` (its own resolveBodyColumns result, the same
  // value that decided `pageFlowActive`), prefer it so the export host and this
  // rendered box CANNOT disagree about the page-flow box. Both call the same
  // resolver on the same template+theme, so the hint normally equals the local
  // derivation; preferring it makes the agreement structural. Absent ⇒ the local
  // derivation (standalone / continuous-viewer path) — byte-stable.
  // N2 — editorial intent FORCES the 2-column body (see `editorial2ColActive`),
  // independent of the preset's `resolveBodyColumns` derivation (every preset is
  // currently neutered to 1-col). Otherwise the value flows through the legacy
  // precedence unchanged (PaginatedReportView's authoritative hint, else the
  // local derivation) so flag-OFF reports are byte-stable.
  const themeBodyColumns: 1 | 2 = editorial2ColActive ? 2 : (bodyColumnsHint ?? localBodyColumns);

  // Two-column body text scale. Applies ONLY in two-column mode; single-column
  // / legacy reports use 1 (byte-stable). BOUNDED so the combined
  // typographyScale × bodyTextScale never drops the 16px base below the ~12px
  // design-token floor (i.e. the product is clamped to >= 0.75). Tuned per
  // preset (Quarterly/Review slightly smaller, Proceedings densest) via
  // `theme.bodyTextScale`.
  const effectiveBodyTextScale = React.useMemo(() => {
    if (themeBodyColumns !== 2) return 1;
    const raw =
      typeof theme.bodyTextScale === 'number' &&
        Number.isFinite(theme.bodyTextScale) &&
        theme.bodyTextScale > 0
        ? theme.bodyTextScale
        : 1;
    const typographyScale = theme.themeTypographyScale ?? 1;
    // Floor the COMBINED product at 0.75 (× 16px = 12px), the token minimum.
    const MIN_COMBINED = 0.75;
    const combined = typographyScale * raw;
    if (combined < MIN_COMBINED) return MIN_COMBINED / typographyScale;
    return raw;
  }, [themeBodyColumns, theme.bodyTextScale, theme.themeTypographyScale]);

  const sectionPageColumnCount = React.useCallback(
    (sectionId: string): 1 | 2 => {
      const base = resolveSectionPageColumnCountForSection(
        sectionId,
        templateConfig?.columnOverrides,
        templateConfig?.sectionPages,
        templateConfig?.pageLayouts,
        defaultPageColumns,
        report.reportType,
        themeBodyColumns,
      );
      // AI LAYOUT PASS (columns) — per-section column-count OVERRIDE FLOOR.
      // ABSENT plan ⇒ `base` (byte-identical). The plan YIELDS to every engine
      // 1-column pin (registry guard / 1-col-preset short-circuit / an explicit
      // 'single' override all collapse to `base === 1`, where the plan must NOT
      // force a fragile 2-column section) and to any explicit per-section
      // `columnOverride`. It takes effect ONLY where the engine used a BLIND
      // 2-column default (a 2-col preset), letting the plan drop a section to a
      // single column for emphasis.
      const planCols = report.layoutPlan?.sections?.[sectionId]?.columnCount;
      if (planCols == null) return base;
      if (base === 1) return 1;
      if (templateConfig?.columnOverrides?.[sectionId]) return base;
      return planCols;
    },
    [templateConfig, defaultPageColumns, report.reportType, themeBodyColumns, report.layoutPlan],
  );

  // #93 — page-level two-column FLOW is engaged ONLY for the two-column
  // academic presets (themeBodyColumns===2) AND only when PaginatedReportView
  // mounts the body inside a paged deck (pageFlowActive). When either is false
  // every section classifies as a legacy #159 "island" — single-column presets
  // stamp NOTHING (see below) and the continuous fallback keeps the island
  // heading-span rule. The page-flow CSS is further gated on the
  // `[data-report-paged]` ancestor so a stray flag is inert outside the deck.
  const pageFlowEngaged = themeBodyColumns === 2 && pageFlowActive;

  // #93 — per-section flow classification (island|flow|span). Returns
  // `undefined` for single-column / legacy presets so they stamp NOTHING and
  // stay byte-identical. For two-column presets it returns "island" unless the
  // paged page-flow deck is engaged, in which case prose → "flow" and
  // wide/chart/card sections → "span".
  const sectionFlowMode = React.useCallback(
    (sectionId: string): string | undefined => {
      if (themeBodyColumns !== 2) return undefined;
      const base = resolveSectionFlowModeForSection(sectionId, pageFlowEngaged, report.reportType);
      // AI LAYOUT PASS (flow) — per-section flow-mode OVERRIDE FLOOR. Effective
      // ONLY inside the engaged page-flow deck, where the engine itself
      // differentiates island/flow/span; outside it every section is a
      // byte-stable 'island' and the plan stays inert. ABSENT plan ⇒ `base`
      // (byte-identical).
      if (!pageFlowEngaged) return base;
      return report.layoutPlan?.sections?.[sectionId]?.flowMode ?? base;
    },
    [themeBodyColumns, pageFlowEngaged, report.reportType, report.layoutPlan],
  );

  const sectionPageColumnProps = React.useCallback(
    (
      sectionId: string,
    ): { 'data-section-columns': string; className?: string; 'data-section-flow'?: string } => {
      const cols = sectionPageColumnCount(sectionId);
      const flowMode = sectionFlowMode(sectionId);
      return {
        'data-section-columns': String(cols),
        ...(cols === 2 ? { className: REPORT_SECTION_PAGE_COLS_2_CLASS } : {}),
        ...(flowMode ? { 'data-section-flow': flowMode } : {}),
      };
    },
    [sectionPageColumnCount, sectionFlowMode],
  );

  // PHASE-D — `sectionStyle` is the single visibility/order chokepoint for
  // every body section. A not-visible section returns `display: 'none'`; the
  // section shell (ClassicSectionShell) treats that as an OMISSION signal and
  // renders `null`, so a hidden section leaves no residual node, no empty
  // page-flow slot, and no dangling hairline — the surrounding flex/column flow
  // closes up. Raw `<section>` sections (template custom inserts) are filtered
  // out at their `.map` for the same true-omission effect. The TOC + section
  // numbering already skip not-visible sections at the data layer
  // (deriveTocEntries → buildSectionDisplayNumbers), so numbering renumbers
  // automatically with no holes.
  const sectionStyle = React.useCallback((sectionId: string): React.CSSProperties => {
    const base: React.CSSProperties = isVisible(sectionId) ? {} : { display: 'none' };
    if (sectionOrder) {
      const idx = sectionOrder.indexOf(sectionId);
      if (idx >= 0) base.order = idx;
      else base.order = 999;
    }
    return base;
  }, [isVisible, sectionOrder]);

  // The inline Action Plan / Recommendations block renders when ANY of its
  // legacy-equivalent ids is visible (`actionPlan` / `devPathway` /
  // `recommendations`). When the effective `sectionOrder` carries a legacy id
  // (`recommendations` / `devPathway`) but NOT the canonical `actionPlan`,
  // `sectionStyle('actionPlan')` falls through to `order: 999` and floats the
  // whole block to the END of the flex flow — visibly AFTER the Conclusion.
  // Resolve the order from the FIRST matching id so the recommendations always
  // sit at their intended slot (before the closing Conclusion), regardless of
  // which alias the report's order happens to carry. Visibility is unchanged
  // (the block's own gate already ORs the three ids). No-op when none of the
  // aliases is in the order (keeps the prior 999 fallback) or when no
  // sectionOrder exists (DOM source order already places it before the tail).
  const actionPlanSectionStyle = React.useCallback((): React.CSSProperties => {
    const base = sectionStyle('actionPlan');
    if (sectionOrder && base.order === 999) {
      for (const aliasId of ['devPathway', 'recommendations']) {
        const idx = sectionOrder.indexOf(aliasId);
        if (idx >= 0) return { ...base, order: idx };
      }
    }
    return base;
  }, [sectionStyle, sectionOrder]);

  const templatePagePhysicsActive = audienceMode === 'export' && templatePageBreakPlan.enabled;
  /**
   * Flatten page shells so CSS `order` on all `[data-section]` nodes applies at
   * the report root.
   *
   * RRQ DEPLOY-BLOCKER FIX (C-1/E-1) — flatten on the CUSTOMER EXPORT path too.
   * Previously flatten was suppressed whenever `templatePagePhysicsActive`
   * (export + a resolved template), so the page-2/3 shells + the
   * ReferencesSection wrapper stayed REAL flex boxes. Sections then lived in
   * THREE different DOM parents, and CSS `order:N` (sectionStyle) only orders
   * siblings WITHIN one parent — so Conclusion + Appendix (in the
   * ReferencesSection wrapper) could not interleave with risk/lcoe/extendedData
   * (in the root flex / page-2 shell) and printed MID-report instead of last.
   * OR-in `audienceMode === 'export'` so flatten is TRUE on the customer PDF:
   * the shells collapse to `display:contents`, every `[data-section]` becomes a
   * direct flex child of `[data-report-root]`, and the single root-level CSS
   * `order` orders ALL sections uniformly — identical to the on-screen deck
   * (which already renders correctly). `studioPreview` already proves
   * flatten + template-physics coexist. Template page-break stamping is
   * preserved (stampTemplatePageBreaks targets the surviving `[data-section]`
   * nodes); the forced shell-first-child break (index.css, scoped
   * `:not([data-section-order-flatten="true"])`) self-disables because the root
   * now stamps `data-section-order-flatten="true"`.
   */
  const sectionOrderFlatten = Boolean(sectionOrder?.length)
    && (!templatePagePhysicsActive || studioPreview || audienceMode === 'export');

  const sectionDisplayNumbers = React.useMemo(() => {
    if (!sectionOrderFlatten) return null;
    return buildSectionDisplayNumbers({
      reportType: report.reportType,
      sectionOrder,
      effectiveVisibility,
      report: (isEditing ? editedReport : report) as unknown as Record<string, unknown>,
      reportOverrides: editedReport.headingOverrides,
      templateOverrides: activeTemplate?.headingOverrides,
      excludeIds: editorialActive && coverHasExecSummaryContent
        ? ['executiveSummary', 'executive']
        : undefined,
    });
  }, [
    sectionOrderFlatten,
    report,
    editedReport,
    sectionOrder,
    effectiveVisibility,
    activeTemplate?.headingOverrides,
    isEditing,
    editorialActive,
    coverHasExecSummaryContent,
  ]);

  // William QA BUG #15 — the Part-II divider mini-TOC (`partIITocEntries`) was
  // removed alongside the divider sheet it fed (the divider page rendered with
  // non-contiguous §NN numbering and out of place). The reportParts lib helpers
  // remain available; this report no longer builds or renders a Part divider.

  // TOC page numbers — owned by the paginator. Project the resolved page
  // map's `startPage` per section into the `{ [sectionId]: startPage }` shape
  // the TableOfContents `pageNumbers` prop consumes so the dotted leaders
  // terminate in real page numbers. Only meaningful when template page
  // physics are present (a resolved map with sections); otherwise undefined →
  // the TOC renders unnumbered leaders exactly as before (no regression on
  // the non-paged preview path). See `templatePages/resolvePageMap`.
  //
  // RRQ DEPLOY-BLOCKER FIX (E-3) — ship REAL TOC page numbers in the customer
  // export too. PREVIOUSLY these were suppressed in `audienceMode==='export'`
  // because the section ORDER was scrambled (E-1) — the page-map estimate could
  // not match the physically-rendered order, so a number was worse than none
  // and the export shipped dotted leaders terminating in EMPTY space (a
  // non-functional contents page on the primary investment-grade deliverable).
  //
  // Step 1 (force flatten on export) makes the rendered section order
  // DETERMINISTIC and identical to the on-screen deck, and Step 2 (real @page
  // margins in the measured-break engine) makes the break placement match
  // Chromium's real content box — so the `pageMap.startPage` estimate is now
  // close to truth (drift ≤1 page on dense reports, acceptable vs a blank TOC,
  // and it matches the on-screen viewer which already shows these numbers).
  // The robust follow-up is a measured page-position read-back, but the
  // estimate path is the correct deploy-scoped fix: it restores viewer↔PDF
  // parity and a functional contents page. The dotted leaders are unchanged.
  const tocPageNumbers = React.useMemo<Record<string, number> | undefined>(() => {
    // E0 SPIKE — measured page numbers from the native two-pass driver win over
    // the page-map estimate; absent ⇒ the estimate path below, unchanged.
    if (tocPageNumbersOverride && Object.keys(tocPageNumbersOverride).length > 0) {
      return tocPageNumbersOverride;
    }
    const sections = templatePageBreakPlan.pageMap.sections;
    if (!templatePageBreakPlan.enabled || sections.length === 0) return undefined;
    const map: Record<string, number> = {};
    for (const entry of sections) {
      if (Number.isFinite(entry.startPage)) map[entry.id] = entry.startPage;
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [templatePageBreakPlan, tocPageNumbersOverride]);

  // HR-QA fix (2026-06-10) — EXPORT DOM-ORDER PARITY. The server Puppeteer PDF
  // + the Cmd-P export render SiteRagReportView in viewMode='export' WITHOUT the
  // Paged.js deck, so the only thing that orders sections is CSS flex `order`
  // (from sectionStyle()). That works when the page shells flatten to
  // display:contents (sectionOrderFlatten), but when a template engages page
  // physics the shells stay real boxes and `order` no longer applies across
  // shells — so the EXPORT DOM order desynced from sectionOrder (rendered:
  // feasibility Appendix before Risk Assessment; financial Appendix before
  // Conclusion). This effect physically reorders each group of [data-section]
  // siblings by their resolved inline `order` so the DOM order matches
  // sectionOrder IDENTICALLY to the viewer — independent of flatten/page-physics
  // and of whether Chromium honours flex `order` during @page fragmentation.
  //
  // Mirrors the former pageFlow reorder (same orderOf / group-by-parent / stable
  // sort / appendChild move) but scopes to the whole report root and runs in
  // EXPORT mode ONLY. Idempotent + byte-stable on the interactive viewer
  // (audienceMode !== 'export' ⇒ early return; the viewer keeps CSS order).
  React.useEffect(() => {
    // EXPORT-ONLY DOM reorder. The server Puppeteer PDF / Cmd-P export render
    // SiteRagReportView in viewMode='export' and rely on this physical sort
    // because `display:block` ignores CSS `order:N`. That render is ONE-SHOT (no
    // interactive reconcile follows it), so the appendChild moves never desync a
    // live React update.
    //
    // The Paged.js DECK source used to be reordered here too (the `pageFlowActive`
    // trigger), mutating the LIVE, React-managed host. That out-of-band move
    // desynced the fiber tree and threw "removeChild … not a child of this node"
    // the next time a template link/switch reconciled the body. The deck now
    // orders its DETACHED clone in `reorderClonedSectionsByFlowOrder`
    // (renderPagedDeck), so the interactive host is never mutated. Hence this
    // effect no longer fires on `pageFlowActive` — export only.
    if (audienceMode !== 'export') return;
    if (!sectionOrder || sectionOrder.length === 0) return;
    const root = reportRef.current;
    if (!root) return;
    const handle = window.setTimeout(() => {
      const live = reportRef.current;
      if (!live) return;
      const sections = Array.from(live.querySelectorAll<HTMLElement>('[data-section]')).filter(
        (el) => !el.parentElement?.closest('[data-section]')
      );
      if (sections.length < 2) return;
      const byParent = new Map<HTMLElement, HTMLElement[]>();
      for (const el of sections) {
        const parent = el.parentElement;
        if (!parent) continue;
        const group = byParent.get(parent);
        if (group) group.push(el);
        else byParent.set(parent, [el]);
      }
      const orderOf = (el: HTMLElement): number => {
        // RRQ PAGINATION FIX — the COVER carries NO inline `order` (it is not
        // driven by sectionStyle()), so the old `NaN → MAX_SAFE_INTEGER`
        // fallback sorted it to the END of its parent group, landing the cover
        // MID-DOCUMENT (observed at page 19 of the export PDF). Pin
        // `data-section="cover"` to -1 so it always sorts FIRST regardless of
        // its missing inline order; everything else keeps its sectionStyle()
        // `order:N`, with the unordered fallback at the tail.
        if (el.getAttribute('data-section') === 'cover') return -1;
        const raw = el.style.order;
        const n = raw ? Number.parseInt(raw, 10) : NaN;
        return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
      };

      if (sectionOrderFlatten) {
        const sorted = sections
          .map((el, i) => ({ el, ord: orderOf(el), i }))
          .sort((a, b) => (a.ord - b.ord) || (a.i - b.i))
          .map((x) => x.el);
        const alreadySorted = sorted.every((el, i) => el === live.children[i]);
        if (!alreadySorted) {
          for (const el of sorted) live.appendChild(el);
        }
      } else {
        byParent.forEach((group, parent) => {
          if (group.length < 2) return;
          const sorted = group
            .map((el, i) => ({ el, ord: orderOf(el), i }))
            .sort((a, b) => (a.ord - b.ord) || (a.i - b.i))
            .map((x) => x.el);
          const alreadySorted = sorted.every((el, i) => el === group[i]);
          if (alreadySorted) return;
          for (const el of sorted) parent.appendChild(el);
        });
      }
    }, 0);
    return () => window.clearTimeout(handle);
  }, [audienceMode, sectionOrder]);

  const sectionNumberProps = React.useCallback(
    (sectionId: string) => sectionNumberDataAttr(sectionDisplayNumbers?.get(sectionId)),
    [sectionDisplayNumbers],
  );

  const page2ShellClass = sectionOrderFlatten
    ? 'contents border-b border-border-subtle'
    : `${reportPageShellPadding} relative border-b border-border-subtle`;
  const page3ShellClass = sectionOrderFlatten ? 'contents' : `${reportPageShellPadding} relative`;
  const pageInnerShellClass = sectionOrderFlatten
    ? 'contents'
    : `flex flex-col ${REPORT_FLOW_GAP_CLASS}${reportContentPadding ? ` ${reportContentPadding}` : ''}`;
  /** Page-1 body outer shell — matches page-2 padding + sidebar placement. */
  const page1BodyShellClass = sectionOrderFlatten
    ? 'contents'
    : `${reportPageShellPadding} relative`;
  const page1InnerShellClass = sectionOrderFlatten
    ? 'contents'
    : `flex flex-col ${REPORT_FLOW_GAP_CLASS}${reportContentPadding ? ` ${reportContentPadding}` : ''}`;

  /** Shared chrome for inline + lifted body sections (matches extracted sections).
   *  K2 — vertical rhythm reads the density-aware spacing token (default `py-6`,
   *  `py-5` compact) so inline sections track the same tightened lattice. */
  const inlineSectionChrome = `${reportSectionGapClass(theme.density)} border-b border-border-subtle`;
  /** Page-2 inline sections defer shell padding to the page wrapper unless flattening. */
  const inlineSectionShellPadding = sectionOrderFlatten;

  const propertyMapSurfaceMode: 'interactive' | 'static' =
    audienceMode === 'export' ? 'static' : 'interactive';

  React.useEffect(() => {
    if (!templatePagePhysicsActive) return;
    const root = reportRef.current;
    if (!root) return;

    const handle = window.setTimeout(() => {
      const live = reportRef.current;
      if (!live) return;
      // 1. Estimate-based stamp — the author-pinned breaks
      //    (page-pin / `section.page`) are the load-bearing breaks
      //    we must honour verbatim.
      stampTemplatePageBreaks(live, templatePageBreakPlan.breakBeforeSectionIds);

      // 2. MEASURED pass — replaces the estimate heuristic with a
      //    real simulated flow over the rendered section heights, so
      //    breaks land only where a section genuinely overflows the
      //    remaining space on the page. This removes the 60%-empty
      //    pages / orphaned-footer artefacts the estimate produced.
      //    Gated on a live print viewport (jsdom collapses every rect
      //    to 0 — a measured pass there would be meaningless).
      const measured = readMeasuredSections(live);
      const hasRealHeights = measured.some(s => s.heightPx > 0);
      if (!hasRealHeights) return; // jsdom / pre-paint — keep the estimate stamp.

      // Page content box from the live sheet: prefer the rendered page
      // width to infer A4-vs-Letter, falling back to the A4 baseline.
      const sheetH = A4_SHEET_HEIGHT_PX;
      const contentH = contentHeightPx(sheetH, DEFAULT_PRINT_MARGINS_MM);
      const result = computeMeasuredBreaks({
        sections: measured,
        contentHeightPx: contentH,
        // Author-pinned breaks remain hard breaks — measured flow can
        // ADD breaks but never drop a pinned one.
        hardBreakIds: templatePageBreakPlan.breakBeforeSectionIds,
        fullPageIds: new Set(['cover']),
      });
      stampTemplatePageBreaks(live, result.breakBeforeSectionIds);
    }, 0);
    return () => {
      window.clearTimeout(handle);
      const live = reportRef.current;
      if (live) {
        live.querySelectorAll('[data-template-page-break]').forEach(el => {
          el.removeAttribute('data-template-page-break');
        });
      }
    };
  }, [templatePagePhysicsActive, templatePageBreakPlan]);

  // EXPORT POLISH — fixed confidential rail on EVERY printed page.
  // The in-flow `ConfidentialSidebar` only paints the pages its section
  // occupies; a `position: fixed` ::before/::after on <body> (in the
  // print stylesheet) repeats on every sheet for a uniform spine. We
  // only flip the body flag + feed the rail color/text via CSS vars in
  // export mode and only when the layout actually shows a rail —
  // on-screen and non-rail covers (hero/minimal/data-dense) are
  // untouched.
  React.useEffect(() => {
    if (audienceMode !== 'export' || !showConfidentialSidebar) return;
    if (typeof document === 'undefined') return;
    const body = document.body;
    const root = document.documentElement;
    body.classList.add('report-print-confidential-rail');
    root.style.setProperty('--report-confidential-rail', theme.sidebarColor);
    // CSS `content` needs a quoted string literal — wrap the rail label.
    const label = (theme.sidebarText || 'Strictly Confidential').toUpperCase().replace(/"/g, '');
    root.style.setProperty('--report-confidential-rail-text', `"${label}"`);
    return () => {
      body.classList.remove('report-print-confidential-rail');
      root.style.removeProperty('--report-confidential-rail');
      root.style.removeProperty('--report-confidential-rail-text');
    };
  }, [audienceMode, showConfidentialSidebar, theme.sidebarColor, theme.sidebarText]);

  const setHeading = React.useCallback((key: string, value: string) => {
    setEditedReport(prev => ({
      ...prev,
      headingOverrides: { ...(prev.headingOverrides || {}), [key]: value },
    }));
  }, [setEditedReport]);

  // --- Custom sections ---
  // PH-C1 / PH-A3 — helpers wrapped in useCallback so the memoized
  // ReferencesSection (which receives all four) sees stable refs and
  // can short-circuit unless its own data props change. Functional
  // setters drop the dep on `editedReport` itself.
  const customSections = React.useMemo(() => editedReport.customSections || [], [editedReport.customSections]);
  const visibleTemplateCustomSections = React.useMemo(
    () => (activeTemplate?.customSections ?? []).filter(
      cs => cs.scope === 'all'
        || (Array.isArray(cs.scope) && report.reportType !== undefined && cs.scope.includes(report.reportType)),
    ),
    [activeTemplate?.customSections, report.reportType],
  );
  const addCustomSection = React.useCallback(() => {
    setEditedReport(prev => ({
      ...prev,
      customSections: [...(prev.customSections || []), { id: 'cs-' + Date.now(), title: 'New Section', content: '' }],
    }));
  }, [setEditedReport]);
  const updateCustomSection = React.useCallback((id: string, field: 'title' | 'content', value: string) => {
    setEditedReport(prev => ({
      ...prev,
      customSections: (prev.customSections || []).map(s => s.id === id ? { ...s, [field]: value } : s),
    }));
  }, [setEditedReport]);
  const removeCustomSection = React.useCallback((id: string) => {
    setEditedReport(prev => ({
      ...prev,
      customSections: (prev.customSections || []).filter(s => s.id !== id),
    }));
  }, [setEditedReport]);
  const moveCustomSection = React.useCallback((id: string, dir: -1 | 1) => {
    setEditedReport(prev => {
      const list = prev.customSections || [];
      const idx = list.findIndex(s => s.id === id);
      if (idx < 0) return prev;
      const target = idx + dir;
      if (target < 0 || target >= list.length) return prev;
      const arr = [...list];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...prev, customSections: arr };
    });
  }, [setEditedReport]);

  // --- Editable references ---
  // Same PH-C1 useCallback treatment as custom-section helpers above.
  const editableRefs = React.useMemo(() => editedReport.references || [], [editedReport.references]);
  const addReference = React.useCallback(() => {
    setEditedReport(prev => ({
      ...prev,
      references: [...(prev.references || []), { field: '', source: '', url: '', retrievedAt: new Date().toISOString() }],
    }));
  }, [setEditedReport]);
  const updateReference = React.useCallback((idx: number, field: string, value: string) => {
    setEditedReport(prev => {
      const refs = [...(prev.references || [])];
      if (!refs[idx]) return prev;
      refs[idx] = { ...refs[idx], [field]: value };
      return { ...prev, references: refs };
    });
  }, [setEditedReport]);
  const removeReference = React.useCallback((idx: number) => {
    setEditedReport(prev => ({
      ...prev,
      references: (prev.references || []).filter((_, i) => i !== idx),
    }));
  }, [setEditedReport]);

  // Editorial restyle — the RAG/verdict + severity colour helpers now
  // resolve through roleColor(theme,…) so the status dots / badges they
  // drive follow the active preset (green→positive, amber→warning,
  // red→negative) instead of fixed `bg-success/bg-warning/bg-danger`
  // utility classes that ignore the report theme. Returned as a concrete
  // CSS colour string for an inline `style={{ background / color }}`.
  const getStatusColor = (status: RagStatus): string => {
    switch (status) {
      case 'green': return roleColor(theme, 'positive');
      case 'amber': return roleColor(theme, 'warning');
      case 'red': return roleColor(theme, 'negative');
      default: return roleColorAlpha(theme, 'accent', 0.2);
    }
  };

  const getStatusIcon = (status: RagStatus) => {
    switch (status) {
      case 'green': return <CheckCircle2 className="w-4 h-4" style={{ color: roleColor(theme, 'positive') }} />;
      case 'amber': return <AlertTriangle className="w-4 h-4" style={{ color: roleColor(theme, 'warning') }} />;
      case 'red': return <XCircle className="w-4 h-4" style={{ color: roleColor(theme, 'negative') }} />;
      default: return <Info className="w-4 h-4" style={{ color: roleColorAlpha(theme, 'accent', 0.4) }} />;
    }
  };

  // Severity → EditorialBadge tone for the outline status pills (the
  // ink + low-alpha border are derived from the same theme role inside
  // EditorialBadge), replacing the old `text-danger bg-danger/15 …`
  // filled-chip class chain.
  const getSeverityTone = (
    severity: string,
  ): 'negative' | 'warning' | 'positive' | 'neutral' => {
    switch (severity) {
      case 'critical': return 'negative';
      case 'high': return 'negative';
      case 'medium': return 'warning';
      case 'low': return 'positive';
      default: return 'neutral';
    }
  };

  const artifactFilename = (format: 'pdf' | 'docx' | 'pptx'): string => (
    buildReportExportFilename({
      siteName: report.siteName,
      reportType: report.reportType,
      version: report.version,
      minorVersion: report.minorVersion,
      format,
    })
  );

  const createArtifactRequest = (format: 'pdf' | 'docx' | 'pptx') => (
    createReportArtifactRequest(actionReport, format, 'A4')
  );

  const buildArtifact = async (
    format: 'pdf' | 'docx' | 'pptx',
    toastId: string,
    retryFailedPdf = false,
  ): Promise<Blob> => {
    const formatName = format === 'pdf'
      ? 'paginated PDF'
      : format === 'docx'
        ? 'Word document'
        : 'PowerPoint';
    // Reuse existing exports. Missing shared PDFs are requested under their
    // canonical owner; the server rechecks the actual requester's access.
    const adminPdfRecovery = retryFailedPdf === true && isPlatformAdmin && format === 'pdf'
      && reportPdfDeliveryNotice(actionReport) !== null;
    const exportAction = adminPdfRecovery ? { kind: 'build' as const } : resolveSharedReportExportAction({
      report: actionReport,
      reportOwnerUid,
      currentUid: auth.currentUser?.uid ?? null,
      format,
      pageSize: 'A4',
      filename: artifactFilename(format),
    });
    if (exportAction.kind === 'unavailable') {
      throw new Error(exportAction.reason);
    }
    if (exportAction.kind === 'download') {
      const { downloadPrivateReportArtifact } = await import('../../services/privateReportArtifactDownload');
      toast.loading(`Downloading the author's ${formatName}…`, { id: toastId });
      return downloadPrivateReportArtifact(exportAction.artifact, { save: () => {} });
    }
    const baseRequest = createArtifactRequest(format);
    const request = format === 'pdf'
      ? { ...baseRequest, pdfBuilderVersion: REPORT_PDF_BUILDER_VERSION }
      : baseRequest;
    const { buildReportArtifact } = await import('../../services/reportArtifactJobsClient');
    toast.loading(`Preparing ${formatName}…`, { id: toastId });
    const result = await buildReportArtifact(request, {
      ...(adminPdfRecovery && reportOwnerUid ? { adminReportOwnerUid: reportOwnerUid } : {}),
      ...(!adminPdfRecovery && 'reportOwnerUid' in exportAction && exportAction.reportOwnerUid
        ? { reportOwnerUid: exportAction.reportOwnerUid } : {}),
      onProgress: (job) => {
        if (job.status === 'queued' || job.status === 'running') {
          const emailScheduled = job.deliveryNotification?.status === 'email-scheduled'
            || job.deliveryNotification?.status === 'email-sent';
          toast.loading(format === 'pdf'
            ? `Preparing PDF… ${job.progress}%. ${emailScheduled
              ? 'You can leave this page; we will email you when it is ready.'
              : 'You can keep working while it builds.'}`
            : `Preparing ${formatName}… ${job.progress}%`, { id: toastId });
        }
      },
    });
    return result.blob;
  };

  const activeCompanyId = useStore(s => s.companyId);
  const exportIdentity = [
    activeCompanyId ?? '',
    actionReport.siteId ?? '',
    actionReport.id,
    actionReport.reportRevision?.revisionId ?? '',
  ].join('::');
  const exportIdentityRef = React.useRef(exportIdentity);
  const exportRequestSeqRef = React.useRef(0);
  const exportLifecycleEpochRef = React.useRef(0);
  const isExportMountedRef = React.useRef(true);
  if (exportIdentityRef.current !== exportIdentity) {
    exportIdentityRef.current = exportIdentity;
    exportLifecycleEpochRef.current += 1;
    exportRequestSeqRef.current += 1;
  }
  const isExportRequestCurrent = (
    requestIdentity: string,
    requestSeq: number,
    requestEpoch: number,
  ): boolean =>
    isExportMountedRef.current
    && exportLifecycleEpochRef.current === requestEpoch
    && exportIdentityRef.current === requestIdentity
    && exportRequestSeqRef.current === requestSeq;
  React.useEffect(() => {
    isExportMountedRef.current = true;
    return () => {
      isExportMountedRef.current = false;
      exportLifecycleEpochRef.current += 1;
      exportRequestSeqRef.current += 1;
    };
  }, []);
  React.useEffect(() => {
    setIsExporting(false);
  }, [exportIdentity]);

  /**
   * PDF is the only export that invokes the paginated layout pass. The normal
   * report reader stays continuous HTML; this action builds an immutable PDF
   * from the exact current revision. The server job continues if the reader
   * leaves this page; completed bytes are downloaded without an empty popup.
   */
  const handleExportPDF = async (retryFailedPdf = false) => {
    if (isExporting) return;
    const pdfNotice = reportPdfDeliveryNotice(actionReport);
    if (requiresLegacySimulationRegeneration) {
      toast.error('This report contains retired legacy simulation evidence. Regenerate it before export.', {
        id: 'pdf-export',
      });
      return;
    }
    try {
      // Bind the request to the complete immutable revision before enqueueing.
      createArtifactRequest('pdf');
    } catch (error) {
      if (pdfNotice && !(retryFailedPdf === true && isPlatformAdmin)) {
        toast.info(pdfNotice, { id: 'pdf-export' });
        return;
      }
      toast.error((error as Error).message, { id: 'pdf-export' });
      return;
    }

    const requestIdentity = exportIdentity;
    const requestEpoch = exportLifecycleEpochRef.current;
    const requestSeq = ++exportRequestSeqRef.current;
    const expectedFilename = artifactFilename('pdf');
    const expectedReportId = report.id;
    setIsExporting(true);
    try {
      const blob = await buildArtifact('pdf', 'pdf-export', retryFailedPdf === true);
      if (!isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
        return;
      }
      const { saveAs } = await import('file-saver');
      if (!isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
        return;
      }
      saveAs(blob, expectedFilename);
      toast.success('PDF ready. Your download has started.', { id: 'pdf-export' });
      // Refresh delivery metadata after handing the verified file to the
      // browser. A slow or failed report-list refresh cannot block its download.
      void Promise.resolve()
        .then(() => useStore.getState().fetchRagReports())
        .then(() => {
          if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
            return useStore.getState().hydrateReportBody(expectedReportId);
          }
          return false;
        })
        .catch(() => console.warn('[report-pdf] Download completed; report metadata refresh remains pending.'));
    } catch {
      if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
        toast.error('PDF could not be prepared. You can continue reading this report and try the download again later.', { id: 'pdf-export' });
      }
    } finally {
      if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
        setIsExporting(false);
      }
    }
  };
  // R-131 publish gate (stage 4) — share routes through the server-authoritative
  // publish path, which runs the audit gate and (if allowed) writes the public
  // share snapshot via the Admin SDK. The old ungated client `setDoc` is gone
  // (firestore.rules now denies all client writes to shares/). A blocked publish
  // opens BlockedPublishDialog with the actual findings + a deep link into the
  // Audit tab pre-filtered to this report.
  const handleShareReport = async () => {
    try {
      const { publishReportRemote } = await import('../../services/publishGateClient');
      const reportRevisionId = actionReport.reportRevision?.revisionId;
      if (!reportRevisionId) {
        throw new Error('This legacy report must be regenerated before it can be published.');
      }
      const r = await publishReportRemote({
        reportId: actionReport.id,
        reportRevisionId,
        ownerUid: actionReport._ownerUid,
      });
      if (!r.allowed) {
        setBlockedPublishFindings(r.blockingFindings ?? []);
        return;
      }
      const url = r.shareUrl ?? `${window.location.origin}/shared/${r.shareId}`;
      await navigator.clipboard.writeText(url);
      toast.success('Share link created and copied');
    } catch (e) {
      toast.error(`Failed to share: ${(e as Error).message}`);
    }
  };

  // Sentiment-First Verdict SF2 — every verdict chip on the report surface
  // (hero card, cover verdict chip, in-body Conclusion) routes through this
  // one helper so the cover and the hero render the same tier + qualitative
  // label ("Strong" / "Conditional" / "Challenging") and can never disagree.
  // Numeric scores are not user-facing — kept internally on `verdict.internalScore`
  // for ranking but never rendered here.
  const verdict = getDisplayVerdict(report);
  const verdictLabel = verdict.label;
  const typeShortLabel = REPORT_TYPE_REGISTRY[report.reportType ?? 'feasibility'].shortLabel;

  // RI4 — count critical Supporting Documents rows that are still
  // unsatisfied for this report's type. Reads the report's persisted
  // `evidenceConfidence.missingCriticalIds` (populated at gen time
  // from the doc checklist + uploaded docs) and removes any row the
  // user has since resolved by picking a non-default tier
  // (`evidenceStrategy[id]` set to Tier 2 / Tier 3 / Tier 4 — only
  // 'pending' or absent rows remain countable). The pill on the
  // header strip surfaces this number when > 0 and deeplinks to the
  // Site Evidence tab where EA's tier-aware actions live.
  const missingCriticalCount = React.useMemo(() => {
    const ids = report.evidenceConfidence?.missingCriticalIds ?? [];
    if (ids.length === 0) return 0;
    const strategy = report.evidenceStrategy ?? {};
    return ids.filter(id => {
      const choice = strategy[id];
      // 'pending' (or absent) means the user hasn't resolved the gap
      // yet. Any explicit tier choice — even tier1-doc, which would
      // also flip the missing flag on the next rerun — counts as
      // resolved for this view.
      return !choice || choice === 'pending';
    }).length;
  }, [report.evidenceConfidence?.missingCriticalIds, report.evidenceStrategy]);

  const reportFieldCoveragePct = React.useMemo(() => {
    const reportType = (report.reportType ?? 'feasibility') as ReportTypeId;
    // Intent-scope the evidence badge's denominator. Prefer the LIVE site
    // intent (matches the live SED this badge reads); fall back to the
    // intent pinned at generation time. Empty/unknown → full-set (no
    // regression). See lib/coverage/requirementIntentRelevance.
    const projectIntent = reportSite?.projectIntent ?? report.projectIntentAtGen ?? null;
    const coverage = computeReportTypeFieldCoverage({
      reportType,
      sed: liveSiteSed,
      authoredClaims: reportSite?.siteContext?.claims ?? [],
      projectIntent,
    });
    return coverage?.pct ?? null;
  }, [report.reportType, liveSiteSed, reportSite?.siteContext?.claims, reportSite?.projectIntent, report.projectIntentAtGen]);

  // RI4 + EV2 — navigate to the owning site's Site Evidence tab
  // (setActiveSite + setActiveRagReport(null) + ?tab=evidence) so the
  // SiteLandingPage's initial-tab resolver lands on the right surface.
  //
  // EV2 (2026-05-15): the always-on breadcrumb in ReportHeaderStrip
  // passes `{ filterReportId: report.id }` so the canonical tab can
  // pre-scope SEDVariableBrowser to the variables cited by this
  // report (EV3). The opts arg is optional so callers that just want
  // the unscoped tab (rare — only the legacy missing-docs pill ever
  // did that) keep working.
  //
  // No-op when the report isn't attached to a site (legacy
  // free-standing reports — the breadcrumb is hidden in that case
  // anyway because the parent only passes the handler when
  // report.siteId is truthy).
  const handleOpenSiteEvidence = React.useCallback((opts?: { filterReportId?: string; focus?: 'conflicts' }) => {
    if (!report.siteId) return;
    // Push the tab marker BEFORE updating store state so that when
    // SiteLandingPage mounts (triggered by setActiveRagReport(null)),
    // its `initialTabFromUrl` resolver reads the right tab on first
    // render.
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        url.pathname = siteOverviewPath(report.siteId);
        url.searchParams.set('tab', 'evidence');
        if (opts?.filterReportId) {
          url.searchParams.set('filterReportId', opts.filterReportId);
        } else {
          url.searchParams.delete('filterReportId');
        }
        // EDX-10 — banner CTA passes `focus: 'conflicts'` so the
        // SiteLandingPage focus receiver scrolls + highlights the
        // EvidenceConflictsPanel on mount. Bare `conflicts` token
        // (no `:id`) matches the parseFocusParam contract added in
        // EDX-8.
        if (opts?.focus === 'conflicts') {
          url.searchParams.set('focus', 'conflicts');
        } else {
          url.searchParams.delete('focus');
        }
        window.history.pushState({}, '', url.toString());
      } catch { /* ignore — UA without URL or history APIs */ }
    }
    useStore.getState().setActiveSite(report.siteId);
    useStore.getState().setActiveRagReport(null);
  }, [report.siteId]);

  const handleBackToReports = React.useCallback(() => {
    // X-USER-BACK — "‹ REPORTS" goes to the TOP-LEVEL Reports tab
    // (William 2026-07-03), with navigate-first + EXPLICIT store clears.
    // Hydration alone cannot own this transition (it bails on the dashboard
    // path), and clearing both ids is what demotes the derived engineView all
    // the way to 'dashboard'. Order/race history documented in
    // `navigateBackToReports` (reportBackNavigation.ts).
    const { setActiveSite, setActiveRagReport } = useStore.getState();
    navigateBackToReports({
      navigate: rerunRouteNavigate,
      setActiveSite,
      setActiveRagReport,
      siteId: report.siteId ?? null,
    });
  }, [rerunRouteNavigate, report.siteId]);

  // ── Report-page status (William, 2026-10-13: "i dont want any banners on
  // report pages"). Every state this page used to announce with a banner
  // resolves to ONE header status chip, from the banners' own derivations
  // (reportStatusChip.ts), and every banner-only action is a ⋮ entry.
  //
  // In-flight generation for THIS site + report type (Update reruns and
  // add-another siblings alike). The selector returns a primitive key so the
  // reader re-renders only when this report's run changes — never on progress
  // ticks of unrelated sites or types.
  const activeGenerationKey = useStore((s) => {
    const run = selectActiveReportGeneration(
      s.siteRag.activeGenerations ?? STABLE_EMPTY_RECORD,
      resolvedSiteId,
      report.reportType,
    );
    return run ? JSON.stringify(run) : '';
  });
  const activeGeneration = React.useMemo(
    () => (activeGenerationKey
      ? JSON.parse(activeGenerationKey) as NonNullable<ReturnType<typeof selectActiveReportGeneration>>
      : null),
    [activeGenerationKey],
  );
  const pdfDeliveryNotice = reportPdfDeliveryNotice(report);
  const statusChip = React.useMemo(
    () => resolveReportStatusChip({
      report: actionReport,
      renderedReport: report,
      site: reportSiteWithSed,
      generation: activeGeneration,
      truthQuestionCount: truthQuestions.length,
      requiresLegacySimulationRegeneration,
      pdfDeliveryNotice,
      isAdmin,
      templateStale,
    }),
    [
      actionReport, report, reportSiteWithSed, activeGeneration, truthQuestions.length,
      requiresLegacySimulationRegeneration, pdfDeliveryNotice, isAdmin, templateStale,
    ],
  );
  // The in-section quarantine / withheld / meta-leak notes, for the on-demand
  // "Section quality notes" dialog (the page itself renders none of them).
  const sectionNotices = React.useMemo(
    () => collectReportSectionNotices(report, { isAdmin }),
    [report, isAdmin],
  );
  const [showSectionNotes, setShowSectionNotes] = React.useState(false);
  const liveHighConflictCount = React.useMemo(
    () => liveHighSeverityConflicts(report, reportSiteWithSed).live,
    [report, reportSiteWithSed],
  );
  const narrativeDegraded = isNarrativeDegraded(report);
  // resolveDraftTierUx can build the full document model for legacy bodies —
  // memoised on the action report, like the chip above.
  const legacyDraft = React.useMemo(
    () => resolveDraftTierUx(actionReport).kind === 'legacy-draft',
    [actionReport],
  );
  const providerConfigBroken = isAdmin && misconfiguredProviders(report.providerStatuses).length > 0;
  const handleOpenProviderHealth = React.useCallback(() => {
    // The event the former ProviderMisconfiguredBanner CTA (and the
    // ProviderFailureCard config CTA) fire; App.tsx routes it to the
    // AdminPanel Health tab.
    window.dispatchEvent(new CustomEvent('open-admin-panel', { detail: { tab: 'health' } }));
  }, []);

  // RN-2 — derive an auto-fill hint for a given sectionId by reading
  // the report's coverage matrix (`coverageCells`). The CAR-1..5
  // provider chains stamp `{fillSource, providerId, providerLabel,
  // value}` per requirement on the report at gen time; if `fillSource`
  // is anything other than 'missing' AND a provider id is present, the
  // chip is offered. Conservative — returns undefined when any of the
  // required fields are absent (the InsufficientData chip is then
  // simply not rendered).
  const autoFillFor = React.useCallback((sectionId: string): { providerId: string; providerLabel: string } | undefined => {
    const cells = (report as unknown as {
      coverageCells?: Record<string, {
        fillSource?: string;
        providerId?: string;
        providerLabel?: string;
        value?: string;
      }>
    }).coverageCells;
    const cell = cells?.[sectionId];
    if (!cell) return undefined;
    if (!cell.fillSource || cell.fillSource === 'missing') return undefined;
    if (typeof cell.providerId !== 'string' || !cell.providerId.trim()) return undefined;
    if (typeof cell.value !== 'string' || !cell.value.trim()) return undefined;
    return {
      providerId: cell.providerId,
      providerLabel: typeof cell.providerLabel === 'string' && cell.providerLabel.trim() ? cell.providerLabel : cell.providerId,
    };
  }, [report]);

  // Citation unification — the in-text `[Ref N]` marker must agree with
  // the appendix `[Ref N]` rows. HR-QA fix (2026-06-10): the prior
  // `computeAppendixVisibleRefIndices` dropped every `citedInThisReport ===
  // false` row, but the APPENDIX renderer has a POPULATE FALLBACK — when the
  // cited-only filter would empty the list it surfaces ALL non-sentinel refs
  // (the gen-time markCitations pass routinely mis-flags every row uncited).
  // So the appendix rendered a `#ref-N` anchor while this set, applying the
  // stricter predicate, marked the SAME token out-of-bounds → `[Ref ?]`. That
  // desync produced the 49-61 orphaned citations per report. Use the resolver
  // that mirrors the appendix's EFFECTIVE render predicate (including the
  // populate fallback) so every token that has a rendered row resolves.
  const appendixVisibleRefIndices = React.useMemo(
    () => {
      const visible = computeEffectiveAppendixVisibleRefIndices(report.references, { reportType: report.reportType });
      return admittedReportReferenceIndices(report, visible ?? undefined);
    },
    [report],
  );

  const renderCitedText = React.useCallback(
    (text: string | undefined | null) =>
      walkClaimsText(text, report.references, report.claims, appendixVisibleRefIndices),
    [report.references, report.claims, appendixVisibleRefIndices],
  );

  // Immutable V2 artifacts treat Contents as front matter. Build the node once
  // and mount it before the first body section below. Legacy reports retain the
  // historical Cover -> Executive Summary -> Contents DOM order so this rollout
  // does not silently repaginate existing revisions.
  const tableOfContents = (() => {
    if (!isVisible('toc') || theme.tocEnabled === false) return null;
    if (coverCarriesTocActive) return null;
    const allTocEntries = deriveTocEntries({
      reportType: report.reportType,
      sectionOrder,
      effectiveVisibility,
      reportOverrides: editedReport.headingOverrides,
      templateOverrides: activeTemplate?.headingOverrides,
      report: report as unknown as Record<string, unknown>,
    });
    const tocEntries = coverHasExecSummaryContent
      ? allTocEntries.filter(e => e.id !== 'executiveSummary' && e.id !== 'executive')
      : allTocEntries;
    if (tocEntries.length < 3) return null;
    return (
      <EditorialSectionShell
        data-section="toc"
        data-section-id="toc"
        data-page-numbering="front-matter"
        data-section-flow="span"
        style={immutableArtifactMode
          ? { ...sectionStyle('toc'), order: -1 }
          : sectionStyle('toc')}
        shellPadding={inlineSectionShellPadding}
        omitSectionSidebar={omitSectionSidebar}
        theme={theme}
        extraClassName={inlineSectionShellPadding ? inlineSectionChrome : undefined}
      >
        <TableOfContents
          entries={tocEntries}
          columns={sectionPageColumnCount('toc')}
          pageNumbers={tocPageNumbers}
          pageNumberFormat={presentation.pageNumberFormat}
          heading={resolveHeading(
            'toc',
            'Contents',
            editedReport.headingOverrides,
            activeTemplate?.headingOverrides,
          )}
        />
      </EditorialSectionShell>
    );
  })();

  return (
    // v1.2.1 #2 — single theme source. Hook consumers (SleekSection, SectionShell,
    // ContextualKeyFindingsBlock) read useEffectiveReportTheme(); without this they
    // got the RAW store theme (technical → IBM Plex Mono) while prop-threaded
    // sections got the resolved editorial preset — same report, two fonts. Wrapping
    // the body in the resolved `theme` makes the hook return it everywhere, killing
    // the monospace leak + the technical-scale column mis-pack. Mirrors PrintReportView.
    <ChartShrinkContext.Provider value={chartShrinkMap}>
      {/* Reader color mode for the whole report subtree. Nested components that
          call useEffectiveReportTheme() directly re-derive their theme through
          coerceBrandStrings (which strips the Symbol the prop-drilled `theme`
          carries), so they read the mode from THIS context to re-stamp. Export
          resolves 'light', so export hosts are byte-identical. */}
      <ReportReaderModeContext.Provider value={reportReaderMode}>
      <ThemeOverrideProvider value={theme}>
        <ViewModeProvider value={audienceMode}>
        {/* In-section notes (quarantine, withheld narrative, admin meta-leak)
            render only in delivered copies — the PDF / print tree and the
            public share. The interactive report page shows none of them
            (William, 2026-10-13); its header chip + Section quality notes
            carry the same information. */}
        <InSectionNoticesProvider visible={audienceMode === 'export' || audienceMode === 'public-share'}>
        {/* Task 3 — make the studio-preview flag reachable by deep section
        components (the inline + router-mounted pathway comparison) so empty
        optional sections self-omit in the live viewer / export but KEEP their
        placeholder in the Template Studio preview. */}
        <StudioPreviewProvider value={studioPreview}>
          <FigureCounterProvider>
            <div className="space-y-8 pb-12 print:pb-0">
              {/* Reader chrome above the cover: the sticky header owns
           metadata, the ONE status chip, Rerun, Review quality details and
           the ⋮ actions. There are no banners on report pages (William,
           2026-10-13) — the reason-specific recovery that used to sit in
           text banners below it (legacy draft, quality hold, invalid
           artifact, degraded narrative, evidence conflict, input drift…)
           is the header chip plus ⋮ entries.
           The legacy ReportImprovementsPanel (and the "Improve" tab
           that hosted it) was deleted 2026-05-14 — the Supporting
           Documents checklist on the Site Evidence tab is now the
           single source of truth for evidence-gap work. The header
           strip carries a "N missing critical docs →" pill for
           in-report awareness.
           TSC-1 (2026-05-18) — gate the strip on `audienceMode !== 'export'`.
           The Studio's `TemplatePreviewPane` mounts this viewer with
           `viewMode="export"` to suppress runtime chrome, and the EDIT /
           RERUN / INSIGHTS (now Review quality details) buttons +
           date+version chip were leaking into the design surface. Live
           export takes a separate immutable artifact-job render path, so
           this guard does not touch delivered PDF/DOCX/PPTX bytes. */}
              {audienceMode !== 'export' && (
                <ReportHeaderStrip
                  report={actionReport}
                  site={reportSite}
                  onBackToReports={handleBackToReports}
                  showTemplateLinkChip={false}
                  templateName={activeTemplate?.name ?? null}
                  coverLayout={coverLayout}
                  templateLinked={!!reportTemplateId && reportTemplateId === activeTemplate?.id}
                  availableTemplates={reportTemplates}
                  currentTemplateId={reportTemplateId ?? null}
                  onLinkTemplate={async (templateId) => {
                    try {
                      const res = await applyTemplateToExistingReports(templateId, [report.id]);
                      if (res.linked > 0) {
                        toast.success('Template linked — cover, sections and colours updated.');
                      } else {
                        toast.error('Could not link the template — reload and retry.');
                      }
                    } catch (e) {
                      toast.error(`Link failed: ${(e as Error)?.message ?? 'unknown error'}`);
                    }
                  }}
                  onRerun={handleOpenRerunPanel}
                  rerunLabel={
                    report.reportType === 'executive-summary'
                      ? 'Open live Decision'
                      : undefined
                  }
                  // "Review quality details" replaced the Insights button
                  // (2026-10-13) — same Audit-tab handler, same badge count.
                  onReviewQuality={openQualityAudit}
                  reviewQualityBadgeCount={(() => {
                    const narrativeTexts = collectNarrativeStrings(report);
                    const joinedNarrative = narrativeTexts.join('\n');
                    const baseSanity = (report.sanityFindings ?? []).filter(
                      (f) => !isFalsePositiveQueueDurationFindingText(f.finding ?? '', narrativeTexts),
                    );
                    const unresolvedSanity = baseSanity.filter(
                      (f) =>
                        (!f.resolution || f.resolution === 'apply') &&
                        !isVerifiedSanityCategory(f.category) &&
                        (f.severity as string) !== 'verified',
                    ).length;
                    const existingTexts = new Set(
                      baseSanity.map((f) => (f.finding ?? '').trim().toLowerCase()),
                    );
                    const reviewIssues = report.qualityReview?.issues ?? [];
                    let synthesizedCount = 0;
                    let suppressedCount = 0;
                    for (let idx = 0; idx < reviewIssues.length; idx += 1) {
                      const rawIssue = (reviewIssues[idx] ?? {}) as {
                        issue?: unknown;
                        description?: unknown;
                        message?: unknown;
                      };
                      const primaryText =
                        typeof rawIssue.issue === 'string' && rawIssue.issue.trim().length > 0
                          ? rawIssue.issue.trim()
                          : typeof rawIssue.description === 'string' && rawIssue.description.trim().length > 0
                            ? rawIssue.description.trim()
                            : typeof rawIssue.message === 'string' && rawIssue.message.trim().length > 0
                              ? rawIssue.message.trim()
                              : '';
                      const text =
                        primaryText ||
                        `Flagged quality review issue (#${idx + 1}) has no stored description text on this report record.`;
                      if (
                        isFalsePositiveQueueDurationFindingText(text, narrativeTexts) ||
                        isReviewIssueAlreadyQualifiedInText(text, joinedNarrative, report) ||
                        (/water-use screen check flagged out-of-scope load bindings/i.test(text) &&
                          truthQuestions.every((q) => q.detector !== 'waterUseScreening'))
                      ) {
                        suppressedCount += 1;
                        continue;
                      }
                      if (existingTexts.has(text.toLowerCase())) continue;
                      existingTexts.add(text.toLowerCase());
                      synthesizedCount += 1;
                    }
                    const declaredIssueCount =
                      typeof report.qualityReview?.issueCount === 'number' &&
                      Number.isFinite(report.qualityReview.issueCount)
                        ? Math.max(0, Math.trunc(report.qualityReview.issueCount))
                        : 0;
                    const missingDeclared = Math.max(
                      0,
                      declaredIssueCount - (unresolvedSanity + synthesizedCount + suppressedCount),
                    );
                    return unresolvedSanity + synthesizedCount + missingDeclared + truthQuestions.length;
                  })()}
                  statusChip={statusChip}
                  // Former banner-only actions → ⋮ entries, each offered only
                  // while its state holds. Regeneration actions step aside
                  // while a run for this site + type is in flight.
                  onConfirmTruth={truthQuestions.length > 0 && resolvedSiteId ? openTruthAudit : undefined}
                  truthQuestionCount={truthQuestions.length}
                  onFinalizeDraft={legacyDraft && report.siteId && !activeGeneration ? handleFinalizeDraft : undefined}
                  isFinalizing={isFinalizing}
                  onRefreshNarrative={narrativeDegraded && !activeGeneration
                    ? () => { void useStore.getState().regenerateNarrativeOnly(report.id); }
                    : undefined}
                  onReviewConflicts={report.siteId && liveHighConflictCount > 0
                    ? () => handleOpenSiteEvidence({ filterReportId: report.id, focus: 'conflicts' })
                    : undefined}
                  onOpenSectionNotes={sectionNotices.length > 0 ? () => setShowSectionNotes(true) : undefined}
                  sectionNoteCount={sectionNotices.length}
                  onOpenProviderHealth={providerConfigBroken ? handleOpenProviderHealth : undefined}
                  onRetryPdf={pdfDeliveryNotice ? () => void handleExportPDF(true) : undefined}
                  missingCriticalCount={missingCriticalCount}
                  reportFieldCoveragePct={reportFieldCoveragePct}
                  onOpenSiteEvidence={report.siteId ? handleOpenSiteEvidence : undefined}
                  onDelete={() => setShowDeleteDialog(true)}
                  onExportPdf={() => void handleExportPDF()}
                  onOpenSummary={actionReport.siteId && reportSummaryEligibility(actionReport).ok ? () => rerunRouteNavigate(reportSummaryPath(actionReport.siteId!, actionReport.id)) : undefined}
                  onExportSummaryPdf={reportSummaryEligibility(actionReport).ok ? () => {
                    const requestIdentity = exportIdentity;
                    const requestEpoch = exportLifecycleEpochRef.current;
                    const requestSeq = ++exportRequestSeqRef.current;
                    setIsExporting(true);
                    void downloadReportSummary(actionReport, {
                      shouldSave: () => isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch),
                    }).catch(e => {
                      if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
                        toast.error((e as Error).message);
                      }
                    }).finally(() => {
                      if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
                        setIsExporting(false);
                      }
                    });
                  } : undefined}
                  pdfBuildPending={isExporting}
                  onExportDocx={async () => {
                    if (requiresLegacySimulationRegeneration) {
                      toast.error('This report contains retired legacy simulation evidence. Regenerate it before export.', { id: 'docx-q' });
                      return;
                    }
                    const requestIdentity = exportIdentity;
                    const requestEpoch = exportLifecycleEpochRef.current;
                    const requestSeq = ++exportRequestSeqRef.current;
                    const expectedFilename = artifactFilename('docx');
                    setIsExporting(true);
                    try {
                      const blob = await buildArtifact('docx', 'docx-q');
                      if (!isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) return;
                      const { saveAs } = await import('file-saver');
                      if (!isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) return;
                      saveAs(blob, expectedFilename);
                      toast.success('Word document exported', { id: 'docx-q' });
                    } catch (e) {
                      if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
                        console.error('[SiteRagReportView] DOCX export failed:', e);
                        toast.error(`Export failed: ${(e as Error).message}`, { id: 'docx-q' });
                      }
                    }
                    finally {
                      if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
                        setIsExporting(false);
                      }
                    }
                  }}
                  onExportPptx={async () => {
                    if (requiresLegacySimulationRegeneration) {
                      toast.error('This report contains retired legacy simulation evidence. Regenerate it before export.', { id: 'pptx-q' });
                      return;
                    }
                    const requestIdentity = exportIdentity;
                    const requestEpoch = exportLifecycleEpochRef.current;
                    const requestSeq = ++exportRequestSeqRef.current;
                    const expectedFilename = artifactFilename('pptx');
                    setIsExporting(true);
                    try {
                      const blob = await buildArtifact('pptx', 'pptx-q');
                      if (!isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) return;
                      const { saveAs } = await import('file-saver');
                      if (!isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) return;
                      saveAs(blob, expectedFilename);
                      toast.success('PowerPoint exported', { id: 'pptx-q' });
                    } catch (e) {
                      if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
                        console.error('[SiteRagReportView] PPTX export failed:', e);
                        toast.error(`Export failed: ${(e as Error).message}`, { id: 'pptx-q' });
                      }
                    }
                    finally {
                      if (isExportRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
                        setIsExporting(false);
                      }
                    }
                  }}
                  onExportSed={report.sedText ? () => {
                    const blob = new Blob([report.sedText!], { type: 'text/markdown' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `SED_${report.siteName.replace(/\s+/g, '_')}.md`; a.click(); URL.revokeObjectURL(url);
                  } : undefined}
                  onExportKmz={reportSite && (reportSite.siteBoundary?.length || (reportSite.siteLayers?.length ?? 0) > 0)
                    ? handleExportKmzShared
                    : undefined}
                />
              )}

              {/* NO BANNERS ON REPORT PAGES (William, 2026-10-13: "i dont want
                  any banners on report pages"). The reader chrome used to stack
                  reason-specific banners here — PDF delivery, truth questions,
                  DraftBanner (draft / quality hold / audit recommended /
                  invalid), ReportAlertBanner (degraded narrative, evidence
                  conflicts, SED / supply-chain / financial drift, citation
                  rewrite), legacy simulation evidence, template refresh and
                  the admin ProviderMisconfiguredBanner. Their state now
                  resolves to ONE compact chip in ReportHeaderStrip
                  (`statusChip`, reportStatusChip.ts) and every action that
                  lived only on a banner is a header ⋮ entry. None of them ever
                  reached the PDF (all were export-gated, most print:hidden). */}

              {/* Citation-integrity verification banner removed per product decision —
          reports no longer surface a "N citations marked for verification" note.
          Genuine block-severity citation errors remain visible in the Audit tab. */}

              {/* NB-4 — admin-only "Cleaned at render" chip. Renders when the
           NB-2 memo's lazy hygiene backfill annotated this read with a
           `backfill-strip-applied` qualityWarnings entry. Tooltip lists
           the strip rules that fired so ops can spot a strip-rule
           regression (e.g. RNH-5 firing on a brand-new report would
           signal a gen-time bypass somewhere in the pipeline). Gated
           on `isAdmin && audienceMode !== 'export'` so:
             - Non-admin viewers never see the chip.
             - PDF / DOCX exports strip it even for admin users (the
               customer artefact shouldn't carry internal hygiene
               telemetry). */}
              {isAdmin && audienceMode !== 'export' && (() => {
                const backfillWarning = report.qualityWarnings?.find(
                  w => w.type === 'backfill-strip-applied',
                );
                if (!backfillWarning || backfillWarning.type !== 'backfill-strip-applied') return null;
                return (
                  <div
                    data-testid="backfill-strip-chip"
                    title={`Strips applied at render: ${backfillWarning.applied.join(', ')}`}
                    className="my-2 inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
                  >
                    <span aria-hidden>•</span>
                    <span>Cleaned at render ({backfillWarning.applied.length})</span>
                  </div>
                );
              })()}

              {/* CI-2 (CSE-FOLLOWUP-CONFLICT-DETECTION) — admin-only chip
           surfacing internal CSE-claim conflicts. Gated on
           `isAdmin && audienceMode !== 'export'` so the PDF / DOCX
           export artefacts never carry this internal-quality signal.
           Renders nothing when no conflicts fired. */}
              {audienceMode !== 'export' && (
                <CseConflictChip
                  report={report}
                  isAdmin={isAdmin}
                  onOpenSiteEvidence={report.siteId ? handleOpenSiteEvidence : undefined}
                />
              )}

              {/* Modals — kept mounted so the strip's handlers have somewhere
           to dispatch into. They have no visible footprint until
           toggled. */}
              <RerunWrapper report={report} open={showRerunPanel} onClose={handleCloseRerunPanel} />

              {/* Workflow Prompt 7 — delete confirmation. Post-delete: navigate back
          by clearing the active report so SiteRagEngine renders the landing
          page (or the sites list if the report had no siteId). */}
              <DeleteReportDialog
                report={report}
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onDeleted={() => {
                  setShowDeleteDialog(false);
                  if (report.siteId) {
                    try {
                      const target = siteOverviewPath(report.siteId, 'reports');
                      window.history.replaceState({}, '', target);
                      rerunRouteNavigate(target, { replace: true });
                    } catch { /* ignore */ }
                    useStore.getState().setActiveSite(report.siteId);
                  }
                  useStore.getState().setActiveRagReport(null);
                }}
              />
              {/* Section quality notes — the on-demand home (header ⋮) for what
           the in-section quarantine / withheld / meta-leak notes offered on
           the page: the reason, the original AI output, Author and Re-run
           section. Section re-runs step aside while a run is in flight. */}
              {showSectionNotes && sectionNotices.length > 0 && (
                <SectionNoticesDialog
                  notices={sectionNotices}
                  onClose={() => setShowSectionNotes(false)}
                  onAuthor={() => setIsEditing(true)}
                  onRerunSection={activeGeneration ? undefined : (sectionId) => { void handleSectionRegen(sectionId); }}
                />
              )}
              {/* SET-D1 (2026-05-17) — the inline <DocumentLibrary /> mount
           (opened via the now-removed ⋮ More "Documents" entry) was
           retired. Doc library lives at the top-level Documents tab. */}

              {/* SED-version drift is the header status chip's "Inputs
           changed" state (pickReportRefreshAlert, sed-drift case). The
           standalone SedVersionBanner mount — and, since 2026-10-13, every
           other report-page banner — is gone. */}

              {/* EV1 (2026-05-15) — the inline <SEDBrowserPanel> mount that
           used to live here was removed. It was a confusing duplicate
           surface: the dedicated Site Evidence tab hosts the same data
           with a richer toolbar (variable browser + amendments +
           conflicts + section trail + doc checklist + refresh).
           Reach the canonical tab via the always-on breadcrumb in the
           report header (EV2) — clicking it carries
           `?filterReportId={report.id}` so the tab opens pre-scoped to
           the variables this report cites (EV3). The unrendered
           SEDBrowserPanel function, and the SEDSectionEditor it wrapped,
           were deleted on 2026-09-29. */}

              {/* Export buttons folded into ReportHeaderStrip's ⋮ More menu
           (Reader Polish Prompt 3); the unresolved-sanity count is the
           Review quality details badge. Quality/Sanity/Cost live on the
           site Audit tab's Report-quality block. */}

              {/* Report Content. Reader Polish follow-up — locked to the
           light-mode color tokens regardless of the app's global
           theme. The dark-mode app chrome makes secondary text on
           the white report card near-invisible; `data-theme-scope`
           re-asserts light values for every text/border token
           inside the tree (see index.css for the rule).

           SLK-H4 — partial: SiteRagReportShell is built, primitives
           ship, useEffectiveReportTheme is wired here, SectionShell
           carries the Sleek skin (id + data-section + h2[data-heading]),
           Recharts <Tooltip> instances use SleekChartTooltip. But the
           IN-VIEWER mount of the shell chrome is deferred: the
           paginated reportRef body's div tree has 2 more `</div>`
           than `<div>` within the reportRef block (the imbalance
           balances against an outer wrapper above reportRef), so
           wrapping reportRef inline in <SleekShell>...</SleekShell>
           confuses the JSX parser into thinking the shell never
           closes. Follow-up: decompose reportRef body into balanced
           sub-trees, then re-enable the inline wrap. SLK-D + SLK-S
           consume the shell directly via the './SiteRagReportShell'
           module. */}

              <div
                // R-CRASH (2026-06-27/28): DEFENSE-IN-DEPTH key on the body's layout
                // signature (templateId + resolved column count). The ROOT CAUSE of the
                // "removeChild … not a child of this node" crash on a template link/SWITCH
                // — the post-render `appendChild` reorder effects mutating React-managed
                // [data-section] nodes out-of-band — is now fixed at source: the live tree
                // is no longer reordered (deck/export ordering moved to the detached pagedjs
                // clone in `reorderClonedSectionsByFlowOrder`). This key is RETAINED purely
                // as belt-and-suspenders: on a layout switch it remounts [data-report-root]
                // as ONE unit (a single valid removeChild against the stable parent) rather
                // than risk reconciling any future out-of-band DOM. (The keyed remount alone
                // was insufficient — the effects re-desynced the fresh DOM, so the next
                // non-keyed reconcile crashed again; removing the mutation is the real fix.)
                key={`rpt-${report.templateId ?? 'stock'}-${themeBodyColumns}`}
                ref={reportRef}
                role="document"
                data-report-root
                data-demonstration-notice={reportDemonstrationDisclosure(report)?.notice}
                data-report-id={report.id}
                data-immutable-artifact={immutableArtifactMode ? 'true' : undefined}
                data-theme-scope="light"
                data-section-order-flatten={sectionOrderFlatten ? 'true' : undefined}
                aria-labelledby="cover-title"
                // Reader dark-mode marker — stamped ONLY when the on-screen reader
                // resolves dark (export always resolves 'light', so it is absent on
                // every export/paged/light render ⇒ byte-stable). The additive dark
                // island in index.css keys off this attribute to remap the report
                // tokens; inline `roleColor` chrome is handled by the mode carried
                // on `theme` (attachReaderMode). Placed after aria-labelledby so the
                // role↔label pair stays adjacent (a11y source-proximity guard).
                data-report-reader-mode={reportReaderMode === 'dark' ? 'dark' : undefined}
                data-report-body-columns={themeBodyColumns === 2 ? '2' : undefined}
                // Part L2 — editorial-monograph marker. Stamped whenever the
                // VITE_EDITORIAL_LAYOUTS flag is ON (editorialActive) — the editorial
                // structure is the DEFAULT for every preset, not only `editorial`. The
                // scoped pull-quote ([data-editorial-pullquote]) + lead-paragraph drop-cap
                // CSS are gated under `[data-report-root][data-editorial]`, so with the
                // flag OFF the attribute is absent and those rules never match — byte-stable.
                data-editorial={editorialActive ? 'true' : undefined}
                // A-container — ON-SCREEN (interactive viewer) only: center the report
                // card in a shared max-width column so every block (cover, body sections,
                // the closing IMPORTANT NOTICE disclaimer, and the footer) shares ONE
                // consistent left/right content edge instead of pinning hard-left with a
                // ragged ~50-60% measure. Stamped ONLY when `audienceMode !== 'export'`
                // so the PDF / DOCX / paged-deck export paths (fixed A4 page boxes) are
                // byte-stable — they must fill the page width, not a centered column.
                // The CSS (index.css `[data-report-content-column]`) caps width +
                // margin-inline:auto; `width:100%` keeps it inside the available `main`
                // area (already minus the persistent editor sidebar), so content is
                // never clipped on either edge. The cover stays full-bleed WITHIN this
                // card (its own page shell), so centering the card does not break it.
                data-report-content-column={audienceMode !== 'export' ? 'true' : undefined}
                // RRQ — export/print marker. Stamped ONLY in viewMode==='export' (the
                // server Puppeteer PDF + the /print client export, which mount
                // SiteRagReportView directly without the Paged.js deck). ChartFrame
                // reads this to mount its recharts container EAGERLY at the reserved
                // height — without it the export render measured a not-yet-laid-out
                // chart box and rendered legend-only. Absent on the interactive
                // viewer ⇒ byte-stable (the deck path is covered by data-report-paged).
                data-print-export={audienceMode === 'export' ? 'true' : undefined}
                // #93 — page-level two-column FLOW marker. Stamped on the report body
                // (the [data-report-root] subtree) ONLY when a two-column academic
                // preset is mounted inside the paged deck (pageFlowEngaged). The
                // page-flow CSS is gated `[data-report-paged][data-page-flow="2"]`, so
                // this marker is inert outside the deck and single-column / continuous
                // reports stamp nothing (byte-stable).
                data-page-flow={pageFlowEngaged ? '2' : undefined}
                // TSP-2 — `flex flex-col` so root-level section siblings (pages,
                // FeasibilityProfile, LcoeChart, ExtendedData) honour CSS
                // `order: N` from `sectionStyle()`. The watermark overlay uses
                // `position:absolute inset-0` so it sits on top of the flex
                // layout regardless. No `gap` here — the page divs carry their
                // own bottom borders for separation, matching the legacy
                // block-flow rendering.
                // HR-QA fix (2026-06-10) — print:shadow-none: the frame's drop shadow
                // printed as a smeared gray horizontal band in short PDFs (visible
                // whenever the report ends well above the last page boundary).
                // N1.5C-flat (EDITORIAL-MODE ONLY) — William's call: the editorial
                // monograph is a FLAT FULL-BLEED cream sheet, so when `editorialActive`
                // is ON the only drop-shadow (`shadow-2xl`) is dropped (the page bg /
                // `--page-bg` var + `overflow-hidden` are kept verbatim). Flag OFF keeps
                // the floating card shadow so the flag-OFF frame stays byte-identical.
                className={`${editorialActive ? '' : 'shadow-2xl '}print:shadow-none overflow-hidden relative flex flex-col${templatePagePhysicsActive
                  ? ` template-page-physics template-export-cols-${templatePageBreakPlan.printColumns}`
                  : ''
                  }`}
                data-template-export-columns={
                  templatePagePhysicsActive ? templatePageBreakPlan.printColumns : undefined
                }
                // Two-column DENSITY hook (Proceedings = compact). Only stamped in
                // two-column compact mode so single-column / legacy / comfortable
                // reports are byte-stable; the inline CSS below tightens the flow
                // column gap when it equals 'compact'.
                data-report-body-density={
                  themeBodyColumns === 2 && theme.density === 'compact' ? 'compact' : undefined
                }
                style={{
                  // W4 BLACK-PAGES FIX — the on-screen continuous report frame is a
                  // light-paper document, ALWAYS, independent of the app's dark/light
                  // mode. Previously this read raw `theme.backgroundColor`, which in the
                  // LIVE viewer resolves off the store/app report theme — when that
                  // theme is dark (no light-paper preset overriding it) the frame went
                  // BLACK. Routing through `resolvePageBg` (the SAME paper-guarded
                  // resolution the `--page-bg` var below uses) substitutes white paper
                  // for any dark-luminance value while leaving the legitimate light
                  // preset paper tints (journal/monograph/review/white) verbatim — so
                  // the on-screen frame matches the `.pagedjs_page` deck + the PDF path.
                  // DARK reader mode (on-screen only — export resolves 'light')
                  // paints the sheet a deep graphite that matches the CSS island
                  // (`READER_DARK_PAGE_BG` ↔ index.css). Light path is verbatim.
                  backgroundColor: reportReaderMode === 'dark'
                    ? READER_DARK_PAGE_BG
                    : resolvePageBg(presentation.backgroundColor),
                  // W4 RC2 — publish the resolved paper tint as the `--page-bg` CSS
                  // variable on the report frame so BOTH the on-screen frame AND the
                  // `.pagedjs_page` print/PDF surface (index.css reads `var(--page-bg)`)
                  // resolve the SAME colour. Defaults to white when no tint is set, so
                  // a no-preset report stamps `#ffffff` and stays byte-identical. The
                  // var equals the paper-guarded `presentation.backgroundColor`,
                  // surfaced via the contract so the paper tint reaches the PDF path,
                  // not just the screen `backgroundColor` above.
                  [PAGE_BG_VAR]: reportReaderMode === 'dark'
                    ? READER_DARK_PAGE_BG
                    : resolvePageBg(presentation.backgroundColor),
                  // RRQ CHARTS FIX (B) — publish the printable A4 column width so the
                  // export-root cap (index.css `[data-report-root][data-print-export]`)
                  // pins the report-root to the FINAL print width during the
                  // screen-media headless load. This makes the w-full chart wrappers
                  // measure ~688px at load time, so the LIVE Recharts containers build
                  // their scales/clip/series geometry at the print width instead of the
                  // 1280px puppeteer viewport (which `page.pdf()` then squeezed,
                  // clipping the data series out of the narrow column). Stamped always;
                  // it is only consumed by the `[data-print-export]`-scoped cap, so the
                  // interactive viewer / paged deck are byte-stable.
                  '--report-printable-width': REPORT_PRINTABLE_WIDTH_CSS,
                  // Root text fallback. Light: verbatim (byte-stable). Dark:
                  // the on-dark body ink from the ramp (theme carries the mode).
                  color: reportReaderMode === 'dark'
                    ? roleColor(theme, 'body')
                    : roleColor(theme, 'body'),
                  fontFamily: theme.bodyFont,
                  '--report-body-font': theme.bodyFont,
                  // Editorial APPARATUS face (section eyebrows/numerals, micro-labels,
                  // captions, TOC numerals, citation markers). Falls back to the body
                  // font when the theme sets no apparatusFont, so legacy reports — where
                  // the apparatus resolved to `--report-body-font` — stay byte-identical.
                  '--report-apparatus-font': theme.apparatusFont ?? theme.bodyFont,
                  // House-style 2026-06-29 — expose the resolved brand ACCENT (teal in
                  // the academic-editorial preset) as a CSS var so the scoped editorial
                  // rules in index.css (drop-cap, centered pull-quote top-rule) + the
                  // citation markers can tint to it. roleColor keeps it theme-aware, so
                  // every preset threads its OWN accent (not a hardcoded teal).
                  '--report-accent': roleColor(theme, 'accent'),
                  '--heading-color': reportHeadingInk(theme),
                  '--heading-font': theme.headingFont,
                  '--sidebar-color': theme.sidebarColor,
                  // TSE-2 C3 — typography scale multiplier. Existing typography
                  // rules can multiply onto this CSS var without code changes.
                  // Default 1 keeps every legacy report visually identical.
                  '--theme-typography-scale': String(theme.themeTypographyScale ?? 1),
                  // Unified report BODY size (scale-driven + theme-scaled). Consumed by
                  // the `[data-report-root] .text-sm` override in index.css so the
                  // Tailwind text-sm body utility tracks the densified 9.5pt baseline
                  // (reportTypographySizes(theme).body == reportShellBodyFontSize).
                  '--report-body-size': reportTypographySizes(theme).body,
                  // Two-column body text scale. In two-column mode the academic
                  // presets render denser body type via `bodyTextScale`, BOUNDED so
                  // the effective body font never drops below the ~12px token floor
                  // (0.75 × 16px = 12px). Single-column / legacy reports use 1, so
                  // they are byte-identical. Both multipliers fold into fontSize.
                  // Legacy/inherited body base. Densified 0.9rem (~14.4px) from
                  // 1rem/16px 2026-06-27 per William; the editorial scale body
                  // (reportTypographySizes(theme).body, now 0.79rem) overrides
                  // this for editorial prose, so this only governs inherited text.
                  fontSize: `calc(0.9rem * ${theme.themeTypographyScale ?? 1} * ${effectiveBodyTextScale})`,
                } as React.CSSProperties}>
                {showConfidentialSidebar && sectionOrderFlatten && (
                  <ConfidentialSidebar theme={theme} />
                )}
                {/* Per-section page column layout (template `columnOverride` + page columns). */}
                <style>{`
          /* W2 RC1 — THE central fix. The per-section ISLAND column box
             (column-count:2) must carry the SAME page-flow carve-out the
             heading rule below (~:2675) already has:
             :not([data-section-flow="flow"]):not([data-section-flow="span"]).
             When PAGE-LEVEL flow engages, a prose section simultaneously carries
             data-section-columns="2" (island) AND data-section-flow="flow"/"span"
             (page-flow). Without this carve-out the section keeps its OWN
             column-count:2 island INSIDE the column-count:2 page-flow box → 4
             effective columns, content crushed to quarter width (the 10-iteration
             squash). Excluding flow/span here means a page-flow section is NEVER
             also an island — it inherits the single continuous flow box instead.
             Single-column presets carry no data-section-columns="2", so this is
             inert there (byte-stable). */
          [data-section-columns="2"]:not([data-section="cover"]):not([data-section-flow="flow"]):not([data-section-flow="span"]) {
            display: block;
            column-count: 2;
            column-gap: 1.5rem;
            column-fill: balance;
          }
          [data-section-columns="2"]:not([data-section="cover"]) > section,
          [data-section-columns="2"]:not([data-section="cover"]) [data-sleek-section] {
            break-inside: avoid;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
          }
          [data-section-columns="1"]:not([data-section="cover"]) {
            column-count: 1;
          }

          /* ── HTML READER: single-column body (William, 2026-08-06) ──
             On SCREEN the body sections read as one column. The two-column
             academic presets were designed for the paged/print artifact, where
             a 2-col measure is the point; in the continuous scrolling reader
             the same measure forces the eye back up the viewport on every
             section and makes long prose harder to follow.

             Scoped so it changes ONE surface:
               - @media screen — the puppeteer PDF and the @media print
                 rules in index.css render under print media and are untouched,
                 so the delivered artifact is byte-identical.
               - it mirrors the island selector above (including the
                 data-section-flow carve-outs), which is the CONTINUOUS
                 reader's path — the paged deck flows through
                 [data-page-flow-box] instead and never matches here.

             KEPT at two columns by explicit request: toc (a 2-col contents
             list is the convention and reads better dense), plus conclusion
             and appendix, which stay exactly as they were. lcoeChart is
             already single-column via its supportsColumnLayout: false
             registry guard, so it needs no exclusion.

             NB: the kept-section values are deliberately UNQUOTED. They are
             valid CSS identifiers, and the TNV structure sentinel scans this
             file for data-section="X" literals expecting a matching id="X" on
             a JSX wrapper — conclusion and appendix are rendered in
             SectionsRouter, not here, so quoting them here trips a guard that
             cannot distinguish CSS from JSX. Do not "tidy" these into quotes. */
          @media screen {
            [data-section-columns="2"]:not([data-section=cover]):not([data-section-flow="flow"]):not([data-section-flow="span"]):not([data-section=toc]):not([data-section=conclusion]):not([data-section=appendix]) {
              column-count: 1;
            }
          }

          /* ── Two-column academic presets (Quarterly / Review / Proceedings) ──
             Scoped to [data-report-body-columns="2"] so single-column / legacy
             reports are byte-identical (the attribute is absent there). */
          /* Academic prose measure: LEFT-aligned (TYPO-UNIFY William 2026-06-09
             — justified text produced ragged word-gaps; all report body text is
             now left-aligned and consistent), with orphan/widow control so each
             column reads cleanly. Applies to the column-flowed prose sections
             (data-section-columns="2") only. */
          [data-report-body-columns="2"] [data-section-columns="2"]:not([data-section="cover"]) {
            text-align: left;
            orphans: 3;
            widows: 3;
          }
          /* Wide figures / tables / charts must NOT be clipped to one column.
             They span both columns (column-span: all) and never split across a
             column break, so every figure/table that renders in single-column
             ALSO renders in two-column — no data loss. The map figure stays
             in-column (framed-figure / exhibit per Phase 4) because it carries
             [data-keep-in-column]. */
          ${spanColumnsSelectors(
                  '[data-report-body-columns="2"] [data-section-columns="2"]:not([data-section="cover"])',
                )} {
            column-span: all;
            -webkit-column-span: all;
            break-inside: avoid;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
          }
          /* ── #159/#163 PER-SECTION heading-span ──
             EVERY section that owns its own column-count:2 box
             ([data-section-columns="2"], rule above) is a journal "island":
             its H2 must span the FULL island width with the body flowing in
             two columns BENEATH it. Previously this was over-gated on
             [data-section-flow="island"] — but the modern per-type sections
             (SectionsRouter → EditorialSectionShell) stamp data-section-columns
             WITHOUT a flow marker, so the gate never matched and their headings
             stayed trapped in column 1 (the "heading alone in the empty left
             half, body crammed into the right column" defect). The span is the
             defining behaviour of a 2-col island, so it now keys off
             data-section-columns="2" directly. EXCLUDED: the cover, and the
             page-level FLOW path (data-section-flow="flow"/"span") where the
             heading packs inline with its column instead (rules below).
             Single-column presets carry no data-section-columns="2", so this
             never matches there (byte-stable). text-align:left so a wrapping
             heading is never justify-stretched by the section's prose measure. */
          [data-report-body-columns="2"] [data-section-columns="2"]:not([data-section="cover"]):not([data-section-flow="flow"]):not([data-section-flow="span"]) > h2[data-heading="true"] {
            column-span: all;
            -webkit-column-span: all;
            text-align: left;
            break-inside: avoid;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
          }
          /* Bordered content CARDS inside a flowing two-column section must not
             split mid-card across the column break — they read as discrete
             units (assessment blocks, finding lists). Same break protection
             the wide-figure / section rules already apply, extended to the
             explicit [data-flow-card] hook so the column flow stays coherent
             (a coherent two-column journal, not a torn mix). */
          [data-report-body-columns="2"] [data-section-columns="2"]:not([data-section="cover"]) [data-flow-card] {
            break-inside: avoid;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
          }

          /* ── Compact two-column density (Proceedings) ──
             The IEEE/ACM-dense preset tightens the column gap of flowing
             two-column sections so the page reads denser. Scoped to
             [data-report-body-density="compact"], stamped ONLY in two-column
             compact mode, so every other preset (and all single-column /
             legacy reports) is byte-identical. The default flow gap is
             1.5rem (above); compact narrows it to 1rem. */
          [data-report-body-density="compact"] [data-section-columns="2"]:not([data-section="cover"]) {
            column-gap: 1rem;
          }

          /* ════════════════════════════════════════════════════════════════
             #93 — PAGE-LEVEL two-column FLOW (paged deck only)
             Gated on BOTH the [data-report-paged] ancestor (stamped by
             PaginatedReportView on the paged deck) AND [data-page-flow="2"]
             (stamped on this report body for two-column academic presets in
             page-flow mode). Outside the paged deck NEITHER marker pair is
             present, so every rule here is inert and the #159 per-section
             islands (above) govern — single-column / continuous reports are
             byte-identical. Mutually exclusive with the island rule: a section
             is data-section-flow="island" OR "flow"/"span", never matched by
             both column-span heading rules at once.

             The PAGE content box itself becomes ONE continuous two-column flow
             (column-fill: auto so columns fill top-to-bottom up to the page
             box height, not balanced — the user DECISION). PaginatedReportView
             owns the page box height; this only sets the column geometry. */
          /* W2 (d) — make the page-flow box TRANSPARENT to flex. The report root
             is normally display:flex; flex-direction:column so section siblings
             honour CSS order:N from sectionStyle(). But CSS order is IGNORED
             inside a multicol container — sections flow in DOM order — so a flex
             root would silently drop the user's drag-reorder once the page-flow
             box multicol engages. When data-page-flow="2" is on, switch the root
             to display:block so it is a plain block ancestor of the multicol
             box, and rely on the JS pre-sort (the reorder effect above) to emit
             the post-cover sections in resolved order in the DOM. Gated on the
             [data-report-root] + [data-page-flow="2"] pair so single-column /
             continuous reports keep the flex layout (byte-stable). */
          [data-report-root][data-page-flow="2"] {
            display: block;
          }
          /* ROUND-3 DEFECT-A — the page-flow box is a FULL-WIDTH single column.
             pagedjs 0.4.3's chunker forces single-column on .pagedjs_page_content
             and never fills a 2nd column of an inner multicol box, so a
             column-count:2 box (the old approach) left column 2 EMPTY (dead 2-col
             prose) AND squashed flow sections to ~317px (crushing their grids).
             The real 2-column PROSE packing is now done POST-LAYOUT by
             reflowFlowProseToTwoColumns (measurePagesFromDom), which columnises
             ONLY the running-prose containers. The deck stylesheet
             (PaginatedReportView.buildPagedStylesheet) stamps column-count:1
             !important on the box; this on-screen rule matches so the offscreen
             export host pagedjs clones is consistent. */
          [data-report-paged][data-page-flow="2"] [data-page-flow-box],
          [data-report-paged] [data-page-flow="2"] [data-page-flow-box] {
            column-count: 1;
            column-gap: 1.5rem;
            column-fill: auto;
          }
          /* FLOW sections pack into the page column context with an INLINE
             heading. ROUND-3 DEFECT-A FIX — a flow section's PROSE must be
             allowed to SPLIT across the column boundary (break-inside auto),
             otherwise a break-inside-avoid section is an ATOMIC block that
             sits wholly in ONE column and NO paragraph ever flows into the
             second page column (the "2-col prose is dead" defect: every flow
             section was trapped in column 1). The discrete units that must NOT
             tear mid-block (cards / callouts / figures / tables / wide
             exhibits) keep their own break-inside-avoid via the dedicated
             card/figure/span rules BELOW — so the running prose flows across
             columns while cards stay coherent. The heading flows with its
             column (break-after avoid keeps it attached to its first prose
             line) and explicitly does NOT span both columns. */
          [data-report-paged][data-page-flow="2"] [data-section-flow="flow"],
          [data-report-paged] [data-page-flow="2"] [data-section-flow="flow"] {
            break-inside: auto;
            -webkit-column-break-inside: auto;
            page-break-inside: auto;
            display: block;
            column-span: none;
            -webkit-column-span: none;
            /* W2 RC1 — collapse any RESIDUAL inner island. The island carve-out
               above already stops a flow section from establishing its own
               column-count:2, but a flow section that ALSO still carries
               data-section-columns="2" (or a future selector path) must not
               re-open a nested multicol inside the page-flow box. Forcing
               column-count:1 here guarantees the section is a plain block child
               of the ONE [data-page-flow-box] multicol (no 4-column squash),
               belt-and-suspenders with the carve-out. */
            column-count: 1;
            -webkit-column-count: 1;
          }
          /* ROUND-3 DEFECT-A — the running PROSE inside a flow section flows
             across the column break. A flow section's own paragraphs / lists
             must be splittable so column 1 fills then prose continues in column
             2. (Discrete cards / callouts / figures / tables keep their atomic
             break-inside: avoid via the rules below.) */
          [data-report-paged][data-page-flow="2"] [data-section-flow="flow"] p,
          [data-report-paged] [data-page-flow="2"] [data-section-flow="flow"] p,
          [data-report-paged][data-page-flow="2"] [data-section-flow="flow"] ul,
          [data-report-paged] [data-page-flow="2"] [data-section-flow="flow"] ul,
          [data-report-paged][data-page-flow="2"] [data-section-flow="flow"] ol,
          [data-report-paged] [data-page-flow="2"] [data-section-flow="flow"] ol {
            break-inside: auto;
            -webkit-column-break-inside: auto;
            page-break-inside: auto;
          }
          [data-report-paged][data-page-flow="2"] [data-section-flow="flow"] > h2[data-heading="true"],
          [data-report-paged] [data-page-flow="2"] [data-section-flow="flow"] > h2[data-heading="true"] {
            column-span: none;
            -webkit-column-span: none;
            break-after: avoid;
            -webkit-column-break-after: avoid;
            page-break-after: avoid;
          }
          /* SPAN sections (wide figure / chart / card) span BOTH page columns.
             The existing wide selectors (table / figure / [data-figure] /
             [data-span-columns] / .recharts-wrapper) inside ANY flow section
             also span, so a wide table inside a "flow" prose section still
             breaks out across both columns rather than squashing. */
          [data-report-paged][data-page-flow="2"] [data-section-flow="span"],
          [data-report-paged] [data-page-flow="2"] [data-section-flow="span"],
          ${spanColumnsSelectors(
                  '[data-report-paged][data-page-flow="2"] [data-page-flow-box]',
                )} {
            column-span: all;
            -webkit-column-span: all;
            break-inside: avoid;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
          }
          /* B1 — the Table of Contents + any full-width banner span BOTH page
             columns inside the flow box. The TOC is a wide two-level list that
             reads poorly squashed into one column; full-width banners
             (phased-summary, alert) are page-wide chrome. They join the
             span:all group above so they break out of the multicol flow. */
          [data-report-paged][data-page-flow="2"] [data-page-flow-box] [data-section="toc"],
          [data-report-paged][data-page-flow="2"] [data-page-flow-box] #toc,
          [data-report-paged][data-page-flow="2"] [data-page-flow-box] [data-report-banner],
          [data-report-paged][data-page-flow="2"] [data-page-flow-box] [data-full-width-banner] {
            column-span: all;
            -webkit-column-span: all;
            break-inside: avoid;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
          }
          /* B1 — cards / callouts must not split across a column OR page break
             inside the continuous flow. They read as discrete units (finding
             cards, KPI tiles, assessment blocks, callouts), so a half-card at a
             column foot is a defect. Extends the [data-flow-card] island
             protection to the page-flow box via the same hooks the rest of the
             report already stamps. */
          [data-report-paged][data-page-flow="2"] [data-page-flow-box] [data-flow-card],
          [data-report-paged][data-page-flow="2"] [data-page-flow-box] [role="note"],
          [data-report-paged][data-page-flow="2"] [data-page-flow-box] [data-report-card],
          [data-report-paged][data-page-flow="2"] [data-page-flow-box] [data-callout] {
            break-inside: avoid;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
          }
        `}</style>
                {/* TSE-2 C7 — Watermark overlay, driven by the SINGLE contract
             (`presentation.watermark`, already normalised) so the on-screen
             overlay is byte-identical to the DOCX/PPTX watermark. Renders
             only when enabled. Diagonal -45° text behind report content;
             pointer-events: none so it doesn't intercept clicks; absolute
             positioning so it follows the scroll across the full report
             height (the report root is the scroll parent). */}
                {resolvedWatermark.enabled && (
                  <div
                    aria-hidden="true"
                    data-testid="report-watermark"
                    data-template-watermark
                    className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center overflow-hidden"
                    style={{
                      opacity: resolvedWatermark.opacity,
                    }}
                  >
                    <span
                      style={{
                        color: resolvedWatermark.color,
                        fontSize: 'clamp(80px, 12vw, 180px)',
                        fontWeight: 900,
                        transform: 'rotate(-45deg)',
                        whiteSpace: 'nowrap',
                        userSelect: 'none',
                      }}
                    >
                      {resolvedWatermark.text}
                    </span>
                  </div>
                )}
                {/* Page 1 — TCP-5 (2026-05-18) wraps the cover in an outer
             `cover-preview-frame` so the Studio's screen-mode preview
             can enforce A4 portrait aspect ratio (1 / √2 ≈ 0.707)
             without leaking the constraint into the production PDF/DOCX
             render paths. The actual aspect-ratio styling is scoped to
             `.studio-preview [data-testid="cover-preview-frame"]` via
             the inline stylesheet in TemplatePreviewPane.tsx — outside
             the studio, the wrapper is a transparent passthrough so
             existing reports / exports render unchanged. */}
                <div
                  id="cover"
                  data-page="1"
                  data-section="cover"
                  data-testid="section-page-1"
                  data-cover-layout={coverLayout}
                  className={`${coverPageShellPadding} relative border-b border-border-subtle`}
                  style={immutableArtifactMode ? { order: -2 } : undefined}
                >
                  {showConfidentialSidebar && (
                    <ConfidentialSidebar theme={theme} />
                  )}
                  <div data-testid="cover-preview-frame" data-cover-preview-frame="">
                    <div data-testid="cover-preview-inner" className={`relative${reportContentPadding ? ` ${reportContentPadding}` : ''}`}>
                      {(() => {
                        const coverDisclosure = reportDemonstrationDisclosure(report);
                        if (!coverDisclosure) return null;
                        return (
                          <div
                            data-testid="cover-demonstration-notice"
                            className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded border border-amber-300/80 bg-amber-50/90 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-900"
                          >
                            <span>{coverDisclosure.notice}</span>
                            <span>Demonstration scenario ({coverDisclosure.scenarioId})</span>
                          </div>
                        );
                      })()}

                      {/* William QA 2026-06-28 — the DAM lineage chip ("DAM v2") is
               REMOVED from the report cover for ALL report types. It was an
               admin-only on-screen affordance (print:hidden) that still leaked
               into the report header for admin reviewers. The pinned version
               data still lives on the report doc (`report.damVersionUsed` /
               `sedVersionUsed`) + the admin metadata panel; it just no longer
               renders on the report itself. */}

                      {renderCover({
                        layout: coverLayout,
                        report: isEditing ? editedReport : report,
                        // SINGLE THEME SEAM — the cover MUST paint the SAME merged theme the
                        // body reads (`presentation.theme`, which folds the template preset
                        // via resolveReportPresentation → resolveEffectiveTheme). Passing the
                        // raw `theme` here risked the hero/cover gradient + cover colours
                        // diverging from the body whenever the presentation seam re-resolves
                        // a field. `presentation.theme` is the canonical, fully-merged theme.
                        //
                        // DARK READER: `presentation.theme` comes from
                        // `resolveReportPresentation` -> `resolveEffectiveTheme`, which does NOT
                        // pass through `attachReaderMode`. The reader mode rides on the theme
                        // under a Symbol key, so an unstamped theme makes every `roleColor` in
                        // the cover subtree correctly resolve LIGHT — which is why the cover
                        // title rendered #19191a (light ink) on the #0b0f14 dark page at 1.09:1
                        // contrast while the body headings, reading the stamped `theme`, were
                        // correct. Re-stamping here keeps the single-theme-seam invariant above
                        // (same merged theme) while restoring mode-awareness. `attachReaderMode`
                        // is a shallow copy and 'light' stamps are inert, so light mode and every
                        // export (audienceMode 'export' pins 'light') are byte-identical.
                        theme: attachReaderMode(presentation.theme, reportReaderMode),
                        branding: coverBranding,
                        isAdmin,
                        pickerOpen: readerEditing,
                        onTogglePicker: () => setReaderEditing((v) => !v),
                        onSelectArchetype: handleCoverArchetypeSelect,
                        disclaimer: resolvedDisclaimer,
                        isEditing,
                        editedAddress: editedReport.address,
                        onAddressChange: (address) => setEditedReport({ ...editedReport, address }),
                        template: activeTemplate ?? null,
                        // DEFECT 8 — thread the computed TOC entries + page-map
                        // + format into the cover so the masthead renders its
                        // compact 2-column numbered index below the abstract;
                        // the standalone TOC section is suppressed below
                        // (coverCarriesTocActive). Non-masthead covers ignore.
                        tocEntries: coverTocEntries,
                        tocPageNumbers,
                        tocPageNumberFormat: presentation.pageNumberFormat,
                        boundedStandfirst: immutableArtifactMode,
                      })}
                    </div>{/* cover-preview-inner — cover-only surface for Studio A4 scaler */}
                  </div>{/* cover-preview-frame — A4 aspect + overflow scoped to cover art only */}
                </div>{/* #cover — cover art only; page-1 body sections are a sibling below */}

                {/* B1 — CONTINUOUS page-level two-column FLOW box. When pageFlowEngaged
             (two-column academic preset in the paged deck) ALL post-cover body
             sections become ordinary block children of ONE [data-page-flow-box]
             multicol container (column-fill: auto) instead of per-section
             islands. When not engaged this is a transparent Fragment, so
             single-column / continuous reports are byte-identical. The cover
             above stays OUTSIDE the box (single full-bleed page). */}
                <PageFlowBox engaged={pageFlowEngaged}>

                  {/* TCP-3 (2026-06-05) — Executive Letter, INLINE placement. For
             NON-full-page academic title covers (classic / boundary-plate /
             data-snapshot / masthead / minimal) the letter renders directly
             BENEATH the cover on page 1 (no page break before it). Hero-image
             (full-page) covers instead render the letter as its own page 2
             further below (see the `heroCoverLayout` mount). Same testids in
             both modes; the executive summary is forced onto a fresh page
             whenever this inline placement is active. */}
                  {coverOverlayBelowCover && activeTemplate?.coverOverlay && (
                    <ExecutiveLetterPage
                      overlay={activeTemplate.coverOverlay}
                      theme={theme}
                      omitSectionSidebar={omitSectionSidebar}
                      belowCover
                    />
                  )}

                  {/* TCP-A (2026-05-18) — the page-1 body content (verdict chip,
               executive summary, TOC, key findings, BTM tech overview,
               siting notes, climate) must render REGARDLESS of cover
               variant. Pre-TCP this whole flex-column was gated on
               `coverLayout === 'classic'`, which silently dropped
               sections 1-5 in the Studio preview the moment the user
               picked any non-classic variant — making the preview
               jump from cover to "§ 6 Grid Power Study" on page 2.
               Now the wrapper is unconditional; cover variants paint
               their own headers via `renderCover()` above. Page-1 body
               sits OUTSIDE cover-preview-frame so Studio overflow
               clipping does not truncate sections below the hero. */}
                  {/* TSP-2 — flex column (or `display:contents` when flattening)
             so CSS `order: N` from `sectionStyle()` applies at the
             report root alongside page-2 sections. */}
                  <div data-testid="section-page-1-body" data-paged-flow-shell="true" className={page1BodyShellClass}>
                    {showConfidentialSidebar && !sectionOrderFlatten && (
                      <ConfidentialSidebar theme={theme} />
                    )}
                    {/* RRQ — `data-paged-flow-shell` marker so the paged deck stylesheet
              (buildPagedStylesheet) can force this `display:contents` inner shell
              to `display:block`. pagedjs 0.4.3's chunker cannot reliably fragment
              content nested under a boxless `display:contents` wrapper across page
              boundaries — it silently DROPS the `[data-section]` wrappers (or
              strips them while keeping loose text). Restoring a real box on every
              shell down to the section level fixes the section-drop. The marker is
              inert on the on-screen continuous render (no rule matches it there)
              and absent in non-flattened mode → byte-stable. */}
                    <div data-paged-flow-shell="true" className={page1InnerShellClass}>

                      {/* Run preferences are no longer surfaced in-report — the
                "These preferences shaped the AI's analysis" block was
                noise to readers. typedPreferences still persist on the
                report (wizard captures them, AI prompt consumes them);
                they're just hidden from the rendered output. Re-enable
                by re-mounting <RunPreferencesPanel report={report} />. */}

                      {/* V2 front matter is emitted before body content in the
                canonical DOM order. This is intentionally a real structural
                move, not CSS ordering, so pagination, TOC destinations,
                accessibility order, PDF labels, and projections agree. */}
                      {immutableArtifactMode && tableOfContents}

                      {/* Executive Summary narrative — flex sibling so
                `sectionStyle('executiveSummary')` reorder applies.
                PSR uses the `executive` section via SectionsRouter instead.
                EXEC-SUMMARY DE-DUP (decisions 4/5) — suppressed when the
                cover carries the exec-summary-equivalent (hero exec summary /
                masthead·classic abstract); the TOC entry is dropped to
                match (see deriveTocEntries call). */}
                      {report.reportType !== 'public-sentiment' && !coverHasExecSummaryContent && (
                        <EditorialSectionShell
                          id="executiveSummary"
                          className="space-y-8"
                          data-section="executiveSummary"
                          data-page-numbering={immutableArtifactMode ? 'main' : undefined}
                          {...sectionPageColumnProps('executiveSummary')}
                          {...sectionNumberProps('executiveSummary')}
                          style={sectionStyle('executiveSummary')}
                          shellPadding={inlineSectionShellPadding}
                          omitSectionSidebar={omitSectionSidebar}
                          theme={theme}
                          // TCP-3 — when the executive letter sits inline beneath a
                          // non-full-page cover (above, on page 1), the executive
                          // summary (first body section) is forced onto a FRESH page
                          // via the same `print:break-before-page` utility the export
                          // (puppeteer/print) + studio-preview pipelines key off, so the
                          // body never crowds the letter. Composed with the existing
                          // inline chrome so neither placement loses its padding rule.
                          extraClassName={[
                            inlineSectionShellPadding ? inlineSectionChrome : undefined,
                            coverOverlayBelowCover ? 'print:break-before-page' : undefined,
                          ].filter(Boolean).join(' ') || undefined}
                        >
                          <div className="space-y-4">
                            {/* P2.4 / heading-scale — routed through `headingChrome`, which
                       derives size/weight/rule/spacing from `theme.headingScale`
                       (editorial / technical / compact). Color + font still come
                       from the theme. A `technical`-scale theme (and the legacy
                       DEFAULT_THEME path) resolves to the prior
                       `text-xl font-bold border-b-2 pb-2` literal, so legacy
                       (no-preset) reports are byte-identical. */}
                            <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true"><EditableHeading headingKey="executiveSummary" defaultText={sectionDefaultHeading('executiveSummary', 'Executive summary')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} /></h2>
                            {(() => {
                              const upfrontAudit = openAuditFindings(report);
                              if (upfrontAudit.flag) {
                                return (
                                  <div
                                    data-upfront-audit-banner="true"
                                    style={{
                                      border: '1px solid #d97706',
                                      borderLeft: '4px solid #b45309',
                                      backgroundColor: '#fffbeb',
                                      padding: '10px 14px',
                                      marginBottom: '12px',
                                    }}
                                  >
                                    <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 700, color: '#92400e', lineHeight: 1.4 }}>
                                      {`${report.qualityHold === 'needs-revision' && upfrontAudit.kind !== 'flagged' ? 'AUTHORITATIVE QUALITY HOLD — NEEDS REVISION · ' : ''}AUDIT RECOMMENDED — ${openAuditBannerSummary(upfrontAudit, 'document')}. ${openAuditDisclosureSentence(upfrontAudit)}`}
                                    </p>
                                    {upfrontAudit.findings.slice(0, 6).map((f, idx) => (
                                      <p key={idx} style={{ margin: '2px 0 0', fontSize: '11.5px', color: '#78350f', lineHeight: 1.4 }}>
                                        {`• ${f}`}
                                      </p>
                                    ))}
                                  </div>
                                );
                              }
                              if (report.reportType === 'feasibility') {
                                return (
                                  <div
                                    data-upfront-feasibility-notice="true"
                                    style={{
                                      border: '1px solid #cbd5e1',
                                      borderLeft: '4px solid #0284c7',
                                      backgroundColor: '#f8fafc',
                                      padding: '10px 14px',
                                      marginBottom: '12px',
                                    }}
                                  >
                                    <p style={{ margin: 0, fontSize: '11.5px', color: '#1e293b', lineHeight: 1.45 }}>
                                      <strong>FEASIBILITY-STAGE ISSUANCE NOTICE — </strong>
                                      This document is a feasibility-stage screening report. Clearly labelled planning assumptions, indicative engineering estimates, and identified site data gaps do not block feasibility issuance; they are documented in Assumptions &amp; Limitations, What This Report Does Not Know, and Next Steps as conditions for subsequent investment, utility interconnection (SPP/utility DISIS/HILL), and detailed engineering design approval.
                                    </p>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                            {/* SNT-3 — executive-summary quarantine banner (no-op on
                       clean reports). */}
                            <SectionQuarantineBanner
                              sectionId="executiveSummary"
                              sectionLabel="Executive Summary"
                              guardStatus={report.sectionGuardStatus?.['executiveSummary']}
                              quarantinedOriginal={report.quarantinedOriginal?.['executiveSummary']}
                              renderedText={report.executiveSummary}
                              onAuthor={() => setIsEditing(true)}
                            />
                            {/* MEL-MR-2 — admin-only meta-instruction-leak chip.
                       Renders nothing on clean reports / non-admin readers.
                       Composes with the SectionQuarantineBanner above. */}
                            <MetaLeakChip
                              report={report}
                              sectionId="executiveSummary"
                              sectionLabel="Executive Summary"
                              isAdmin={isAdmin}
                              onRegenerate={handleSectionRegen}
                            />
                            {isEditing ? (
                              <div className="relative">
                                <textarea
                                  className="w-full h-48 p-2 text-sm leading-relaxed text-text-primary border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary"
                                  value={editedReport.executiveSummary || editedReport.summary}
                                  onChange={(e) => setEditedReport({ ...editedReport, executiveSummary: e.target.value })}
                                  maxLength={3000}
                                />
                                <span className="absolute bottom-1 right-2 text-xs text-text-secondary">
                                  {(editedReport.executiveSummary || editedReport.summary || '').length}/3000
                                </span>
                              </div>
                            ) : report.reportType === 'custom-brief' && report.customBriefExecutiveSummary ? (
                              <div className="space-y-3">
                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={handleEditCustomBriefQuestions}
                                    className="h-7 px-3 bg-surface-2 border border-border text-text-primary font-mono type-section hover:bg-surface-3 hover:border-border-strong transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                                    data-testid="custom-brief-edit-questions"
                                  >
                                    Edit Questions
                                  </button>
                                </div>
                                <CustomBriefExecutiveSummaryBody
                                  report={report}
                                  summary={report.customBriefExecutiveSummary}
                                />
                              </div>
                            ) : displayExecutiveSummary ? (
                              <div className="space-y-3">
                                {/* SECTION-PORT (D1, "Verdict-led") — the locked Executive
                           Summary layout leads with the verdict + a KPI rail,
                           then the narrative. Both are theme-token-driven
                           editorial primitives that render across all presets
                           AND in viewMode='export'. PRESERVES the existing prose
                           wiring below (splitNarrativeParagraphs + walkClaimsText)
                           — the verdict/rail are composed ABOVE it, not in place
                           of it. Each piece is gated so it shows only when real
                           data exists (no fabrication): the verdict mark only when
                           the report carries a banded tier (verdict.tier !==
                           'none'); the rail only when ≥1 KPI resolved a real value
                           via the canonical cover-chip chain. */}
                                {(() => {
                                  // K6 — lead the Executive Summary with the signature hero
                                  // numeral (the redesign's dominant-figure motif), re-skinned
                                  // to OUR tokens. The hero is the type's primary metric pulled
                                  // from the SAME canonical cover-chip chain the rail uses (no
                                  // fabrication); when promoted it is dropped from the rail so
                                  // the figure is not duplicated. Falls back cleanly to the
                                  // rail-only layout when no clean single numeral resolves.
                                  const hero = resolveExecutiveSummaryHero(report);
                                  const railItems = resolveExecutiveSummaryRail(report, hero?.fromChipId);
                                  const showVerdict = verdict.tier !== 'none' && verdict.label && verdict.label !== '—';
                                  if (!showVerdict && !hero && railItems.length === 0) return null;
                                  // N2 RESIDUAL (a) — flow the ACTUAL verdict INTO the KEY FIGURES
                                  // hero region as the lead hero metric ("Overall verdict — Conditional"),
                                  // toned to the verdict tier, instead of a separate small "Assessment"
                                  // mark line. The numeric primary metric (Power / LCOE …) follows as a
                                  // second hero so the verdict reads as the dominant figure up top.
                                  const verdictTone =
                                    verdict.tier === 'green' ? 'positive'
                                    : verdict.tier === 'amber' ? 'warning'
                                    : verdict.tier === 'red' ? 'negative'
                                    : 'default';
                                  return (
                                    <div className="space-y-3" data-exec-verdict-rail="true">
                                      {showVerdict && (
                                        <div data-exec-verdict="true">
                                          <EditorialHeroMetric
                                            theme={theme}
                                            label="Overall verdict"
                                            value={verdict.label}
                                            tone={verdictTone}
                                            toneLabel={false}
                                            size="lg"
                                          />
                                        </div>
                                      )}
                                      {hero && (
                                        <div data-exec-hero="true">
                                          <EditorialHeroMetric
                                            theme={theme}
                                            label={hero.label}
                                            value={hero.value}
                                            unit={hero.unit}
                                            size={showVerdict ? 'md' : 'lg'}
                                          />
                                        </div>
                                      )}
                                      {railItems.length > 0 && (
                                        <EditorialKpiRail theme={theme} items={railItems} />
                                      )}
                                    </div>
                                  );
                                })()}
                                {/* WAVE 3 — prose-led monograph for the executive summary. When a
                                    GENERATED editorial layer exists, the flowing EditorialProseBody
                                    re-voices the summary (drop-cap + pull-quote) and the structured
                                    paragraph split is SUPPRESSED so the prose is not doubled. No
                                    layer ⇒ byte-identical structured paragraphs (parity fixtures
                                    carry no editorialNarrative). */}
                                {hasGeneratedEditorialLayer(report, 'executiveSummary') ? (
                                  <EditorialProseBody report={report} sectionId="executiveSummary" theme={theme} />
                                ) : (
                                  splitNarrativeParagraphs(displayExecutiveSummary).map((paragraph, i) => (
                                    <p key={i} className="text-sm leading-relaxed text-text-primary">
                                      {walkClaimsText(paragraph, report.references, report.claims, appendixVisibleRefIndices)}
                                    </p>
                                  ))
                                )}
                              </div>
                            ) : (
                              <InsufficientData
                                section="Executive Summary"
                                onEdit={() => setIsEditing(true)}
                                reportId={report.id}
                                sectionId="executiveSummary"
                                autoFill={autoFillFor('executiveSummary')}
                              />
                            )}
                          </div>
                        </EditorialSectionShell>
                      )}

                      {/* Legacy revisions keep their historical Contents
                 placement; V2 mounted the same node before the body above. */}
                      {!immutableArtifactMode && tableOfContents}

                      {isVisible('projectIntentOverview') && report.reportType !== 'custom-brief' && (
                        <SectionErrorBoundary sectionId="projectIntentOverview" label="Project Intent & Site Overview">
                          <ProjectIntentOverviewSection
                            report={report}
                            theme={theme}
                            reportSite={reportSite}
                            reportSiteLayers={reportSiteLayers}
                            propertyMapSurfaceMode={propertyMapSurfaceMode}
                            onEditLayers={() => setLayerEditorOpen(true)}
                            isAdmin={isAdmin}
                            onSectionRegen={handleSectionRegen}
                            isEditing={isEditing}
                            headingOverrides={headingOverrides}
                            onHeadingChange={setHeading}
                            sectionDefaultHeading={sectionDefaultHeading}
                            sectionStyle={sectionStyle('projectIntentOverview')}
                            sectionPageColumnProps={sectionPageColumnProps('projectIntentOverview')}
                            shellPadding={inlineSectionShellPadding}
                            omitSectionSidebar={omitSectionSidebar}
                            extraClassName={inlineSectionShellPadding ? inlineSectionChrome : undefined}
                            exportMode={audienceMode === 'export'}
                            presetId={presentation.presetId}
                            editorialActive={editorialActive}
                            editorialSectionNumber={sectionDisplayNumbers?.get('projectIntentOverview')}
                            actionPlanAppendix={{
                              visible: isVisible('appendix'),
                              heading: headingOverrides.appendix || 'Appendix & References',
                            }}
                          />
                        </SectionErrorBoundary>
                      )}

                      {/* Report-type-specific sections — legacy aggregate blocks. Only render
                    when the report does NOT use the new per-type section IDs. Reports
                    generated after Workflow Prompt 1.5 have sectionOrder populated with
                    the new IDs and route through NewTypeSectionsRouter below.
                    RTU-1 (2026-05-19): environmental now also has a modern per-section
                    registry; `EnvironmentalSections.tsx` becomes the fallback for
                    archived reports lacking the `nepaScreening` sentinel. */}
                      {report.reportType === 'environmental' && !hasModernSections(report) && <EnvironmentalSections report={report} theme={theme} presetActive={presentation.presetId != null} pageColumns={themeBodyColumns} shellPadding={inlineSectionShellPadding} omitSectionSidebar={omitSectionSidebar} />}
                      {report.reportType === 'power-availability' && !hasModernSections(report) && <PowerSections report={report} theme={theme} presetActive={presentation.presetId != null} pageColumns={themeBodyColumns} shellPadding={inlineSectionShellPadding} omitSectionSidebar={omitSectionSidebar} />}
                      {report.reportType === 'water-resource' && !hasModernSections(report) && <WaterSections report={report} theme={theme} presetActive={presentation.presetId != null} pageColumns={themeBodyColumns} shellPadding={inlineSectionShellPadding} omitSectionSidebar={omitSectionSidebar} />}
                      {report.reportType === 'test-fit' && !hasModernSections(report) && <TestFitSections report={report} theme={theme} presetActive={presentation.presetId != null} pageColumns={themeBodyColumns} shellPadding={inlineSectionShellPadding} omitSectionSidebar={omitSectionSidebar} />}
                      {report.reportType === 'interconnection' && !hasModernSections(report) && <InterconnectionSections report={report} theme={theme} presetActive={presentation.presetId != null} pageColumns={themeBodyColumns} shellPadding={inlineSectionShellPadding} omitSectionSidebar={omitSectionSidebar} />}
                      {/* New per-type section components — driven by sectionOrder (CSS order) */}
                      <SectionErrorBoundary sectionId="newTypeSections" label="Report sections">
                        <NewTypeSectionsRouter
                          report={report}
                          sectionStyle={sectionStyle}
                          sectionPageColumnCount={sectionPageColumnCount}
                          sectionFlowMode={sectionFlowMode}
                          sectionNumberProps={sectionNumberProps}
                          site={reportSite}
                          sed={liveSiteSed}
                          theme={theme}
                          omitSectionSidebar={omitSectionSidebar}
                          shellPadding={inlineSectionShellPadding}
                          sectionChrome={inlineSectionChrome}
                          isEditing={isEditing}
                          headingOverrides={headingOverrides}
                          onHeadingChange={setHeading}
                          sectionDefaultHeading={sectionDefaultHeading}
                          isAdmin={isAdmin}
                          onSectionRegen={handleSectionRegen}
                          suppressExecutiveSection={coverHasExecSummaryContent}
                          editorialActive={editorialActive}
                        />
                      </SectionErrorBoundary>

                      {/* William QA BUG #15 — the full-bleed Part-divider PAGE was
                          removed (it rendered with non-contiguous §NN numbering and
                          out of place). The reportParts lib helpers remain in place;
                          we simply no longer render the divider sheet. */}
                      {/* Feasibility-specific sections — hidden for environmental reports */}
                      {isVisible('btmOverview') && (
                        <SectionErrorBoundary sectionId="btmOverview" label="Behind-the-Meter Technology">
                          <EditorialSectionShell
                            id="btmOverview"
                            data-section="btmOverview"
                            data-section-id="btmOverview"
                            {...sectionPageColumnProps('btmOverview')}
                            {...sectionNumberProps('btmOverview')}
                            style={sectionStyle('btmOverview')}
                            shellPadding={inlineSectionShellPadding}
                            omitSectionSidebar={omitSectionSidebar}
                            theme={theme}
                            // `space-y-4` moves into extraClassName so the spread's
                            // `className` (report-section-page-cols-2 in two-column
                            // mode) is preserved — a later explicit `className` JSX
                            // prop would otherwise override the spread.
                            extraClassName={[
                              inlineSectionShellPadding ? inlineSectionChrome : undefined,
                              'space-y-4',
                            ].filter(Boolean).join(' ')}
                            editorialActive={editorialActive}
                            sectionNumber={sectionDisplayNumbers?.get('btmOverview')}
                            heading={<EditableHeading headingKey="btmOverview" defaultText={sectionDefaultHeading('btmOverview', 'Behind-the-Meter Technology')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />}
                            standfirst={hasGeneratedEditorialLayer(report, 'btmOverview') ? walkClaimsText(resolveEditorialLayer(report, 'btmOverview').editorialThesis ?? '', report.references, report.claims, appendixVisibleRefIndices) : undefined}
                            standfirstIsThesis={hasGeneratedEditorialLayer(report, 'btmOverview')}
                          >
                            {/* Classic mode (flag OFF) — caller-owned head (byte-identical
                                to the retired ClassicSectionShell path). Editorial mode:
                                the shell owns the opener, so this is suppressed. */}
                            {!editorialActive && (
                              <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true"><EditableHeading headingKey="btmOverview" defaultText={sectionDefaultHeading('btmOverview', 'Behind-the-Meter Technology')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} /></h2>
                            )}
                            {/* WAVE 3 — flowing editorial prose body (drop-cap + pull-quote)
                                when a generated layer is present; the per-option Assessment /
                                Recommended Action grid is kept as the section exhibit. No
                                layer ⇒ byte-identical. */}
                            {hasGeneratedEditorialLayer(report, 'btmOverview') && (
                              <EditorialProseBody report={report} sectionId="btmOverview" theme={theme} />
                            )}

                            {!isEditing && (!report.btmOptions || report.btmOptions.length === 0) ? (
                              <InsufficientData
                                section="BTM Technology Options"
                                onEdit={() => setIsEditing(true)}
                                reportId={report.id}
                                sectionId="btmOverview"
                                autoFill={autoFillFor('btmOverview')}
                              />
                            ) : (
                              <>
                                {/* SECTION-PORT (5B, "strategy + suitability matrix") — the
                        locked BTM capability table. The raw text-utility/font-mono
                        comparison <table> is replaced by EditorialDataTable
                        (theme-token-driven, var(--report-body-font), export-safe);
                        the LCOE column is `numeric` so its old font-mono leak is
                        gone. The Risk column keeps its EditorialBadge tone signal.
                        GAP: the per-criterion fit-mark matrix (strong/moderate/weak
                        per criterion × technology) is NOT a generated field — we do
                        NOT fabricate marks; instead we render the 5A CAPABILITY
                        table from REAL fields (technology / riskLevel) + the
                        existing per-tech derivations (corrected LCOE, tech-keyed
                        timeline/CO₂ heuristics, centralised land footprint). */}
                                {!isEditing && (
                                  // `data-span-columns` so the wide capability table spans BOTH
                                  // flow columns in two-column mode (no-op in single-column).
                                  <div data-span-columns>
                                    {(() => {
                                      const siteAcres = typeof report.siteArea === 'number' && Number.isFinite(report.siteArea) && report.siteArea > 0 ? report.siteArea : null;
                                      const landBasis = resolveBtmLandBasis(report);

                                      // Shared with DOCX: reported values keep their basis; missing
                                      // project costs never become geography-adjusted reference ranges.
                                      const lcoeCell = (techLabel: string): string => deriveBtmLcoeCell(techLabel, report);

                                      // Share frozen-demand/nameplate semantics with the Word export.
                                      const landCell = (label: string): React.ReactNode => {
                                        const land = deriveBtmLandCell(label, report);
                                        if (land.acres == null || land.nameplateMW == null) return land.text;
                                        return (
                                          <span>
                                            <LandRequirementCell
                                              acres={Math.ceil(land.acres)}
                                              exceedsSiteArea={siteAcres != null && land.acres > siteAcres}
                                            />
                                            <span className="block text-[9px]">{Number(land.nameplateMW.toFixed(2))} MW nameplate</span>
                                          </span>
                                        );
                                      };

                                      const getTechData = (tech: string) => {
                                        const t = tech.toLowerCase();
                                        if (t.includes('nuclear') || t.includes('smr')) {
                                          return { lcoe: lcoeCell(tech), timeline: '5-7 yr', land: landCell(tech), co2: 'Zero' };
                                        }
                                        if (t.includes('gas')) {
                                          return { lcoe: lcoeCell(tech), timeline: '~18–36+ mo (lateral & permits)', land: landCell(tech), co2: '350-500 kg' };
                                        }
                                        if (t.includes('solar')) {
                                          return { lcoe: lcoeCell(tech), timeline: '1-2 yr', land: landCell(tech), co2: 'Zero' };
                                        }
                                        if (t.includes('wind')) {
                                          return { lcoe: lcoeCell(tech), timeline: '1-3 yr', land: landCell(tech), co2: 'Zero' };
                                        }
                                        return { lcoe: lcoeCell(tech), timeline: '–', land: '–', co2: '–' };
                                      };

                                      const reportBtmOptions = report.btmOptions ?? [];
                                      const tableRows = reportBtmOptions.map((opt) => {
                                        const td = getTechData(opt.technology);
                                        return [
                                          opt.technology,
                                          <EditorialBadge
                                            key="risk"
                                            theme={theme}
                                            variant="outline"
                                            tone={opt.riskLevel === 'High' ? 'negative' : opt.riskLevel === 'Medium' ? 'warning' : 'positive'}
                                          >
                                            {opt.riskLevel === 'High' ? 'Red' : opt.riskLevel === 'Medium' ? 'Amber' : 'Green'}
                                          </EditorialBadge>,
                                          td.lcoe,
                                          td.timeline,
                                          td.land,
                                          td.co2,
                                        ];
                                      });
                                      // Redhead review wave 3 (defect 8) — drop any column whose EVERY
                                      // cell is an empty dash (e.g. LCOE all "–" when the project-
                                      // specific LCOE is pending for this report) instead of shipping a
                                      // dash-only column; % widths are rescaled to keep summing to 100.
                                      const techTable = dropAllEmptyColumns(
                                        [
                                          // J5 — explicit column widths + a no-wrap
                                          // "Technology" header so the fixed-layout
                                          // table no longer breaks the short label
                                          // mid-word ("TECHNOLO/GY") at the narrow
                                          // print column. Widths sum to 100%.
                                          { header: 'Technology', width: '28%', headerNoWrap: true },
                                          { header: 'Risk', align: 'center' as const, width: '10%' },
                                          // Cost cells include their evidence basis. Let that disclosure
                                          // wrap inside its column instead of covering adjacent values.
                                          { header: 'LCOE', width: '22%' },
                                          // William QA 2026-06-29 — nowrap so "TIMELINE" / "LAND" /
                                          // "CO2" headers never snap a trailing character to a 2nd row
                                          // ("TIMELIN"+"E") at the tight uppercase+tracked column.
                                          { header: 'Timeline', width: '12%', headerNoWrap: true },
                                          { header: 'Land (acres)', width: '16%', headerNoWrap: true },
                                          { header: <CarbonDioxideFormula />, width: '12%', headerNoWrap: true },
                                        ],
                                        tableRows,
                                      );
                                      const keptPctSum = techTable.columns
                                        .reduce((sum, c) => sum + (parseFloat(c.width) || 0), 0);
                                      const rescaledColumns = keptPctSum > 0 && keptPctSum < 100
                                        ? techTable.columns.map((c) => ({
                                            ...c,
                                            width: `${Math.round(((parseFloat(c.width) || 0) / keptPctSum) * 1000) / 10}%`,
                                          }))
                                        : techTable.columns;
                                      return (
                                        <>
                                          <EditorialDataTable
                                            theme={theme}
                                            columns={rescaledColumns}
                                            rows={techTable.rows}
                                          />
                                          <p className="mt-2 text-[10px]" data-testid="btm-land-basis">{landBasis.description}</p>
                                        </>
                                      );
                                    })()}
                                    <LcoeProvenanceFootnote report={report} />
                                  </div>
                                )}

                                {/* Per-tech entries. Theme-adaptive sub-block (sectionBlockChrome):
                        editorial → hairline-separated entry with the GREEN/AMBER/RED
                        badge FLOATING beside the tech name (no boxy card); technical →
                        thin left accent rule; compact → tight top hairline. The
                        ASSESSMENT / RECOMMENDED ACTION pair is a labelled definition
                        flow, not a boxed grid. The old `border` flow-card box is gone. */}
                                <div className="mt-4" style={{ display: 'flex', flexDirection: 'column', gap: blockChrome.scale === 'compact' ? 12 : 18 }}>
                                  {(isEditing ? editedReport.btmOptions || [] : report.btmOptions || []).map((opt, idx) => (
                                    <div key={idx} data-flow-card style={blockChrome.entryStyle}>
                                      <div style={blockChrome.headerRowStyle}>
                                        <div className="flex items-center gap-3">
                                          {isEditing ? (
                                            <input className="text-sm font-bold text-text-primary bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary w-48"
                                              value={opt.technology} onChange={(e) => { const o = [...(editedReport.btmOptions || [])]; o[idx] = { ...o[idx], technology: e.target.value }; setEditedReport({ ...editedReport, btmOptions: o }); }} />
                                          ) : (
                                            <h4 style={blockChrome.labelStyle}>{opt.technology}</h4>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {isEditing ? (
                                            <>
                                              <select className="bg-transparent border border-primary/20 text-sm uppercase px-1 py-0.5" value={opt.riskLevel}
                                                onChange={(e) => { const o = [...(editedReport.btmOptions || [])]; o[idx] = { ...o[idx], riskLevel: e.target.value as any }; setEditedReport({ ...editedReport, btmOptions: o }); }}>
                                                <option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option>
                                              </select>
                                              <select className="bg-transparent border border-primary/20 text-sm uppercase px-1 py-0.5" value={opt.impactLevel}
                                                onChange={(e) => { const o = [...(editedReport.btmOptions || [])]; o[idx] = { ...o[idx], impactLevel: e.target.value as any }; setEditedReport({ ...editedReport, btmOptions: o }); }}>
                                                <option value="Red">Red</option><option value="Amber">Amber</option><option value="Green">Green</option>
                                              </select>
                                            </>
                                          ) : (
                                            <EditorialBadge theme={theme} variant="outline" tone={opt.riskLevel === 'High' ? 'negative' : opt.riskLevel === 'Medium' ? 'warning' : 'positive'}>{opt.riskLevel === 'High' ? 'Red' : opt.riskLevel === 'Medium' ? 'Amber' : 'Green'}</EditorialBadge>
                                          )}
                                        </div>
                                      </div>
                                      {isEditing ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                          <div><div style={blockChrome.defLabelStyle}>Reason</div><textarea className="w-full h-16 p-2 text-xs border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary" value={opt.reason} onChange={(e) => { const o = [...(editedReport.btmOptions || [])]; o[idx] = { ...o[idx], reason: e.target.value }; setEditedReport({ ...editedReport, btmOptions: o }); }} /></div>
                                          <div><div style={blockChrome.defLabelStyle}>Action</div><textarea className="w-full h-16 p-2 text-xs border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary" value={opt.action} onChange={(e) => { const o = [...(editedReport.btmOptions || [])]; o[idx] = { ...o[idx], action: e.target.value }; setEditedReport({ ...editedReport, btmOptions: o }); }} /></div>
                                        </div>
                                      ) : (opt.reason || opt.action) ? (
                                        // SECTION-PORT (5B prose) — the regulatory / site-specific
                                        // assessment + recommended-action prose renders through the
                                        // theme-adaptive EditorialSubsection (var(--report-body-font),
                                        // no raw text-utility classes). renderCitedText preserves
                                        // citation walking.
                                        // William QA BUG #3 — force the WCAG-clamped body ink
                                        // (roleColor 'body') when themed so the Assessment /
                                        // Recommended-Action prose reads high-contrast in BOTH
                                        // light + dark (mirrors ProjectIntentOverviewSection:324).
                                        // Without it the EditorialSubsection prose inherited a
                                        // muted slate colour and rendered faint/illegible.
                                        // William QA 2026-06-29 — the BTM per-tech Assessment /
                                        // Recommended-Action descriptions were rendered in a BARE
                                        // inline `display:grid` (no class, no span marker), which
                                        // pagedjs 0.4.3 DROPS during PDF fragmentation → the
                                        // descriptions vanished from the printed report. Use the
                                        // protected pattern: a `grid-cols` CLASS (so the deck grid-
                                        // flatten rule collapses it to one fragmentation-safe track)
                                        // + `data-span-columns` (column-span:all + break-inside:avoid)
                                        // so each tech's prose stays whole on one page instead of
                                        // being clipped/dropped.
                                        <div data-span-columns className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: blockChrome.defGroupGap + 8, ...(theme ? { color: roleColor(theme, 'body') } : {}) }}>
                                          {opt.reason && <EditorialSubsection theme={theme} label="Assessment">{renderCitedText(opt.reason)}</EditorialSubsection>}
                                          {opt.action && <EditorialSubsection theme={theme} label="Recommended Action">{renderCitedText(opt.action)}</EditorialSubsection>}
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </EditorialSectionShell>
                        </SectionErrorBoundary>
                      )}

                      {/* TCP-6 (2026-05-19, row 0.99233) — stray duplicate
              * climate-profile block removed. The block used to render
              * here as a top-level sibling of the section grid, which
              * surfaced as a SECOND "Monthly Climate Profile" rendering
              * in the Studio preview (the first comes from
              * `<ClimateProfileChart>` inside the climateSevereWeather
              * section above). The duplicate sat outside any
              * `data-section` wrapper so page-assignment + visibility
              * toggles couldn't reach it, and it surfaced as the orphan
              * "section #2" the user reported in the live-prod Template
              * Studio walkthrough. The canonical chart now lives at the
              * climate section (climateSevereWeather →
              * ClimateProfileChart) and is the single source of truth. */}

                      {/* Page-1 inline footer removed (Body Footer Dedup pack, BF1).
                 The footer text now renders exactly once, at the end of the
                 report, via the canonical <ReportFooter> at the foot of
                 page 3 (AG pack, AG3 — formerly a separate final page).
                 Per-page footers in exported files come from the export
                 pipelines (DOCX section `footers`, puppeteer
                 `footerTemplate` for PDF), not from this div. */}
                    </div>{/* page-1 inner */}
                  </div>{/* section-page-1-body */}

                  {/* TCP-2 (2026-05-18) — Executive Letter page. Optional. When
             the active template carries a `coverOverlay` block, render
             it as its OWN page between the cover (page 1) and the body
             pages (page 2 onward). Pre-TCP this content was injected at
             the TOP of the cover wrapper, which crowded the title
             block on every non-classic variant. The new component
             carries the same testids (`cover-overlay`,
             `cover-overlay-signature-render`,
             `cover-overlay-dateline-render`) so pinned overlay tests
             keep finding it.

             TCP-3 (2026-06-05) — placement by cover TYPE. Only FULL-PAGE
             (hero-image) covers get the letter as its own separate page
             HERE; the non-full-page academic title covers (classic /
             boundary-plate / data-snapshot / masthead / minimal) render
             the letter inline BENEATH the cover on page 1 (mounted inside
             the `#cover` block above) and instead break the executive
             summary onto a fresh page (see `coverOverlayBelowCover`). */}
                  {activeTemplate?.coverOverlay && heroCoverLayout && (
                    <ExecutiveLetterPage overlay={activeTemplate.coverOverlay} theme={theme} omitSectionSidebar={omitSectionSidebar} />
                  )}

                  {/* Contents is mounted in the page-1 flow shell above. V2
             emits canonical front matter before Executive Summary; legacy
             revisions retain their historical post-summary placement. */}

                  {/* Page 2 */}
                  <div data-page="2" data-testid="section-page-2" data-paged-flow-shell="true" className={page2ShellClass}>
                    {/* Confidential Sidebar — omitted when cover variant drops the rail. */}
                    {showConfidentialSidebar && !sectionOrderFlatten && (
                      <ConfidentialSidebar theme={theme} />
                    )}

                    {/* TSP-2 — flex column on the page-2 container so CSS `order: N`
               from `sectionStyle()` reorders the 5 study sections inside
               (gridStudy, transportStudy, connectivityStudy, riskAssessment,
               actionPlan). `gap-8` preserves the visual rhythm `space-y-8`
               provided. */}
                    {/* RRQ — `data-paged-flow-shell` marker: see the page-1 inner shell
              above. This `display:contents` wrapper holds transportStudy /
              connectivityStudy / sitingNotes etc.; without a real box pagedjs
              dropped their `[data-section]` wrappers during fragmentation. */}
                    <div data-paged-flow-shell="true" className={pageInnerShellClass}>
                      {/* Infrastructure section — hidden (replaced by study sections) */}
                      <div style={{ display: 'none' }} data-legacy-hidden>
                        <div className={colLayout('infrastructure') > 1 ? 'lg:col-span-7 space-y-8' : 'space-y-8'}>
                          <div className="space-y-4">
                            <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true"><EditableHeading headingKey="infrastructure" defaultText="Infrastructure &amp; interconnection" className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} /></h2>
                            <div className="space-y-6">
                              <div className="space-y-2">
                                <h3 className={`text-sm font-bold ${labelClass(labelTransform)} text-text-secondary`}>Electric & grid</h3>
                                {isEditing ? (
                                  <textarea
                                    className="w-full h-24 p-2 text-sm text-text-primary leading-relaxed border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary"
                                    value={editedReport.infrastructureInterconnection?.electricGrid || ""}
                                    onChange={(e) => setEditedReport({
                                      ...editedReport,
                                      infrastructureInterconnection: {
                                        ...editedReport.infrastructureInterconnection,
                                        electricGrid: e.target.value
                                      } as any
                                    })}
                                  />
                                ) : (
                                  <p className="text-xs text-text-secondary leading-relaxed">
                                    {report.infrastructureInterconnection?.electricGrid}
                                  </p>
                                )}
                              </div>
                              <div className="space-y-2">
                                <h3 className={`text-sm font-bold ${labelClass(labelTransform)} text-text-secondary`}>Transport & fiber</h3>
                                {isEditing ? (
                                  <textarea
                                    className="w-full h-24 p-2 text-sm text-text-primary leading-relaxed border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary"
                                    value={editedReport.infrastructureInterconnection?.transportFiber || ""}
                                    onChange={(e) => setEditedReport({
                                      ...editedReport,
                                      infrastructureInterconnection: {
                                        ...editedReport.infrastructureInterconnection,
                                        transportFiber: e.target.value
                                      } as any
                                    })}
                                  />
                                ) : (
                                  <p className="text-xs text-text-secondary leading-relaxed">
                                    {report.infrastructureInterconnection?.transportFiber}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <h3 className={`text-sm font-bold ${labelClass(labelTransform)} text-text-secondary`}>Municipal utilities snapshot</h3>
                            <table className="w-full text-left text-xs border-collapse">
                              <thead>
                                <tr className="border-b border-border">
                                  <th className="py-2 font-bold uppercase tracking-wider text-text-secondary">System</th>
                                  <th className="py-2 font-bold uppercase tracking-wider text-text-secondary">Total Capacity</th>
                                  <th className="py-2 font-bold uppercase tracking-wider text-text-secondary">Current Use</th>
                                  <th className="py-2 font-bold uppercase tracking-wider text-text-secondary">Available</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {((isEditing ? editedReport : report).infrastructureInterconnection?.municipalUtilities || []).map((util: any, idx: number) => (
                                  <tr key={idx}>
                                    <td className="py-3 font-medium text-text-primary">
                                      {isEditing ? (
                                        <input className="w-full bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary text-xs" value={util.system}
                                          onChange={(e) => {
                                            const utils = [...(editedReport.infrastructureInterconnection?.municipalUtilities || [])];
                                            utils[idx] = { ...utils[idx], system: e.target.value };
                                            setEditedReport({ ...editedReport, infrastructureInterconnection: { electricGrid: editedReport.infrastructureInterconnection?.electricGrid || "", transportFiber: editedReport.infrastructureInterconnection?.transportFiber || "", ...editedReport.infrastructureInterconnection, municipalUtilities: utils } });
                                          }} />
                                      ) : util.system}
                                    </td>
                                    <td className="py-3 text-text-secondary">
                                      {isEditing ? (
                                        <input className="w-full bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary text-xs" value={util.totalCapacity}
                                          onChange={(e) => {
                                            const utils = [...(editedReport.infrastructureInterconnection?.municipalUtilities || [])];
                                            utils[idx] = { ...utils[idx], totalCapacity: e.target.value };
                                            setEditedReport({ ...editedReport, infrastructureInterconnection: { electricGrid: editedReport.infrastructureInterconnection?.electricGrid || "", transportFiber: editedReport.infrastructureInterconnection?.transportFiber || "", ...editedReport.infrastructureInterconnection, municipalUtilities: utils } });
                                          }} />
                                      ) : util.totalCapacity}
                                    </td>
                                    <td className="py-3 text-text-secondary">
                                      {isEditing ? (
                                        <input className="w-full bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary text-xs" value={util.currentUse}
                                          onChange={(e) => {
                                            const utils = [...(editedReport.infrastructureInterconnection?.municipalUtilities || [])];
                                            utils[idx] = { ...utils[idx], currentUse: e.target.value };
                                            setEditedReport({ ...editedReport, infrastructureInterconnection: { electricGrid: editedReport.infrastructureInterconnection?.electricGrid || "", transportFiber: editedReport.infrastructureInterconnection?.transportFiber || "", ...editedReport.infrastructureInterconnection, municipalUtilities: utils } });
                                          }} />
                                      ) : util.currentUse}
                                    </td>
                                    <td className="py-3 font-bold text-text-primary">
                                      {isEditing ? (
                                        <input className="w-full bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary text-xs font-bold" value={util.available}
                                          onChange={(e) => {
                                            const utils = [...(editedReport.infrastructureInterconnection?.municipalUtilities || [])];
                                            utils[idx] = { ...utils[idx], available: e.target.value };
                                            setEditedReport({ ...editedReport, infrastructureInterconnection: { electricGrid: editedReport.infrastructureInterconnection?.electricGrid || "", transportFiber: editedReport.infrastructureInterconnection?.transportFiber || "", ...editedReport.infrastructureInterconnection, municipalUtilities: utils } });
                                          }} />
                                      ) : util.available}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      {/* END hidden infrastructure div */}

                      {/* Grid Power Study — #34 P2 STAGE B: lifted into
                          NEW_SECTION_REGISTRY (GridStudySection). Feasibility now
                          routes through NewTypeSectionsRouter (sentinel
                          `gridStudy`), so this inline block is gated OFF on
                          `!hasModernSections` to avoid a double-mount; it remains
                          the legacy fallback for any non-modern shape. */}
                      {!hasModernSections(report) && isVisible('gridStudy') && (
                        <SectionErrorBoundary sectionId="gridStudy" label="Grid Power Study">
                          <EditorialSectionShell
                            id="gridStudy"
                            data-section="gridStudy"
                            data-section-id="gridStudy"
                            {...sectionPageColumnProps('gridStudy')}
                            {...sectionNumberProps('gridStudy')}
                            extraClassName={inlineSectionChrome}
                            style={sectionStyle('gridStudy')}
                            omitSectionSidebar={omitSectionSidebar}
                            shellPadding={inlineSectionShellPadding}
                            theme={theme}
                          >
                            <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true">
                              <EditableHeading headingKey="gridStudy" defaultText={sectionDefaultHeading('gridStudy', 'Grid Power Study')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
                            </h2>
                            <ProvenanceStrip report={report} sectionId="gridStudy" />
                            {!report.gridStudy ? (
                              <div {...emptyStateNoteProps(editorialActive, theme, true)} data-testid="gridStudy-empty">
                                {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
                              </div>
                            ) : (
                              // This section IS an internal two-column grid (assessment
                              // column + recommendations/risks column). Under a two-column
                              // body preset it would otherwise be squeezed into ONE of the
                              // wrapper's two flow columns, fighting column-count. Mark it
                              // [data-span-columns] so it spans both flow columns and reads
                              // as a clean full-width exhibit (its own 2-col grid intact)
                              // instead of a torn half-width grid. No-op in single-column.
                              <div data-span-columns className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                                {/* Left column — assessment sub-blocks routed through the
                        theme-adaptive EditorialSubsection (editorial = hairline-top
                        prose flow, technical = thin left rule, compact = dense top
                        hairline). The heavy `border border-border` cards are gone. */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: blockChrome.scale === 'compact' ? 10 : 16 }}>
                                  <EditorialSubsection theme={theme} label="Interconnection Assessment">{renderCitedText(report.gridStudy.interconnectionAssessment)}</EditorialSubsection>
                                  <EditorialSubsection theme={theme} label="Transmission Access">{renderCitedText(report.gridStudy.transmissionAccess)}</EditorialSubsection>
                                  <EditorialSubsection theme={theme} label="Capacity Analysis">{renderCitedText(report.gridStudy.capacityAnalysis)}</EditorialSubsection>
                                </div>
                                {/* Right column — recommendations via the accent-LEFT-rule
                        EditorialRecommendations (no boxed accent card) + confirmed risks. */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                  <EditorialRecommendations
                                    theme={theme}
                                    items={((report.gridStudy?.recommendations) || []).map((rec) => renderCitedText(rec))}
                                  />
                                  {(report.gridStudy?.risks?.length ?? 0) > 0 && (
                                    <FindingClaimsList
                                      entries={report.gridStudy?.risks ?? []}
                                      references={report.references}
                                      siteId={report.siteId}
                                      reportId={report.id}
                                      sectionId="gridStudy"
                                      responses={report.openQuestionResponses}
                                      redFlagsHeading="Grid risks (confirmed)"
                                      className="space-y-4"
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                          </EditorialSectionShell>
                        </SectionErrorBoundary>
                      )}

                      {/* Capacity-Credit & Firm Adequacy (ELCC). #34 P2 STAGE B:
                          `capacityCreditDeepDive` is a SHARED id — feasibility / power /
                          financial / exec-summary all carry it and route it through the
                          NewTypeSectionsRouter above (it is one of the 5 study ids lifted
                          into NEW_SECTION_REGISTRY). Like the sibling study blocks
                          (gridStudy / transportStudy / connectivityStudy / riskAssessment)
                          this inline mount must be gated on `!hasModernSections` so the
                          router owns it on every MODERN report and the inline fork only
                          fires for a legacy / pre-modern report that carries the section.
                          The earlier `reportType !== 'feasibility'` gate let every modern
                          non-feasibility type (power / financial / exec) render the section
                          TWICE — once via the router AND once here. */}
                      {!hasModernSections(report) && isVisible('capacityCreditDeepDive') && (
                        <SectionErrorBoundary sectionId="capacityCreditDeepDive" label="Capacity-Credit & Firm Adequacy (ELCC)">
                          <EditorialSectionShell
                            id="capacityCreditDeepDive"
                            data-section="capacityCreditDeepDive"
                            data-section-id="capacityCreditDeepDive"
                            {...sectionPageColumnProps('capacityCreditDeepDive')}
                            {...sectionNumberProps('capacityCreditDeepDive')}
                            extraClassName={inlineSectionChrome}
                            style={sectionStyle('capacityCreditDeepDive')}
                            omitSectionSidebar={omitSectionSidebar}
                            shellPadding={inlineSectionShellPadding}
                            theme={theme}
                          >
                            <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true">
                              <EditableHeading headingKey="capacityCreditDeepDive" defaultText={sectionDefaultHeading('capacityCreditDeepDive', 'Capacity-Credit & Firm Adequacy (ELCC)')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
                            </h2>
                            <ProvenanceStrip report={report} sectionId="capacityCreditDeepDive" />
                            <RouterSectionChromeProvider value={{ isEditing, headingOverrides: headingOverrides ?? {}, onHeadingChange: setHeading, sectionDefaultHeading, isAdmin, onSectionRegen: handleSectionRegen }}>
                              <SectionIdProvider value="capacityCreditDeepDive">
                                <CapacityCreditDeepDiveSection report={report} label="Capacity-Credit & Firm Adequacy (ELCC)" />
                              </SectionIdProvider>
                            </RouterSectionChromeProvider>
                          </EditorialSectionShell>
                        </SectionErrorBoundary>
                      )}

                      {/* Transport & Logistics Study — #34 P2 STAGE B: lifted
                          (TransportStudySection); inline gated OFF on modern. */}
                      {!hasModernSections(report) && isVisible('transportStudy') && (
                        <SectionErrorBoundary sectionId="transportStudy" label="Transport & Logistics">
                          <EditorialSectionShell
                            id="transportStudy"
                            data-section="transportStudy"
                            data-section-id="transportStudy"
                            {...sectionNumberProps('transportStudy')}
                            extraClassName={inlineSectionChrome}
                            style={sectionStyle('transportStudy')}
                            omitSectionSidebar={omitSectionSidebar}
                            shellPadding={inlineSectionShellPadding}
                            theme={theme}
                          >
                            <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true">
                              <EditableHeading headingKey="transportStudy" defaultText={sectionDefaultHeading('transportStudy', 'Transport & Logistics')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
                            </h2>
                            {!report.transportStudy ? (
                              <div {...emptyStateNoteProps(editorialActive, theme, true)} data-testid="transportStudy-empty">
                                {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                                {/* Assessment sub-blocks → theme-adaptive EditorialSubsection (no boxy card). */}
                                <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: blockChrome.scale === 'compact' ? 10 : 16 }}>
                                  <EditorialSubsection theme={theme} label="Road Access">{renderCitedText(report.transportStudy.roadAccess)}</EditorialSubsection>
                                  <EditorialSubsection theme={theme} label="Rail Access">{renderCitedText(report.transportStudy.railAccess)}</EditorialSubsection>
                                  <EditorialSubsection theme={theme} label="Port / Waterway">{renderCitedText(report.transportStudy.portAccess)}</EditorialSubsection>
                                  <EditorialSubsection theme={theme} label="Heavy Haul Route">{renderCitedText(report.transportStudy.heavyHaulRoute)}</EditorialSubsection>
                                </div>
                                <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                  <EditorialRecommendations
                                    theme={theme}
                                    items={((report.transportStudy?.recommendations) || []).map((r) => renderCitedText(r))}
                                  />
                                  {(report.transportStudy?.risks?.length ?? 0) > 0 && (
                                    <FindingClaimsList
                                      entries={report.transportStudy?.risks ?? []}
                                      references={report.references}
                                      siteId={report.siteId}
                                      reportId={report.id}
                                      sectionId="transportStudy"
                                      responses={report.openQuestionResponses}
                                      redFlagsHeading="Transport risks (confirmed)"
                                      className="space-y-4"
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                          </EditorialSectionShell>
                        </SectionErrorBoundary>
                      )}

                      {/* Connectivity & Fiber Study — #34 P2 STAGE B: lifted
                          (ConnectivityStudySection); inline gated OFF on modern. */}
                      {!hasModernSections(report) && isVisible('connectivityStudy') && (
                        <SectionErrorBoundary sectionId="connectivityStudy" label="Connectivity & Fiber">
                          <EditorialSectionShell
                            id="connectivityStudy"
                            data-section="connectivityStudy"
                            data-section-id="connectivityStudy"
                            {...sectionNumberProps('connectivityStudy')}
                            extraClassName={inlineSectionChrome}
                            style={sectionStyle('connectivityStudy')}
                            omitSectionSidebar={omitSectionSidebar}
                            shellPadding={inlineSectionShellPadding}
                            theme={theme}
                          >
                            <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true">
                              <EditableHeading headingKey="connectivityStudy" defaultText={sectionDefaultHeading('connectivityStudy', 'Connectivity & Fiber')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
                            </h2>
                            {!report.connectivityStudy ? (
                              <div {...emptyStateNoteProps(editorialActive, theme, true)} data-testid="connectivityStudy-empty">
                                {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
                                {/* Assessment sub-blocks → theme-adaptive EditorialSubsection (no boxy card). */}
                                <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: blockChrome.scale === 'compact' ? 10 : 16 }}>
                                  <EditorialSubsection theme={theme} label="Fiber Infrastructure">{renderCitedText(report.connectivityStudy.fiberInfrastructure)}</EditorialSubsection>
                                  <EditorialSubsection theme={theme} label="Internet Exchange">{renderCitedText(report.connectivityStudy.internetExchange)}</EditorialSubsection>
                                  <EditorialSubsection theme={theme} label="Data Center Facilities">{renderCitedText(report.connectivityStudy.datacenters)}</EditorialSubsection>
                                  <EditorialSubsection theme={theme} label="Latency Analysis">{renderCitedText(report.connectivityStudy.latencyAnalysis)}</EditorialSubsection>
                                </div>
                                <div data-layout-group="row-group" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                                  <EditorialRecommendations
                                    theme={theme}
                                    items={((report.connectivityStudy?.recommendations) || []).map((r) => renderCitedText(r))}
                                  />
                                  {(report.connectivityStudy?.risks?.length ?? 0) > 0 && (
                                    <FindingClaimsList
                                      entries={report.connectivityStudy?.risks ?? []}
                                      references={report.references}
                                      siteId={report.siteId}
                                      reportId={report.id}
                                      sectionId="connectivityStudy"
                                      responses={report.openQuestionResponses}
                                      redFlagsHeading="Connectivity risks (confirmed)"
                                      className="space-y-4"
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                          </EditorialSectionShell>
                        </SectionErrorBoundary>
                      )}

                      {/* COMBINES (merge #2) — Siting & Severe Weather. The
                          surviving `sitingNotes` section now renders BOTH the
                          siting prose AND the formerly-standalone
                          `climateSevereWeather` body (KPI rail + severe-weather
                          risks + monthly climate-profile chart) under one
                          "Siting & Severe Weather" heading. The standalone
                          `climateSevereWeather` inline block was removed; that id
                          is no longer in feasibility REPORT_SECTIONS so
                          `isVisible('climateSevereWeather')` is false (no
                          double-render). Both source FIELDS (`report.sitingNotes`,
                          `report.climateSevereWeather`) are still emitted by the
                          prompt and consumed here. */}
                      {isVisible('sitingNotes') && (
                        <SectionErrorBoundary sectionId="sitingNotes" label="Siting & Severe Weather">
                          <EditorialSectionShell
                            id="sitingNotes"
                            data-section="sitingNotes"
                            {...sectionPageColumnProps('sitingNotes')}
                            {...sectionNumberProps('sitingNotes')}
                            extraClassName={`${inlineSectionChrome} space-y-4`}
                            style={sectionStyle('sitingNotes')}
                            omitSectionSidebar={omitSectionSidebar}
                            shellPadding={inlineSectionShellPadding}
                            theme={theme}
                            editorialActive={editorialActive}
                            sectionNumber={sectionDisplayNumbers?.get('sitingNotes')}
                            heading={<EditableHeading headingKey="sitingNotes" defaultText={sectionDefaultHeading('sitingNotes', 'Siting & Severe Weather')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />}
                          >
                            {/* Classic mode (flag OFF) — caller-owned head (byte-identical
                                to the retired ClassicSectionShell path). Editorial mode:
                                the shell owns the opener, so this is suppressed. */}
                            {!editorialActive && (
                              <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true">
                                <EditableHeading headingKey="sitingNotes" defaultText={sectionDefaultHeading('sitingNotes', 'Siting & Severe Weather')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
                              </h2>
                            )}
                            {/* ── Sub-body 1: Siting (verbatim former sitingNotes body) ──
                                P-GROUP Phase A (`head-body`) — the whole sub-body is a
                                BOUNDED atom (h3 + banner/chip/strip + ONE ≤1500-char
                                prose <p>), so binding it keeps the "Siting Notes" h3
                                from stranding at a page foot (the live p9 failure:
                                the h3 was the page's last text with 85% blank below). */}
                            {/* SITING-2COL (William 2026-07-04) — the sub-body SPANS both
                                page columns (data-flow="span") so the prose is never
                                isolated in column 1 ahead of the full-width climate
                                exhibits (the "only left column" defect: a short prose
                                block before a spanner leaves column 2 blank under
                                column-fill:auto). The prose then re-columnises into TWO
                                balanced columns in 2-col presets — see the inner box
                                below. */}
                            <div className="space-y-3" data-subsection="sitingNotes" data-flow="span" data-layout-group="head-body">
                              <h3 className="text-[11px] font-semibold uppercase" style={{ fontFamily: 'var(--report-body-font, inherit)', letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}>Siting Notes</h3>
                              <SectionQuarantineBanner
                                sectionId="sitingNotes"
                                sectionLabel="Siting Notes"
                                guardStatus={report.sectionGuardStatus?.['sitingNotes']}
                                quarantinedOriginal={report.quarantinedOriginal?.['sitingNotes']}
                                renderedText={report.sitingNotes}
                                onAuthor={() => setIsEditing(true)}
                              />
                              <MetaLeakChip
                                report={report}
                                sectionId="sitingNotes"
                                sectionLabel="Siting Notes"
                                isAdmin={isAdmin}
                                onRegenerate={handleSectionRegen}
                              />
                              <ProvenanceStrip report={report} sectionId="sitingNotes" />
                              {isEditing ? (
                                <div className="relative">
                                  <textarea
                                    className="w-full h-24 p-2 text-xs leading-relaxed text-text-secondary italic border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary"
                                    value={editedReport.sitingNotes || ''}
                                    onChange={(e) => setEditedReport({ ...editedReport, sitingNotes: e.target.value })}
                                    placeholder="Detailed site analysis pending ground-based GIS verification."
                                    maxLength={1500}
                                  />
                                  <span className="absolute bottom-1 right-2 text-xs text-text-secondary">
                                    {(editedReport.sitingNotes || '').length}/1500
                                  </span>
                                </div>
                              ) : report.sitingNotes ? (
                                // Two BALANCED prose columns in a 2-col preset (the
                                // sub-body already spans full width above); single-column
                                // presets render a plain full-width paragraph (columnCount
                                // omitted ⇒ byte-stable). Bounded (≤1500 chars) so the
                                // balanced box never exceeds one page.
                                <div style={sectionPageColumnCount('sitingNotes') === 2 ? { columnCount: 2, columnGap: '1.75rem', columnFill: 'balance' } : undefined}>
                                  <p className="text-xs leading-relaxed text-text-secondary italic" style={{ marginTop: 0 }}>
                                    {walkClaimsText(report.sitingNotes, report.references, report.claims, appendixVisibleRefIndices)}
                                  </p>
                                </div>
                              ) : (
                                <InsufficientData
                                  section="Siting Notes"
                                  onEdit={() => setIsEditing(true)}
                                  reportId={report.id}
                                  sectionId="sitingNotes"
                                  autoFill={autoFillFor('sitingNotes')}
                                />
                              )}
                            </div>
                            {/* ── Sub-body 2: Severe Weather & Climate (folded former
                                climateSevereWeather body, verbatim). Keeps a nested
                                `data-section`/`data-section-id` marker (inside the
                                sitingNotes shell) so the climate content stays
                                discoverable as a section subtree on BOTH surfaces
                                — same nesting precedent as `recommendations` inside
                                `actionPlan` — guarding against a silent climate
                                drop from the merged section. */}
                            <div className="space-y-3" data-section="climateSevereWeather" data-subsection="climateSevereWeather" data-section-id="climateSevereWeather">
                              {/* P-GROUP Phase A (`head-body`) — bind the "Severe Weather
                                  & Climate" h3 to its first BOUNDED content (banner strip
                                  + KPI rail, or the insufficient-data note) so the
                                  sub-head can never strand at a page foot. The UNBOUNDED
                                  content (risk subsections + the climate chart) stays
                                  outside the atom and fragments freely. */}
                              <div className="space-y-3" data-layout-group="head-body">
                                <h3 className="text-[11px] font-semibold uppercase" style={{ fontFamily: 'var(--report-body-font, inherit)', letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}>Severe Weather &amp; Climate</h3>
                                {!isEditing && !report.climateSevereWeather ? (
                                  <InsufficientData
                                    section="Climate & Severe Weather"
                                    onEdit={() => setIsEditing(true)}
                                    reportId={report.id}
                                    sectionId="climateSevereWeather"
                                    autoFill={autoFillFor('climateSevereWeather')}
                                  />
                                ) : (
                                  <>
                                    <SectionQuarantineBanner
                                      sectionId="climateSevereWeather.implications"
                                      sectionLabel="Climate Implications"
                                      guardStatus={report.sectionGuardStatus?.['climateSevereWeather.implications']}
                                      quarantinedOriginal={report.quarantinedOriginal?.['climateSevereWeather.implications']}
                                      renderedText={report.climateSevereWeather?.implications}
                                      onAuthor={() => setIsEditing(true)}
                                    />
                                    <ProvenanceStrip report={report} sectionId="climateSevereWeather" />
                                    {/* SECTION-PORT (10A, "dual-axis chart" family) — the locked
                            Climate layout. The dual-axis monthly chart is the existing
                            <ClimateProfileChart> (KEPT, mounted below outside the
                            narrative ternary). Here we add the editorial KPI ledger
                            ABOVE the findings: peak / avg wet-bulb + peak temp from the
                            SAME real climate data the chart plots (climateTimeSeries +
                            datasets.climate) and flood risk from datasets.land — the
                            rail drops any cell whose value is absent (no fabrication). */}
                                    {!isEditing && (() => {
                                      const climate = report.datasets?.climate;
                                      const land = report.datasets?.land;
                                      const ts = resolveReportTimeSeries(report, effectiveLiveSed?.climate?.data ?? null);
                                      const monthlyWetBulb = ts.map(p => p.wetBulbTemp).filter((v): v is number => Number.isFinite(v));
                                      const peakWetBulb = monthlyWetBulb.length ? Math.max(...monthlyWetBulb) : null;
                                      const railItems: EditorialKpiItem[] = [
                                        { key: peakWetBulb != null ? 'Highest monthly wet-bulb (hist. screen)' : 'Avg wet-bulb (hist. screen)', value: peakWetBulb != null ? peakWetBulb.toFixed(1) : (Number.isFinite(climate?.avgWetBulb) ? climate!.avgWetBulb.toFixed(1) : null), unit: '°C', tone: 'warning' },
                                        { key: 'Avg wet-bulb', value: Number.isFinite(climate?.avgWetBulb) ? climate!.avgWetBulb.toFixed(1) : null, unit: '°C' },
                                        { key: 'Peak temp', value: Number.isFinite(climate?.peakTemp) ? climate!.peakTemp.toFixed(0) : null, unit: '°C' },
                                        floodRiskKpi(land),
                                      ];
                                      return <EditorialKpiRail theme={theme} items={railItems} />;
                                    })()}
                                  </>
                                )}
                              </div>
                              {/* Key Risks → editorial findings (no raw text-utility bullet
                          list). View mode renders an EditorialSubsection per risk so
                          the body reads in var(--report-body-font); edit mode keeps
                          the inline textareas. renderCitedText preserves citation
                          walking. Rendered OUTSIDE the head-body atom (unbounded). */}
                              {(isEditing || report.climateSevereWeather) && (
                                isEditing ? (
                                    <div className="space-y-3">
                                      <div className={`text-xs font-bold text-text-secondary ${labelClass(labelTransform)}`}>Key Risks</div>
                                      <div className="space-y-2">
                                        {(editedReport.climateSevereWeather?.risks || []).map((risk, idx) => (
                                          <div key={idx} className="flex gap-2">
                                            <span className="text-text-secondary mt-1">•</span>
                                            <textarea
                                              className="flex-1 h-12 p-1 text-xs text-text-secondary border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary"
                                              value={risk}
                                              onChange={(e) => {
                                                const newRisks = [...(editedReport.climateSevereWeather?.risks || [])];
                                                newRisks[idx] = e.target.value;
                                                setEditedReport({ ...editedReport, climateSevereWeather: { ...editedReport.climateSevereWeather!, risks: newRisks } });
                                              }}
                                            />
                                          </div>
                                        ))}
                                        <Button variant="ghost" size="sm" className="h-6 text-xs text-primary/40" onClick={() => {
                                          const newRisks = [...(editedReport.climateSevereWeather?.risks || []), ''];
                                          setEditedReport({ ...editedReport, climateSevereWeather: { ...editedReport.climateSevereWeather!, risks: newRisks } });
                                        }}>+ Add Risk</Button>
                                      </div>
                                    </div>
                                ) : (
                                  <div className="space-y-4">
                                    {(report.climateSevereWeather?.risks || []).map((risk, idx) => (
                                      // William QA 2026-06-29 — the climate KPI rail above already
                                      // draws a bottom rule; the first risk subsection's own TOP rule
                                      // stacked a SECOND horizontal line right under the table. Drop
                                      // the top rule on the first item so only one line shows.
                                      <EditorialSubsection key={idx} theme={theme} label={`Severe-weather risk ${idx + 1}`} style={idx === 0 ? { borderTop: 'none', paddingTop: 0 } : undefined}>
                                        {renderCitedText(risk)}
                                      </EditorialSubsection>
                                    ))}
                                  </div>
                                )
                              )}
                              {/* Task 5 — Climate Profile chart FOLDED in here (was a
                          standalone `climateProfile` section that stranded a
                          near-empty page). It sits OUTSIDE the narrative /
                          insufficient-data ternary above so it renders whenever the
                          section is visible and a time-series exists. Suppressed in
                          edit mode (chart is read-only). */}
                              {!isEditing && (() => {
                                const climateTimeSeries = resolveReportTimeSeries(
                                  report,
                                  effectiveLiveSed?.climate?.data ?? null,
                                );
                                if (climateTimeSeries.length === 0) return null;
                                return (
                                  // P-GROUP Phase A (`exhibit`) — sub-head + chart +
                                  // caption are ONE bounded atom (ChartFrame caps the
                                  // plot below one page), so the "Monthly Climate
                                  // Profile" h3 can never separate from its figure.
                                  <div data-span-columns data-layout-group="exhibit" className="space-y-2 pt-4">
                                    {/* Sub-heading for the folded climate chart. Kept as an
                                <h3> (not a styled <div>) so it is a SEMANTIC
                                sub-heading of the section (TCP-6). The open tag +
                                text stay on ONE line so the TCP-6 source scan
                                resolves the heading to an <h3>. */}
                                    <h3 className="text-[11px] font-semibold uppercase" style={{ fontFamily: 'var(--report-body-font, inherit)', letterSpacing: '0.14em', color: roleColor(theme, 'ink') }}>Monthly Climate Profile</h3>
                                    <ClimateProfileChart timeSeries={climateTimeSeries} theme={theme} />
                                    {/* Redhead wave 3 (defect 5a) — report-global sequential numbering via
                                        the figure counter (was a hardcoded 1 that collided with the map's
                                        "Figure 1"). */}
                                    <EditorialFigureCaption theme={theme} figureKey="sitingNotes:climateProfile">
                                      Monthly dry-bulb and wet-bulb temperature profile across the year (chart reference line shows the unweighted arithmetic mean of the 12 monthly wet-bulb rows; any difference from the reported climate summary avgWetBulb field is unverified in the stored payload because raw sub-monthly/hourly series and aggregation weights are not retained).
                                    </EditorialFigureCaption>
                                  </div>
                                );
                              })()}
                            </div>
                          </EditorialSectionShell>
                        </SectionErrorBoundary>
                      )}

                      {/* COMBINES (merge #2) — the standalone Climate & Severe
                          Weather inline section was REMOVED: its body (KPI rail +
                          severe-weather risks + monthly climate-profile chart) is
                          now FOLDED INTO the Siting & Severe Weather section above
                          (`sitingNotes`), under the "Severe Weather & Climate"
                          sub-head. `climateSevereWeather` is no longer in
                          feasibility REPORT_SECTIONS, so `isVisible` returns false
                          for it anyway — this block is gone to avoid dead code and
                          any double-render. The `report.climateSevereWeather` field
                          is still emitted by the prompt and consumed by the folded
                          sub-body. */}

                      {/* Page-2 inline footer removed (Body Footer Dedup pack, BF1).
                 See the matching note on page 1: the footer text renders
                 once at the end of the report via <ReportFooter>; exported
                 DOCX/PDF get their per-page footers from the export
                 pipelines, not from this div. */}
                    </div>
                  </div>

                  <ReportSubSectionBlocks
                    report={report}
                    theme={theme}
                    isVisible={isVisible}
                    sectionStyle={sectionStyle}
                    omitSectionSidebar={omitSectionSidebar}
                    shellPadding={inlineSectionShellPadding}
                  />

                  {/* Task 5 — the standalone Climate Profile Chart section was removed:
            the chart now renders FOLDED into the Climate & Severe Weather
            section body above (see the <ClimateProfileChart> mount there), and
            the `climateProfile` section def was dropped from the registry so it
            no longer appears in the section list or the TOC. This eliminates
            the near-empty stranded page the standalone section produced. */}

                  {/* Risk Assessment — #34 P2 STAGE B: lifted (RiskAssessmentSection);
                      inline gated OFF on modern so feasibility renders it via the router. */}
                  {!hasModernSections(report) && (isVisible('riskAssessment') || isVisible('devRisks')) && (
                    <EditorialSectionShell
                      id="riskAssessment"
                      data-section="riskAssessment"
                      data-section-id="riskAssessment"
                      {...sectionNumberProps('riskAssessment')}
                      {...sectionPageColumnProps('riskAssessment')}
                      extraClassName={inlineSectionChrome}
                      style={sectionStyle('riskAssessment')}
                      omitSectionSidebar={omitSectionSidebar}
                      shellPadding={inlineSectionShellPadding}
                      theme={theme}
                    >
                      <h2 className={`${headingChrome.h2Class} ${REPORT_HEADING_MB_CLASS}`} style={headingChrome.h2Style} data-heading="true">
                        <EditableHeading headingKey="riskAssessment" defaultText={sectionDefaultHeading('riskAssessment', 'Risk Assessment')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
                      </h2>
                      <ProvenanceStrip report={report} sectionId="riskAssessment" />
                      {!(mergedDevelopmentRisks.length || report.risks?.length) ? (
                        <div {...emptyStateNoteProps(editorialActive, theme, false)} data-testid="riskAssessment-empty">
                          {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
                        </div>
                      ) : (() => {
                        // SECTION-PORT (14B, "ranked register") — the locked Risk
                        // Assessment layout. GAP: the report carries only a risk LIST —
                        // neither DetailedRisk nor RiskItem carry ordinal likelihood(1-3)
                        // × impact(1-3), so the 14A EditorialRiskMatrix (which plots into
                        // L×I cells) cannot be composed without fabricating coordinates.
                        // Per the port MAP we degrade to the documented 14B fallback: a
                        // ranked register via EditorialDataTable (Risk / Likelihood /
                        // Impact / Mitigation). The likelihood band keeps its EditorialBadge
                        // tone signal; impact + mitigation are the real free-text fields.
                        // When gen emits ordinal L/I scores, switch this block to
                        // EditorialRiskMatrix (14A) — do NOT assign random ordinals here.
                        type RegisterRow = {
                          key: string;
                          name: string;
                          bandLabel: string;
                          tone: 'negative' | 'warning' | 'positive';
                          impact: string;
                          mitigation: string;
                        };
                        const devRows: RegisterRow[] = mergedDevelopmentRisks.map((risk, idx) => ({
                          key: `dev-${idx}`,
                          name: risk.category,
                          bandLabel: risk.riskLevel === 'High' ? 'High' : risk.riskLevel === 'Medium' ? 'Medium' : 'Low',
                          tone: risk.riskLevel === 'High' ? 'negative' : risk.riskLevel === 'Medium' ? 'warning' : 'positive',
                          impact: risk.reason,
                          mitigation: risk.action,
                        }));
                        // BTM-4 — dedupe report.risks against the MERGED dev-risk set
                        // (incl. derived phasing risks) so a phasing risk doesn't echo
                        // a near-duplicate generic risk below it.
                        const devRiskKeywords = mergedDevelopmentRisks.flatMap(dr => {
                          const words = (dr.category + ' ' + (dr.reason || '')).toLowerCase().split(/\s+/);
                          return words.filter(w => w.length > 4);
                        });
                        const uniqueRisks = (report.risks ?? []).filter(r => {
                          const label = r.label.toLowerCase();
                          const impact = (r.impact || '').toLowerCase();
                          const matchCount = devRiskKeywords.filter(kw => label.includes(kw) || impact.includes(kw)).length;
                          return matchCount < 3;
                        });
                        const genRows: RegisterRow[] = uniqueRisks.map((risk, idx) => {
                          const rawRisk = risk as { id?: string; source?: string };
                          const isEvidenceGap =
                            rawRisk.source === 'scoring'
                            || (typeof rawRisk.id === 'string' && rawRisk.id.startsWith('evidence-gap-'))
                            || /not specified in the site evidence\s*[—–-]\s*it is excluded from scoring/i.test(risk.impact || '');
                          return {
                            key: `gen-${idx}`,
                            name: risk.label,
                            bandLabel: isEvidenceGap
                              ? 'Excluded (Evidence Gap)'
                              : risk.severity.charAt(0).toUpperCase() + risk.severity.slice(1),
                            tone: isEvidenceGap
                              ? 'warning'
                              : risk.severity === 'critical' || risk.severity === 'high'
                                ? 'negative'
                                : risk.severity === 'medium'
                                  ? 'warning'
                                  : 'positive',
                            impact: risk.impact,
                            mitigation: risk.mitigation,
                          };
                        });
                        const registerRows = [...devRows, ...genRows];
                        if (registerRows.length === 0) return null;
                        const evidenceGapCount = registerRows.filter((r) => r.bandLabel === 'Excluded (Evidence Gap)').length;
                        const assessedRiskCount = registerRows.length - evidenceGapCount;
                        return (
                          <>
                            <EditorialDataTable
                              theme={theme}
                              // 3 COLUMNS (user request): Likelihood column dropped; its
                              // band badge folded UNDER the risk name in the Risk cell so
                              // Impact + Mitigation get the freed horizontal room. Kept in
                              // lock-step with RiskAssessmentSection.tsx.
                              columns={[
                                { header: 'Risk', width: '22%' },
                                { header: 'Impact' },
                                { header: 'Mitigation' },
                              ]}
                              rows={registerRows.map((row) => [
                                <div key="risk" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                                  <span>{row.name}</span>
                                  <EditorialBadge theme={theme} variant="outline" tone={row.tone} wrap style={{ maxWidth: '100%' }}>
                                    {row.bandLabel}
                                  </EditorialBadge>
                                </div>,
                                renderCitedText(row.impact),
                                row.mitigation ? renderCitedText(row.mitigation) : '–',
                              ])}
                            />
                            {/* Redhead wave 3 (defect 5a) — tables number on their own report-global
                                sequence via the figure counter (was a hardcoded 1). */}
                            <EditorialFigureCaption theme={theme} figureKey="riskAssessment:register" prefix="Table">
                              {evidenceGapCount > 0
                                ? `Risk & evidence-gap register – impact / status and mitigation per identified item (${assessedRiskCount} assessed project-specific risks plus ${evidenceGapCount} unscored evidence gaps).`
                                : 'Risk register – likelihood band, impact and mitigation per identified risk.'}
                            </EditorialFigureCaption>
                          </>
                        );
                      })()}
                    </EditorialSectionShell>
                  )}

                  {!hasModernSections(report) && (isVisible('actionPlan') || isVisible('devPathway') || isVisible('recommendations')) && (
                    <EditorialSectionShell
                      id="actionPlan"
                      data-section="actionPlan"
                      data-section-id="actionPlan"
                      {...sectionNumberProps('actionPlan')}
                      extraClassName={inlineSectionChrome}
                      style={actionPlanSectionStyle()}
                      omitSectionSidebar={omitSectionSidebar}
                      shellPadding={inlineSectionShellPadding}
                      theme={theme}
                    >
                      <h2 className={`${headingChrome.h2Class} ${REPORT_HEADING_MB_CLASS}`} style={headingChrome.h2Style} data-heading="true">
                        <EditableHeading headingKey="actionPlan" defaultText={sectionDefaultHeading('actionPlan', 'Action Plan & Pathway')} className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} />
                      </h2>
                      {!(report.developmentPathway?.length || report.recommendations?.length) ? (
                        <div {...emptyStateNoteProps(editorialActive, theme, false)} data-testid="actionPlan-empty">
                          {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
                        </div>
                      ) : (
                        // SECTION-PORT (17A "numbered list + conditions", w/ a phased
                        //   pathway timeline for the phase-tagged developmentPathway).
                        // The recommendations are a FLAT list (Recommendation: type /
                        // title / description — NO phase tag), so per the port MAP they
                        // render via EditorialRecommendations (17A numbered), each item
                        // composing its title + type EditorialBadge + description.
                        // GAP: phase grouping of the recommendations/actions is NOT
                        // generated — we do NOT invent phase assignments for them. The
                        // developmentPathway IS phase-tagged (phase.phase) and therefore
                        // renders through EditorialTimeline variant='phased' (real phases,
                        // not fabricated), with each phase's title as its window note and
                        // its description as the lone action line.
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                          <div className="lg:col-span-3">
                            <EditorialTimeline
                              theme={theme}
                              variant="phased"
                              phases={(report.developmentPathway ?? []).map((phase) => {
                                const hasDescription = Boolean(phase.description?.trim());
                                return {
                                  label: phase.phase,
                                  window: hasDescription ? phase.title : (phase.cod || phase.capacityMW || undefined),
                                  actions: [renderCitedText(hasDescription ? phase.description : (phase.title || phase.capacityMW || phase.phase))],
                                };
                              })}
                            />
                          </div>
                          <div className="lg:col-span-2">
                            {/* 17A fallback — flat recommendations as a numbered list; the
                        type badge composes inline with each title. */}
                            <EditorialRecommendations
                              theme={theme}
                              heading="Key Recommendations"
                              marker="decimal"
                              items={(report.recommendations || []).map((rec, idx) => (
                                <span key={idx}>
                                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                                    <strong style={{ color: roleColor(theme, 'ink') }}>{rec.title}</strong>
                                    <EditorialBadge theme={theme} variant="outline" tone={rec.type === 'power' ? 'warning' : rec.type === 'cooling' ? 'accent' : 'positive'}>{rec.type}</EditorialBadge>
                                  </span>
                                  {rec.description && (
                                    <span style={{ display: 'block', marginTop: 4 }}>{renderCitedText(rec.description)}</span>
                                  )}
                                </span>
                              ))}
                            />
                          </div>
                        </div>
                      )}
                    </EditorialSectionShell>
                  )}

                  {/* Feasibility Profile (radar + bar list) — extracted into
             FeasibilityProfileSection. The previous inline 5-col grid
             packed the radar (col-span-2) next to the bar list
             (col-span-3); at print width that wrapped category labels
             over multiple lines and bled into the radar's tick text.
             The new section stacks them vertically with a fixed-column
             bar grid. */}
                  <SectionErrorBoundary sectionId="radarChart" label="Feasibility Profile">
                    <FeasibilityProfileSection
                      report={report}
                      theme={theme}
                      isEditing={isEditing}
                      headingOverrides={headingOverrides}
                      setHeading={setHeading}
                      isVisible={isVisible}
                      sectionStyle={sectionStyle}
                      omitSectionSidebar={omitSectionSidebar}
                      shellPadding={inlineSectionShellPadding}
                      sectionPageColumnCount={sectionPageColumnCount}
                      sectionFlowMode={sectionFlowMode}
                      sectionNumberProps={sectionNumberProps('radarChart')}
                      editorialActive={editorialActive}
                      editorialSectionNumber={sectionDisplayNumbers?.get('radarChart')}
                    />
                  </SectionErrorBoundary>

                  {/* SSM-6 — Score Provenance Panel. Per-category breakdown of
             which value drove the score: provider data, doc-derived,
             doc-override, or provider+doc blend. Self-suppresses on
             pre-SSM reports (no `scoringInfluence`, no per-metric
             provenance). */}
                  <ScoreProvenancePanel
                    report={report}
                    site={reportSite}
                    sectionStyle={sectionStyle}
                    omitSectionSidebar={omitSectionSidebar}
                    shellPadding={inlineSectionShellPadding}
                    sectionPageColumnCount={sectionPageColumnCount}
                  />

                  {/* LCOE Cost Comparison — extracted.
             TSP-1 — wire `sectionStyle` so the Studio's drag-reorder
             can place this section anywhere in the visual order. */}
                  {report.reportType !== 'public-sentiment' && (
                    <SectionErrorBoundary sectionId="lcoeChart" label="LCOE Cost Comparison">
                      <LcoeChartSection report={report} theme={theme} isVisible={isVisible} sectionStyle={sectionStyle} omitSectionSidebar={omitSectionSidebar} shellPadding={inlineSectionShellPadding} sectionPageColumnCount={sectionPageColumnCount} editorialActive={editorialActive} editorialSectionNumber={sectionDisplayNumbers?.get('lcoeChart')} />
                    </SectionErrorBoundary>
                  )}

                  {/* R-131-FIN-DECISION-K — pinned pathway comparison. Modern types
             (test-fit / executive-summary) render this via NewTypeSectionsRouter
             above; feasibility has no modern sentinel, so it is mounted here
             inline. The component self-suppresses when no comparison was pinned
             at gen-time (needs ≥ 2 pathways). Gated on visibility so the Studio's
             eye toggle is honoured, and on `!hasModernSections` so it never
             double-renders alongside the router. */}
                  {/* Task 3 — the OUTER shell owns the heading, so it must self-omit on
            the SAME condition as the inner component: in the live viewer + PDF
            export, an unpinned pathway comparison (no snapshot with 2+
            alternatives) reserves no page. The Studio preview keeps the
            placeholder. Without this gate the inner component returns null but
            the outer <h2> + empty shell would still strand a heading. */}
                  {!hasModernSections(report)
                    && isVisible('pathway_comparison')
                    && (studioPreview
                      || Boolean(report.pathwayComparisonAtGen
                        && report.pathwayComparisonAtGen.pathways.length >= 2)) && (
                      <EditorialSectionShell
                        id="pathway_comparison"
                        data-section="pathway_comparison"
                        data-section-id="pathway_comparison"
                        {...sectionNumberProps('pathway_comparison')}
                        style={sectionStyle('pathway_comparison')}
                        data-section-columns={String(sectionPageColumnCount('pathway_comparison'))}
                        data-section-flow={sectionFlowMode('pathway_comparison')}
                        theme={theme}
                        omitSectionSidebar={omitSectionSidebar}
                        shellPadding={inlineSectionShellPadding}
                        extraClassName={inlineSectionShellPadding ? `${inlineSectionChrome} space-y-4` : undefined}
                      >
                        <h2
                          className={headingChrome.h2Class}
                          style={headingChrome.h2Style}
                          data-heading="true"
                        >
                          <EditableHeading
                            headingKey="pathway_comparison"
                            defaultText={sectionDefaultHeading('pathway_comparison', 'Pathway Comparison')}
                            className={headingChrome.textClass}
                            isEditing={isEditing}
                            overrides={headingOverrides}
                            onChange={setHeading}
                          />
                        </h2>
                        <RouterSectionChromeProvider
                          value={{
                            isEditing,
                            headingOverrides,
                            onHeadingChange: setHeading,
                            sectionDefaultHeading,
                            isAdmin,
                            onSectionRegen: handleSectionRegen,
                          }}
                        >
                          <SectionIdProvider value="pathway_comparison">
                            {/* Outer h2 above already renders the heading via
                    headingChrome (matches the canonical style used by
                    ExtendedDataSection + every other classic-shell
                    section). Suppress the SectionShell's inner h2 so
                    the title isn't rendered twice (the doubling was
                    the visible "Pathway Comparison sits indented vs
                    Extended Site Data" complaint). */}
                            <PathwayComparisonReportSection report={report} label="Pathway Comparison" suppressHeading />
                          </SectionIdProvider>
                        </RouterSectionChromeProvider>
                      </EditorialSectionShell>
                    )}

                  {/* Extended Data — extracted.
                      COMBINES (fold #5) — this STANDALONE mount now serves ONLY
                      public-sentiment, whose `sentimentResearchDetail` variant
                      stays its own numbered section. For every other type the
                      Extended Site Data content renders INSIDE the Appendix &
                      References section via the `extendedDataSlot` passed to
                      ReferencesSection below. An ARCHIVED sectionOrder that
                      still lists `extendedData` cannot double-render: the
                      standalone mount is gone for those types (the fold
                      replaces it) and the persisted id is filtered out at
                      order-resolution time (projectTemplateSectionIds drops
                      non-canonical ids) — mirroring the connectivityStudy /
                      capacityCreditDeepDive / pathway_comparison fold
                      suppressions. */}
                  {report.reportType === 'public-sentiment' && (
                    <ExtendedDataSection
                      report={report} theme={theme} isEditing={isEditing} editedReport={editedReport}
                      setEditedReport={setEditedReport} headingOverrides={headingOverrides} setHeading={setHeading}
                      isVisible={isVisible} sectionStyle={sectionStyle}
                      locale={presentation.locale}
                      omitSectionSidebar={omitSectionSidebar}
                      shellPadding={inlineSectionShellPadding}
                      sectionPageColumnCount={sectionPageColumnCount}
                      sectionNumberProps={sectionNumberProps('sentimentResearchDetail')}
                      editorialActive={editorialActive}
                      editorialSectionNumber={sectionDisplayNumbers?.get('sentimentResearchDetail')}
                      // ELV-1 / ELV-2 — feed the live SED so legacy-zero-triple
                      // reports can fall back to the EPQS centroid reading, and
                      // wire the Refresh CTA when a live SiteEntity exists.
                      sed={liveSiteSed}
                      onRefreshElevation={reportSite ? handleRefreshElevation : undefined}
                      isRefreshingElevation={isRefreshingElevation}
                      // ELV-4 — surface the live SiteEntity's "last refreshed"
                      // stamp so the diagnostic can render "5 min ago" after a
                      // successful Refresh. Pre-ELV-4 sites leave the field
                      // undefined and the chip omits the timestamp.
                      siteElevationRefreshedAt={reportSite?.siteElevationRange?.refreshedAt}
                    />
                  )}

                  <ReferencesSection
                    report={report} site={reportSite} sed={liveSiteSed ?? undefined} theme={theme} isAdmin={isAdmin} isEditing={isEditing} editedReport={editedReport}
                    immutableArtifactMode={immutableArtifactMode}
                    setEditedReport={setEditedReport} headingOverrides={headingOverrides} setHeading={setHeading}
                    isVisible={isVisible} sectionStyle={sectionStyle}
                    editableRefs={editableRefs} addReference={addReference} updateReference={updateReference} removeReference={removeReference}
                    customSections={customSections} addCustomSection={addCustomSection} updateCustomSection={updateCustomSection}
                    removeCustomSection={removeCustomSection} moveCustomSection={moveCustomSection}
                    showEditHistory={showEditHistory} setShowEditHistory={setShowEditHistory} setIsEditing={setIsEditing}
                    sectionOrderFlatten={sectionOrderFlatten}
                    omitSectionSidebar={omitSectionSidebar}
                    sectionNumberProps={{
                      conclusion: sectionNumberProps('conclusion') ?? undefined,
                      appendix: sectionNumberProps('appendix') ?? undefined,
                    }}
                    editorialActive={editorialActive}
                    editorialSectionNumbers={{
                      conclusion: sectionDisplayNumbers?.get('conclusion'),
                      appendix: sectionDisplayNumbers?.get('appendix'),
                    }}
                    // COMBINES (fold #5) — Extended Site Data renders INSIDE
                    // the Appendix & References section for every fold type.
                    // Gates: (1) the type carries the fold (public-sentiment /
                    // strip types never do); (2) an ARCHIVED explicit hide
                    // still wins — raw visibility chain, because the id left
                    // the registry so isVisible() would false-block; (3)
                    // content-gated (extendedDataHasContent — the ELCC-fold
                    // empty-suppression pattern) so the appendix bare-heading
                    // guard can trust a non-null slot.
                    extendedDataSlot={
                      reportTypeFoldsExtendedDataIntoAppendix(report.reportType)
                      && resolveSectionVisibility('extendedData', report.sectionVisibility, templateConfig)
                      && extendedDataHasContent(report)
                        ? (
                          <ExtendedDataSection
                            report={report} theme={theme} isEditing={isEditing} editedReport={editedReport}
                            setEditedReport={setEditedReport} headingOverrides={headingOverrides} setHeading={setHeading}
                            isVisible={isVisible} sectionStyle={sectionStyle}
                            locale={presentation.locale}
                            renderAsAppendixSubsection
                            // ELV-1 / ELV-2 / ELV-4 — same live-SED elevation
                            // wiring as the standalone mount (fallback reading,
                            // refresh handler, refreshed-at stamp) so the fold
                            // loses none of the diagnostics.
                            sed={liveSiteSed}
                            onRefreshElevation={reportSite ? handleRefreshElevation : undefined}
                            isRefreshingElevation={isRefreshingElevation}
                            siteElevationRefreshedAt={reportSite?.siteElevationRange?.refreshedAt}
                          />
                        )
                        : undefined
                    }
                    // R3-C1 — Simulation & Resilience renders INSIDE the Appendix
                    // & References section (after Extended Site Data) whenever the
                    // report carries sim-evidence exhibits. Dormant-safe: absent
                    // exhibits ⇒ undefined slot ⇒ byte-identical appendix. B7
                    // governance (citable vs exploratory watermark) lives in the
                    // section renderer, which reads each exhibit's validationStatus.
                    simulationResilienceSlot={
                      hasSimEvidenceExhibits(report)
                        ? (
                          <SimulationResilienceSection
                            report={report}
                            theme={theme}
                            isEditing={isEditing}
                            headingOverrides={headingOverrides}
                            setHeading={setHeading}
                            renderAsAppendixSubsection
                          />
                        )
                        : undefined
                    }
                  />

                  <ReportClosingTail
                    audit={
                      // THE shared resolution (openAuditFlag) — identical to
                      // the page banner, the DOCX cover and the deck's
                      // governance table, duplicates collapsed.
                      //
                      // THE WHOLE PAIR, not `.count`. This passed the bare
                      // number until round 8, and the tail had to guess the
                      // noun: on a flagged-floor report (every stamp
                      // superseded, nothing itemisable left) the reader and
                      // the printed PDF read "1 quality finding is open on
                      // this analysis" while the counsel DOCX and the deck —
                      // which receive the pair — said no individual finding
                      // stands. `kind` is what makes the floor of 1 a FLAG
                      // instead of a tally; throwing it away here is what
                      // split the four surfaces.
                      openAuditFindings(report)
                    }
                    theme={theme}
                    sectionOrderFlatten={sectionOrderFlatten}
                    omitSectionSidebar={omitSectionSidebar}
                    // Reader + paged/PDF share this DOM, so one call covers both
                    // surfaces; DOCX/PPTX resolve the same notice from the same
                    // pure helper. Frozen `report.unresolvedConflicts`, not live
                    // SED drift — an exported artifact states what was true when
                    // it was generated.
                    conflictNotice={resolveReportConflictNotice(report)}
                    demonstrationDisclosure={reportDemonstrationDisclosure(report)}
                  />

                  {/* Page 3 */}
                  <div data-page="3" data-testid="section-page-3" data-paged-flow-shell="true" className={page3ShellClass}>
                    {/* Confidential Sidebar — omitted when cover variant drops the rail. */}
                    {showConfidentialSidebar && !sectionOrderFlatten && (
                      <ConfidentialSidebar theme={theme} />
                    )}

                    {/* Legacy riskMitigation + recommendations — hidden, replaced by riskAssessment + actionPlan */}
                    <div style={{ display: 'none' }} data-legacy-hidden>
                      <div>
                        <div>
                          <div>
                            <h2 className={headingChrome.h2Class} style={headingChrome.h2Style} data-heading="true"><EditableHeading headingKey="riskMitigation" defaultText="Risk and Mitigation Summary" className={headingChrome.textClass} isEditing={isEditing} overrides={headingOverrides} onChange={setHeading} /></h2>
                            <div className="space-y-6">
                              {(editedReport.risks || []).map((risk, idx) => (
                                <div key={idx} className="space-y-2">
                                  {isEditing ? (
                                    <>
                                      <input
                                        className="w-full text-sm font-bold text-text-primary bg-transparent border-b border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-primary mb-1"
                                        value={risk.label}
                                        onChange={(e) => {
                                          const newRisks = [...editedReport.risks];
                                          newRisks[idx] = { ...newRisks[idx], label: e.target.value };
                                          setEditedReport({ ...editedReport, risks: newRisks });
                                        }}
                                      />
                                      <div className="flex flex-col gap-1">
                                        <span className={`font-bold text-text-secondary text-sm ${labelClass(labelTransform)}`}>Impact:</span>
                                        <textarea
                                          className="w-full h-16 p-2 text-sm text-text-primary leading-relaxed border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary"
                                          value={risk.impact}
                                          onChange={(e) => {
                                            const newRisks = [...editedReport.risks];
                                            newRisks[idx] = { ...newRisks[idx], impact: e.target.value };
                                            setEditedReport({ ...editedReport, risks: newRisks });
                                          }}
                                        />
                                      </div>
                                      <div className="flex flex-col gap-1">
                                        <span className={`font-bold text-text-secondary text-sm ${labelClass(labelTransform)}`}>Mitigation:</span>
                                        <textarea
                                          className="w-full h-16 p-2 text-sm text-text-primary leading-relaxed border border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:ring-1 focus:ring-primary"
                                          value={risk.mitigation}
                                          onChange={(e) => {
                                            const newRisks = [...editedReport.risks];
                                            newRisks[idx] = { ...newRisks[idx], mitigation: e.target.value };
                                            setEditedReport({ ...editedReport, risks: newRisks });
                                          }}
                                        />
                                      </div>
                                    </>
                                  ) : (
                                    <div className="py-1 space-y-2">
                                      <div className="flex items-center gap-2">
                                        <h3 className="text-sm font-bold text-text-primary flex-1">{risk.label}</h3>
                                        <EditorialBadge
                                          theme={theme}
                                          variant="outline"
                                          tone={
                                            risk.severity === 'critical' ? 'negative' :
                                              risk.severity === 'high' ? 'warning' :
                                                risk.severity === 'medium' ? 'warning' :
                                                  'neutral'
                                          }
                                        >{risk.severity}</EditorialBadge>
                                        <span className={`text-xs text-text-secondary ${labelClass(labelTransform)}`}>{risk.category}</span>
                                      </div>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                          <div className={`text-xs font-bold text-text-secondary ${labelClass(labelTransform)} mb-1`}>Impact</div>
                                          <p className="text-xs text-text-secondary leading-relaxed">{risk.impact}</p>
                                        </div>
                                        <div>
                                          <div className={`text-xs font-bold text-text-secondary ${labelClass(labelTransform)} mb-1`}>Mitigation Strategy</div>
                                          <p className="text-xs text-text-secondary leading-relaxed">{risk.mitigation}</p>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* William QA BUG #14 — the standalone "Recommendations &
                            next steps" section (POWER / SITE / COOLING + "Report
                            prepared: …") was REMOVED. It duplicated the conclusion's
                            "Recommended next actions" (rendered by ReferencesSection's
                            conclusion block) and was already legacy-hidden here. The
                            report date is carried by the cover dateline + the canonical
                            <ReportFooter> (ReportClosingTail), so nothing visible is
                            lost. */}
                      </div>
                    </div>
                    {/* END hidden legacy riskMitigation div */}

                    {/* Auto-glossary — gated on the active template's
                 glossaryEnabled flag (sovereign / utility / research /
                 owner-operator / consultancy / energy-major audiences
                 default to ON; hyperscaler / colo / IPP / financial-
                 sponsor default to OFF). The acronym extractor walks
                 the rendered narrative once per render; suppressed
                 when no dictionary terms appear. */}
                    {activeTemplate?.glossaryEnabled && (
                      <GlossarySection
                        acronyms={extractAcronymsFromReport(report)}
                        theme={theme}
                        omitSectionSidebar={omitSectionSidebar}
                        shellPadding={inlineSectionShellPadding}
                      />
                    )}

                    {/* TSE-2 C6 — template-level custom sections. Filtered by
                 `scope` ('all' or matching ReportTypeId) and rendered
                 in the order the user added them. Body is plain text
                 with newlines preserved via `whiteSpace: pre-wrap` —
                 React JSX text escapes HTML by default so this is
                 XSS-safe without DOMPurify. */}
                    {(visibleTemplateCustomSections)
                      // PHASE-D — fully omit a custom section resolved to not-visible
                      // (no `display:none` <section> left behind). The section-order
                      // reorder still applies to the survivors via `sectionStyle`.
                      .filter(cs => isVisible(`custom-${cs.id}`))
                      .map(cs => (
                        <section
                          key={cs.id}
                          id={`custom-${cs.id}`}
                          data-section={`custom-${cs.id}`}
                          data-template-custom-section
                          className="space-y-4 [content-visibility:auto] [contain-intrinsic-size:auto_320px] print:[content-visibility:visible]"
                          style={sectionStyle(`custom-${cs.id}`)}
                        >
                          <h2
                            className={headingChrome.h2Class}
                            style={headingChrome.h2Style}
                            data-heading="true"
                          >
                            {cs.title}
                          </h2>
                          <div
                            className="text-sm text-text-secondary"
                            style={{ whiteSpace: 'pre-wrap' }}
                          >
                            {report.sectionContent?.[cs.responseField ?? cs.id] ?? cs.body}
                          </div>
                        </section>
                      ))
                    }

                    {/* The in-body "We can improve this report" upgrade card was
                 removed (2026-05-11) — it now lives only in the Insights
                 side-tray (the Improvements tab), where the rest of the
                 editor-only surfaces moved. It was clutter at the foot of
                 the report content. */}

                    {/* Conclusion callout lives inside ReferencesSection so it
                 shares the conclusion section's template order. */}

                    {/* Report-quality scroll anchor. The Quality / Sanity /
                 Generation-cost panels live in the Insights side-tray;
                 the visible "moved to the Insights panel" note that used
                 to sit here was removed (2026-05-11) — it was clutter at
                 the foot of the report. The empty anchor stays so the
                 unresolved-findings banner's "Review →" link still has a
                 scroll target, and `data-admin-appendix` keeps the export
                 pipelines' exclude marker. */}
                    <section
                      id="report-quality-appendix"
                      data-admin-appendix
                      data-testid="report-quality-appendix"
                      aria-hidden="true"
                      className="h-0 overflow-hidden"
                    ></section>

                  </div>
                </PageFlowBox>{/* B1 — end CONTINUOUS page-level two-column flow box */}
              </div>{/* [data-report-root] */}
            </div>{/* space-y-8 pb-12 outer wrapper */}

            {/* The Insights tray was removed 2026-07-28 — quality review,
           sanity findings, and cost telemetry live on the site Audit
           tab's Report-quality section (ReportQualityAuditSection);
           every former entry point navigates there via openQualityAudit. */}
            {/* KMU — in-report Update KMZ flow. Mutates the LIVE site doc,
           NOT the rendered report. The editor itself runs all the
           normal write-path guards (parseKmzOrKml validation, elevation
           sampling, multi-parcel handling) — KMU adds the
           reproducibility-clarifying toast on close so users see
           explicitly that this report keeps its frozen snapshot. */}
            {kmzEditorOpen && reportSite && (
              <SiteLocationBoundaryEditor
                site={reportSite}
                initialMode="boundary"
                initialBoundaryView="import"
                open
                onClose={() => {
                  setKmzEditorOpen(false);
                  // Detect a real save by comparing the live site's
                  // boundary / updatedAt against the open-time snapshot.
                  // The editor closes on cancel without writing anything,
                  // so a quiet cancel produces matching identities and we
                  // skip the reproducibility toast (the user didn't change
                  // anything — no need to clarify the immutability rule).
                  const before = kmzEditorOpenedSnapshotRef.current;
                  const after = reportSite ? { siteBoundary: reportSite.siteBoundary, updatedAt: reportSite.updatedAt } : null;
                  const boundaryChanged = before?.siteBoundary !== after?.siteBoundary;
                  const updatedAtChanged = before?.updatedAt !== after?.updatedAt;
                  if (boundaryChanged || updatedAtChanged) {
                    toast.message(
                      'This report keeps its original boundary; new runs will use the updated one.',
                      { duration: 6000 },
                    );
                  }
                  kmzEditorOpenedSnapshotRef.current = null;
                }}
              />
            )}
            {layerEditorOpen && reportSite && (
              <SiteLayerEditor
                site={reportSite}
                open
                onClose={() => setLayerEditorOpen(false)}
              />
            )}
            <BlockedPublishDialog
              findings={blockedPublishFindings}
              onClose={() => setBlockedPublishFindings(null)}
              onReviewInAudit={() => {
                setBlockedPublishFindings(null);
                if (report.siteId) {
                  rerunRouteNavigate(`/sites/${report.siteId}?tab=audit&report=${report.id}`);
                }
              }}
            />
          </FigureCounterProvider>
        </StudioPreviewProvider>
        </InSectionNoticesProvider>
      </ViewModeProvider>
    </ThemeOverrideProvider>
    </ReportReaderModeContext.Provider>
    </ChartShrinkContext.Provider>
  );
};

/**
 * #34 P2 STAGE 2 — `SiteRagReportView`: the in-app reader surface. A thin
 * wrapper over the unified {@link SiteRagReportRenderer} with `surface='view'`,
 * preserving the EXACT historical named export + prop shape every consumer
 * (App routes, SampleReportPrintView's continuous path, the parity harness)
 * imports. The 'view' surface resolves `labelTransform='uppercase'` and keeps
 * the navigate-only Back behavior, so its output is byte-identical to the
 * pre-collapse `SiteRagReportView` — independent of the engine-unified flag
 * (the view body never forked; only the export tree did).
 */
export const SiteRagReportView: React.FC<SiteRagReportViewProps> = (props) => (
  <SiteRagReportRenderer {...props} surface="view" />
);

/**
 * Degraded-mode banner — shown at the top of the report when the AI narrative
 * pipeline failed (e.g. Gemini at capacity / 503 / malformed JSON after retries)
 * and the report was assembled from deterministic data only.
 *
 * The "Refresh narrative" button re-runs only the Stage 2 narrative call via
 * `store.regenerateNarrativeOnly(reportId)`. On success the banner clears; on
 * repeat failure a toast surfaces and the banner stays.
 */
const DegradedNarrativeBanner: React.FC<{ reason?: string; reportId: string }> = ({ reason, reportId }) => {
  const [busy, setBusy] = React.useState(false);
  const handleRefresh = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await useStore.getState().regenerateNarrativeOnly(reportId);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      role="status"
      aria-live="polite"
      className="border p-4 flex items-start gap-3"
      style={{
        backgroundColor: 'color-mix(in oklab, var(--warning) 15%, transparent)',
        borderColor: 'color-mix(in oklab, var(--warning) 40%, transparent)',
      }}
    >
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--warning)' }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-text-primary">
          AI narrative unavailable for this report. Showing deterministic data only.
        </div>
        {reason && (
          <div className="text-xs text-text-muted mt-1 truncate" title={reason}>
            {reason}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={handleRefresh}
        disabled={busy}
        className={`shrink-0 h-8 px-3 border border-border bg-background hover:bg-state-hover text-xs font-bold ${labelClass('uppercase')} text-text-primary disabled:opacity-50 transition-colors flex items-center gap-1.5`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
        {busy ? 'Refreshing…' : 'Refresh narrative'}
      </button>
    </div>
  );
};

/**
 * Shown when a report section has no data — prompts user to edit.
 *
 * RN-1 (wave-16): when the call site passes `reportId` + `sectionId`,
 * we surface a `[↻ Regenerate]` button alongside the "Edit Report" CTA
 * so users can re-run the narrative for an empty section without
 * dropping into edit mode. Wired to the store's `regenerateSection`
 * action.
 *
 * RN-2 (wave-16): when the coverage matrix carries an auto-fill source
 * for the section, the call site passes an `autoFill` prop describing
 * the provider; clicking the chip routes to `applyAutoFillToSection`.
 * Conservative — fires only on explicit user click.
 *
 * In export mode (PDF/DOCX render pipelines) all three affordances —
 * Edit, Regenerate, Auto-fill — are stripped so the printed page
 * carries no interactive chrome.
 *
 * HR-QA fix (2026-06-10): the four call sites in this file never passed
 * `exportMode`, so the "EDIT REPORT TO ADD DATA" link and the
 * "↻ Regenerate" button printed verbatim into customer PDFs
 * (supply-chain-risk exec-summary panel). The component now ALSO reads
 * the ViewMode context directly — SiteRagReportView wraps the whole
 * report tree in <ViewModeProvider>, so 'export' (PDF/DOCX) and
 * 'public-share' (unauthenticated reader; store actions like
 * regenerateSection are meaningless there) both strip the interactive
 * chrome regardless of whether the prop was threaded. The prop is kept
 * as an explicit override for non-provider mounts.
 */
export const InsufficientData = ({
  section,
  onEdit,
  reportId,
  sectionId,
  autoFill,
  exportMode = false,
}: {
  section: string;
  onEdit: () => void;
  /** RN-1: when present, enables the [↻ Regenerate] button. */
  reportId?: string;
  /** RN-1: section id passed to `regenerateSection(reportId, sectionId)`. */
  sectionId?: string;
  /** RN-2: when present, enables the [💡 Auto-fill] chip. */
  autoFill?: {
    providerId: string;
    providerLabel: string;
  };
  /** When true, strip ALL interactive chrome (PDF/DOCX exports). */
  exportMode?: boolean;
}) => {
  // Pull both actions outside the click handler so the component
  // remains test-friendly (the store mock satisfies both reads).
  const regenerateSection = useStore((s) => s.regenerateSection);
  const applyAutoFillToSection = useStore((s) => s.applyAutoFillToSection);
  // HR-QA — context-derived export detection (see docstring). The prop is an
  // explicit override; the ViewMode context covers the call sites that never
  // threaded it so interactive chrome can never print into a customer PDF.
  const audience = useViewMode();
  const hideInteractive = exportMode || audience === 'export' || audience === 'public-share';
  const canRegenerate = !hideInteractive && typeof reportId === 'string' && typeof sectionId === 'string' && reportId.length > 0 && sectionId.length > 0;
  const canAutoFill = !hideInteractive && autoFill !== undefined && typeof reportId === 'string' && typeof sectionId === 'string' && reportId.length > 0 && sectionId.length > 0;
  const [regenBusy, setRegenBusy] = React.useState(false);
  const [autoFillBusy, setAutoFillBusy] = React.useState(false);
  return (
    <div
      className="border-2 border-dashed border-amber-300 bg-amber-50 p-4 space-y-2"
      data-testid={sectionId ? `insufficient-data-${sectionId}` : undefined}
    >
      <div className="flex items-center gap-2 text-amber-700">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        {/* #34 P2 STAGE 2 — the eyebrow de-uppercases on the non-interactive
            (export / public-share) render, matching the unified export surface;
            on the in-app view it keeps the historical uppercase chrome. canonClass
            normalizes `uppercase`/`tracking-*`, so this is invisible to the parity
            harness; it reproduces the legacy export-fork de-uppercasing for the PDF. */}
        <span className={`text-xs font-bold ${labelClass(hideInteractive ? 'none' : 'uppercase')}`}>Insufficient Data – {section}</span>
      </div>
      <p className="text-sm text-amber-600 leading-relaxed">
        {hideInteractive
          // Print/share copy must not instruct the reader to click product
          // affordances that do not exist on paper.
          ? 'This section could not be populated from available databases or AI analysis. Site-specific information can be added in the Datacrate platform.'
          : 'This section could not be populated from available databases or AI analysis. Click "Edit Report" to add this information manually based on your site-specific knowledge.'}
      </p>
      {!hideInteractive && (
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            onClick={onEdit}
            // This affordance only renders when interactive (!hideInteractive ⇒
            // the in-app view), so it is always uppercase — routed through
            // labelClass for token consistency with the other 14 strips.
            className={`text-xs font-bold ${labelClass('uppercase')} text-amber-800 underline hover:text-amber-900`}
          >
            Edit Report to Add Data
          </button>
          {canRegenerate && (
            <button
              type="button"
              disabled={regenBusy}
              data-testid={`insufficient-data-regenerate-${sectionId}`}
              onClick={() => {
                if (regenBusy) return;
                setRegenBusy(true);
                void regenerateSection(reportId as string, sectionId as string)
                  .catch((e) => { console.warn('[InsufficientData] regenerate failed', (e as Error)?.message); })
                  .finally(() => { setRegenBusy(false); });
              }}
              className="inline-flex items-center gap-1 px-2 py-1 type-section text-amber-900 border border-amber-400 bg-surface-1 hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded disabled:opacity-50"
            >
              <span aria-hidden="true">↻</span>
              <span>{regenBusy ? 'Regenerating…' : 'Regenerate'}</span>
            </button>
          )}
          {canAutoFill && autoFill && (
            <button
              type="button"
              disabled={autoFillBusy}
              data-testid={`insufficient-data-autofill-${sectionId}`}
              data-provider-id={autoFill.providerId}
              onClick={() => {
                if (autoFillBusy) return;
                setAutoFillBusy(true);
                void applyAutoFillToSection(reportId as string, sectionId as string, autoFill.providerId)
                  .catch((e) => { console.warn('[InsufficientData] auto-fill failed', (e as Error)?.message); })
                  .finally(() => { setAutoFillBusy(false); });
              }}
              className="inline-flex items-center gap-1 px-2 py-1 type-section text-amber-900 border border-amber-400 bg-amber-100 hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded disabled:opacity-50"
            >
              <span aria-hidden="true">💡</span>
              <span>{autoFillBusy ? 'Applying…' : `Auto-fill from ${autoFill.providerLabel}?`}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
